import { useScreenHelp } from "./ScreenHelp";
import { appAlert, appConfirm } from "./appDialogs";
import { API_ORIGIN, apiUrl } from "./apiOrigin";
import { CARE_HEAD_OFFICE_KEYS, isCareNavigationAllowed } from "./careEdition";
// HeadOffice.js — fleet dashboard for /api/headoffice/* endpoints
//
// Scaffolded UI that mirrors the structure of the head-office backend.
// Each module gets a card showing active-pharmacy count plus any urgent
// rule failure counts. Click a card to drill in and see per-pharmacy
// detail for that module.
//
// All endpoints assume server.js handles JWT verification — we just pass
// the Authorization header from localStorage.

import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import PopupContent from "./PopupContent";
import { NearMissAnalysis, SOPCompliance } from "./Mockupp";
import HolidayRota from "./holiday_rota";
import StaffAbsences from "./StaffAbsences"; // <-- added: fleet absence grid
// ActionCentre import removed — the Action centre now lives on the
// Communications page. ActionCentre.js itself is unchanged.
import Staff from "./Staff";
import ClockInOut from "./ClockInOut";
import MhraAlerts from "./MhraAlerts";
import PeriodicTaskEntryDialog from "./PeriodicTaskEntryDialog";
import AccidentBookDetail from "./Riddor";
import RPSignIn from "./RPSignIn";
import PatAppConfig from "./PatAppConfig";
import PatientFeedback from "./PatientFeedback"; // <-- added: Surveys > Feedback
import ConfigurationSummary from "./ConfigurationSummary"; // <-- added: setup checklist across every module
import Geosorting from "./Geosorting";
import DeliveryActivityReview from "./DeliveryActivityReview"; // Deliveries → Activity Review (2026-10)
// Deliveries panel tabs that used to be the left-menu "Deliveries" pages (2026-10);
// "Activity" lives inside Activity Review.
import RouteAnalysis from "./route_analysis";
import Payments from "./Payments";
import MessageConfiguration from "./MessageConfiguration"; // Messaging panel → "Message configuration" tab (was in the left menu)
import { useMaskNames, displayName, MaskToggleButton } from "./nameMask";
import { exportRemovedDrugs } from "./exportRemovedDrugs";
import { ExpiredEmailPanel } from "./ReportEmailPanels"; // Date checking → monthly removed/expired drugs email (moved from Settings)
import "./Popup.css";
const API_BASE = apiUrl("");

// ---------------------------------------------------------------------------
// Module metadata — paths under /api/headoffice/ and which live rules to poll
// ---------------------------------------------------------------------------
const MODULES = [{
  key: "fridge",
  title: "Fridge monitoring",
  path: "fridge",
  blurb: "Live now. Each red number = how many pharmacies are currently failing that check.",
  urgentRules: [{
    rule: "latest-in-range",
    label: "Out-of-range now",
    severity: "fail",
    hint: "Pharmacies whose most recent fridge reading is out of range right now."
  }, {
    rule: "recheck-overdue",
    label: "Recheck overdue",
    severity: "fail",
    hint: "Pharmacies past the deadline to re-check a flagged fridge."
  }, {
    rule: "logged-today",
    label: "Sites not logged today",
    severity: "warn",
    hint: "Active fridges with no temperature reading recorded today."
  }],
  // Drives the reassurance figure on the card + the colour-coded detail grid.
  okStatusRule: "fleet-status",
  // Show "(active/total)" next to the title instead of an Active pharmacies row.
  showActiveRatioInTitle: true,
  // v43: the ratio now counts pharmacies with fridges CONFIGURED, not ones
  // that logged recently — a branch that stopped logging used to vanish
  // from this card along with its own "not logged today" warning. Dormant
  // sites get their own amber row instead of disappearing.
  configuredRatio: "Pharmacies with fridges set up, out of all sites in the group.",
  dormantRow: {
    label: "Sites set up but not logging",
    hint: "Pharmacies with fridges configured that have recorded no reading in the last 90 days. They are still counted in every check above."
  },
  // Config for the per-pharmacy colour-coded status grid in the detail view.
  statusDetail: {
    endpoint: "fleet-status",
    popupColumn: "Fridge",
    // PopupContent branch + Mockupp fetch key
    fetchPath: "fridgelogs",
    // POST {pharmacyName} → { logs }
    logsKey: "logs",
    popupDataKey: "fridgeLogs",
    popupFlag: "isFridgePopup",
    checkedField: "loggedCount",
    totalField: "fridgeCount",
    checkedLabel: "Fridges logged",
    unit: "fridge"
  },
  snapshot: null
}, {
  key: "cleaning",
  title: "Cleaning",
  path: "cleaning",
  blurb: "Live now, except the 30-day figure which is a rolling 30-day rate.",
  urgentRules: [{
    rule: "overdue",
    label: "Areas overdue",
    severity: "fail",
    hint: "Cleaning areas past their due date as of right now."
  }, {
    rule: "unlogged",
    label: "Active areas never logged",
    severity: "warn",
    hint: "Scheduled cleaning areas that have never had a clean logged."
  }, {
    rule: "on-time-pct-30d",
    label: "On-time % below threshold (30d)",
    severity: "fail",
    hint: "Pharmacies whose on-time cleaning rate over the last 30 days is under the configured threshold."
  }],
  okStatusRule: "fleet-status",
  showActiveRatioInTitle: true,
  configuredRatio: "Pharmacies with cleaning areas scheduled, out of all sites in the group.",
  dormantRow: {
    label: "Sites set up but not logging",
    hint: "Pharmacies with scheduled cleaning areas that have recorded no clean in the last 90 days. They are still counted in every check above."
  },
  // All-branches area grid (GET {path}/area-overview) with the
  // areas ⇄ per-area log history drill popup. Replaces the shared
  // statusDetail grid + cleaninglogdetails popup for this module.
  areaFleet: {
    noun: "cleaned"
  },
  snapshot: null
}, {
  key: "handover",
  title: "Handover diary",
  path: "handover",
  blurb: "Live now. 'Items overdue' = handover items past their due date right now.",
  urgentRules: [{
    rule: "overdue",
    label: "Items overdue",
    severity: "fail",
    hint: "Handover items not completed and now past their due date."
  }],
  okStatusRule: "fleet-status",
  showActiveRatioInTitle: true,
  configuredRatio: "Pharmacies that have ever raised a handover item, out of all sites in the group.",
  dormantRow: {
    label: "Sites quiet for 90+ days",
    hint: "Pharmacies that have used the handover diary but recorded nothing in the last 90 days. Any items they left outstanding are still counted above."
  },
  // Extra figures pulled from the per-pharmacy fleet-status roll-up and shown
  // on the card: amber pending row, green completed-today row.
  statusFigures: [{
    field: "pendingCount",
    label: "Items outstanding (not overdue)",
    tone: "warn",
    hint: "Handover items still open but not yet past their due date."
  }, {
    field: "completedTodayCount",
    label: "Items completed today",
    tone: "ok",
    hint: "Handover items marked complete today across the group."
  }],
  statusDetail: {
    endpoint: "fleet-status",
    popupColumn: "Handover",
    fetchPath: "handoverdetails",
    // POST {pharmacyName} → { details }
    logsKey: "details",
    popupDataKey: "handoverDetails",
    popupFlag: "isHandoverPopup",
    countMode: "handover" // special count column (overdue/pending)
  },
  snapshot: "snapshot"
}, {
  key: "dateChecking",
  title: "Date checking",
  path: "date-checking",
  blurb: "Live now. Counts = pharmacies currently failing each check.",
  urgentRules: [{
    rule: "overdue",
    label: "Areas overdue",
    severity: "fail",
    hint: "Date-checking areas past their due date as of right now."
  }, {
    rule: "unlogged",
    label: "Active areas never logged",
    severity: "warn",
    hint: "Scheduled date-checking areas that have never been logged."
  }],
  okStatusRule: "fleet-status",
  showActiveRatioInTitle: true,
  configuredRatio: "Pharmacies with date-checking areas scheduled, out of all sites in the group.",
  dormantRow: {
    label: "Sites set up but not logging",
    hint: "Pharmacies with scheduled date-checking areas that have recorded no check in the last 90 days. They are still counted in every check above."
  },
  statusFigures: [{
    field: "expired",
    label: "Packets overdue removal",
    tone: "fail",
    zeroTone: "ok",
    hint: "Drugs still on shelf that are past their expiry date."
  }, {
    field: "expiring1m",
    label: "Packets expiring within 4wks",
    tone: "warn",
    zeroTone: "ok",
    hint: "Drugs on shelf expiring within the next 4 weeks."
  }],
  statusDetail: {
    endpoint: "fleet-status",
    gridMode: "dateChecking" // dedicated grid with expiry-band columns
  },
  // Second tab in the detail view: all-branches date-checking AREAS grid
  // in the same style as cleaning, with the areas ⇄ log history popup.
  areaFleet: {
    noun: "checked"
  },
  snapshot: "snapshot"
}, {
  key: "cdRegister",
  title: "CD register",
  path: "cd-register",
  blurb: "Live now. Counts = pharmacies currently failing each check.",
  urgentRules: [{
    rule: "balance-check-overdue",
    label: "Balance check overdue",
    hint: "Registers past their configured balance-check interval right now."
  }, {
    rule: "open-discrepancies",
    label: "Unresolved discrepancies",
    hint: "Discrepancies logged but still unresolved as of right now."
  }],
  okStatusRule: "fleet-status",
  showActiveRatioInTitle: true,
  configuredRatio: "Pharmacies with active CD registers, out of all sites in the group.",
  dormantRow: {
    label: "Sites set up but no recent entries",
    hint: "Pharmacies with active CD registers that have recorded no book-out or balance check in the last 90 days. They are still counted in every check above."
  },
  statusDetail: {
    endpoint: "fleet-status",
    gridMode: "cdRegister" // dedicated per-pharmacy grid, inline expand
  },
  snapshot: "snapshot"
}, {
  key: "patientSafety",
  title: "Patient safety",
  path: "patient-safety",
  blurb: "Live now. Shows how many sites have ever recorded a patient safety incident.",
  urgentRules: [],
  // Card shows "ever used / total" instead of recent-active / total.
  everUsedRatio: true,
  // Warning line: sites with LFPSE ticked but no reference recorded.
  cardRollup: {
    endpoint: "incomplete-lfpse",
    label: "Sites with incomplete LFPSE entries",
    hint: "Pharmacies with at least one incident where LFPSE is marked submitted but no reference number was recorded.",
    countField: "sites",
    totalField: "total"
  },
  snapshot: "snapshot"
}, {
  key: "accidentBook",
  title: "Accident book",
  path: "accident-book",
  blurb: "Live now. 'Reviews overdue' = accidents past their review target.",
  urgentRules: [{
    rule: "review-overdue",
    label: "Reviews overdue",
    hint: "Recorded accidents not reviewed within the target number of days."
  }],
  snapshot: "snapshot"
}, {
  key: "rpSignIn",
  title: "RP sign-in",
  path: "rp-signin",
  blurb: "Live now. Reflects the current responsible-pharmacist sign-in state.",
  showActiveRatioInTitle: true,
  // Custom rollup row: of pharmacies engaged in the last 14 days, how many
  // are uncovered (no RP signed in) right now. Green if all covered.
  cardRollup: {
    endpoint: "coverage-today",
    label: "Currently uncovered",
    hint: "Of pharmacies that recorded an RP sign-in in the last 14 days, how many have no RP signed in right now. Green if all are covered.",
    // value shown is uncovered, with the engaged total alongside.
    countField: "uncovered",
    totalField: "engaged"
  },
  urgentRules: [{
    rule: "stale-open-sessions",
    label: "Stale open sessions",
    hint: "Sign-in sessions left open longer than the configured maximum."
  }],
  snapshot: "snapshot"
}, {
  key: "visitorLogs",
  title: "Visitor logs",
  path: "visitor-logs",
  blurb: "Live now. Shows how many sites have ever logged a visitor.",
  everUsedRatio: true,
  everUsedEndpoint: "fleet-summary",
  // Two card rows from one endpoint: sites with a visitor currently on site
  // (amber if any) and sites with a stale, not-signed-out entry (pink if any).
  cardRollups: [{
    endpoint: "fleet-summary",
    field: "sitesWithCurrent",
    label: "Sites with visitors on site now",
    tone: "warn",
    hint: "Pharmacies with at least one visitor signed in today and not yet signed out."
  }, {
    endpoint: "fleet-summary",
    field: "sitesWithStale",
    label: "Sites with stale entries",
    tone: "fail",
    hint: "Pharmacies with at least one visitor still showing as on site from before today (likely not signed out)."
  }],
  urgentRules: [],
  snapshot: "snapshot"
}, {
  key: "messaging",
  title: "Messaging",
  path: "messaging",
  blurb: "Live now, with a rolling 30-day outbound activity breakdown on drill-in.",
  urgentRules: [],
  snapshots: ["patient-enrolment", "prescription-message-rate"]
}].filter(module => CARE_HEAD_OFFICE_KEYS.has(module.key));

// ---------------------------------------------------------------------------
// Tiny fetch helper
// ---------------------------------------------------------------------------
// Append the viewer's organisation as a query param so the backend scopes
// the fan-out to that group's member pharmacies. "Pharmace all" (admin) and
// an absent value both mean "whole fleet" server-side, so we can always send
// it. Mirrors the identity Mockupp.js reads from localStorage.
function withOrgScope(path) {
  const org = localStorage.getItem("organisationName") || "";
  if (!org) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}organisationName=${encodeURIComponent(org)}`;
}
async function apiGet(path, token) {
  let res;
  try {
    res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(path)}`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  } catch (e) {
    // Network-level failure (connection reset, CORS, timeout) — name the path
    // so the surfaced error isn't a bare "Failed to fetch".
    throw new Error(`${path} → network error (${e.message})`);
  }
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return res.json();
}

// Write helper for POST/PUT/DELETE (periodic tasks CRUD). Mirrors apiGet's
// error-naming so failures surface the endpoint.
async function apiSend(path, token, method = "POST", body = null) {
  let res;
  try {
    res = await fetch(`${API_BASE}/api/headoffice/${withOrgScope(path)}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: body != null ? JSON.stringify(body) : undefined
    });
  } catch (e) {
    throw new Error(`${path} → network error (${e.message})`);
  }
  if (!res.ok) {
    let msg = `${path} → HTTP ${res.status}`;
    try {
      const j = await res.json();
      if (j?.error) msg += ` — ${j.error}`;
    } catch (_) {}
    throw new Error(msg);
  }
  return res.json();
}

// Root-host POST helper for the SOP / Near-Miss / Action-Centre summary
// endpoints. These live at the API root (e.g. /headoffice_sop_card), not
// under /api/headoffice/, and are POST with a Bearer token.
async function apiPostRoot(path, token, body = {}) {
  // Carry the viewer's organisation so the root-host card endpoints scope to
  // the group's pharmacies, same as the /api/headoffice/* calls. The backend
  // treats "Pharmace all"/absent as the whole fleet.
  const org = localStorage.getItem("organisationName") || "";
  const scopedBody = org ? {
    ...(body || {}),
    organisationName: org
  } : body || {};
  let res;
  try {
    res = await fetch(`${API_BASE}/${path}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(scopedBody)
    });
  } catch (e) {
    throw new Error(`${path} → network error (${e.message})`);
  }
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Reusable centre-screen loading overlay. Use for detail panels whose data
// takes a noticeable moment to compute (e.g. messaging, which fans out across
// the whole fleet). Renders a dimmed backdrop with a centred spinner + label.
// ---------------------------------------------------------------------------
function LoadingOverlay({
  label = "Loading…",
  fullscreen = true
}) {
  const wrap = fullscreen ? {
    position: "fixed",
    inset: 0,
    zIndex: 1000
  } : {
    position: "absolute",
    inset: 0,
    zIndex: 10
  };
  return <div style={{
    ...wrap,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(255,255,255,0.65)",
    backdropFilter: "blur(1px)"
  }}>
      <style>{`@keyframes ho-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{
      width: 40,
      height: 40,
      borderRadius: "50%",
      border: "4px solid #E6F7FF",
      borderTopColor: "#0E8CFF",
      animation: "ho-spin 0.8s linear infinite"
    }} />
      <div style={{
      marginTop: 12,
      fontSize: 14,
      color: "#434343",
      fontWeight: 500
    }}>
        {label}
      </div>
    </div>;
}

// ---------------------------------------------------------------------------
// Styled hover tooltip. Wrap any element; shows `text` in a dark floating box
// on hover. Self-contained (no portal) — positioned just above the trigger.
// ---------------------------------------------------------------------------
function InfoTip({
  text,
  children,
  width = 260,
  block = false
}) {
  const [show, setShow] = useState(false);
  return <span style={{
    position: "relative",
    display: block ? "block" : "inline-flex",
    alignItems: "center"
  }} onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      {children}
      {show && <span style={{
      position: "absolute",
      bottom: "calc(100% + 8px)",
      left: 0,
      width,
      zIndex: 50,
      background: "#262626",
      color: "#FAFAFA",
      fontSize: 12,
      lineHeight: 1.45,
      fontWeight: 400,
      padding: "8px 10px",
      borderRadius: 6,
      boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
      pointerEvents: "none",
      whiteSpace: "normal"
    }}>
          {text}
          <span style={{
        position: "absolute",
        top: "100%",
        left: 14,
        borderLeft: "6px solid transparent",
        borderRight: "6px solid transparent",
        borderTop: "6px solid #262626"
      }} />
        </span>}
    </span>;
}

// ---------------------------------------------------------------------------
// Style tokens
// ---------------------------------------------------------------------------
const S = {
  wrap: {
    padding: 16,
    fontFamily: "system-ui, sans-serif"
  },
  h1: {
    fontSize: 22,
    fontWeight: 600,
    margin: "0 0 6px"
  },
  sub: {
    fontSize: 13,
    color: "#5D5D5D",
    margin: "0 0 16px"
  },
  // 280px let only five cards across before wrapping, so the Staff row (six)
  // spilled onto a second line with the rest of the width sitting empty.
  // 205px fits six comfortably on a normal screen and seven on a wide one;
  // the 1fr half of the minmax still stretches them to fill whatever is
  // there, so rows with fewer cards look no different.
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(205px, 1fr))",
    gap: 10
  },
  card: {
    background: "#fff",
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    boxSizing: "border-box",
    padding: 11,
    cursor: "pointer",
    // Narrower cards mean longer labels have to wrap rather than
    // push the figure off the right edge.
    minWidth: 0,
    overflowWrap: "anywhere",
    transition: "box-shadow 120ms, border-color 200ms"
  },
  cardTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 5,
    color: "#262626",
    lineHeight: 1.25
  },
  // alignItems/gap so a wrapped label keeps its number beside it rather than
  // stretching the row; the number never wraps.
  metaRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 8,
    fontSize: 12.5,
    color: "#434343",
    padding: "2px 0",
    lineHeight: 1.3
  },
  urgent: {
    color: "#CF1322",
    fontWeight: 600
  },
  ok: {
    color: "#059669",
    fontWeight: 600
  },
  drill: {
    background: "#fff",
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    padding: 16,
    marginTop: 12
  },
  drillTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 10
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13
  },
  th: {
    textAlign: "left",
    borderBottom: "1px solid #D9D9D9",
    padding: "6px 8px",
    fontWeight: 600,
    color: "#434343"
  },
  td: {
    borderBottom: "1px solid #F5F5F5",
    padding: "6px 8px",
    color: "#262626",
    verticalAlign: "top"
  },
  btn: {
    background: "#0E8CFF",
    color: "#fff",
    border: "none",
    borderRadius: 4,
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: 13
  },
  btnGhost: {
    background: "#F5F5F5",
    color: "#434343",
    border: "1px solid #D9D9D9",
    borderRadius: 4,
    padding: "6px 12px",
    cursor: "pointer",
    fontSize: 13
  },
  error: {
    color: "#CF1322",
    fontSize: 13,
    padding: "4px 0"
  }
};

// ---------------------------------------------------------------------------
// Formatters — per-module, per-rule table column definitions
// ---------------------------------------------------------------------------
// Each urgent-rule formatter is a list of column defs; rows are produced by
// flattening fleetLive results (one row per (pharmacy × offender)).
// Each snapshot formatter takes the snapshot payload and returns JSX.

const fmtDate = d => d ? new Date(d).toLocaleDateString("en-GB") : "—";
const fmtDateTime = d => d ? new Date(d).toLocaleString("en-GB", {
  day: "2-digit",
  month: "short",
  hour: "2-digit",
  minute: "2-digit"
}) : "—";
const fmtPct = n => n == null ? "—" : `${n.toFixed(1)}%`;

// Background colour for a 0–100 percentage: full green at 100%, full red at
// 10% or below (but >0), interpolated through amber/yellow between. Returns
// undefined for null so the cell stays plain. Text stays dark for legibility
// since these are pale fills.
function pctBg(pct) {
  if (pct == null || Number.isNaN(pct)) return undefined;
  // Clamp the meaningful band to [10, 100]; <=10 is full red, >=100 full green.
  const t = Math.max(0, Math.min(1, (pct - 10) / 90));
  // Interpolate hue 0 (red) → 120 (green) in HSL, pale and legible.
  const hue = Math.round(120 * t);
  return `hsl(${hue}, 75%, 88%)`;
}
const fmtNum = n => n == null ? "—" : n.toLocaleString();
const fmtTemp = (min, max) => {
  const m = v => v == null ? null : `${v}°C`;
  if (min == null && max == null) return "—";
  if (min != null && max != null) return min === max ? m(min) : `${min}–${max}°C`;
  return m(min) ?? m(max);
};

// Flatten {pharmacy, offenders[]} → flat rows
function flattenOffenders(results) {
  const out = [];
  for (const r of results || []) {
    for (const o of r.offenders || []) {
      out.push({
        pharmacyId: r.pharmacyId,
        pharmacyName: r.pharmacyName,
        ...o
      });
    }
  }
  return out;
}
function GenericTable({
  rows,
  columns,
  empty
}) {
  const masked = useMaskNames();
  if (rows.length === 0) return <div style={{
    fontSize: 13,
    color: "#059669"
  }}>{empty || "All good — nothing failing."}</div>;
  return <table style={S.table}>
      <thead>
        <tr>{columns.map(c => <th key={c.key} style={S.th}>{c.label}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, i) => <tr key={i}>
            {columns.map(c => <td key={c.key} style={S.td}>
                {c.render ? c.render(row, masked) : row[c.key] ?? "—"}
              </td>)}
          </tr>)}
      </tbody>
    </table>;
}
const PHARMACY_COL = {
  key: "pharmacyName",
  label: "Pharmacy",
  render: (r, masked) => <b>{displayName(r.pharmacyName, masked)}</b>
};

// Urgent-rule formatters keyed by `${module}/${rule}`
const URGENT_FORMATTERS = {
  "fridge/latest-in-range": [PHARMACY_COL, {
    key: "fridgeName",
    label: "Fridge",
    render: r => r.fridgeName + (r.location ? ` (${r.location})` : "")
  }, {
    key: "readingAt",
    label: "Last reading",
    render: r => fmtDateTime(r.readingAt)
  }, {
    key: "temp",
    label: "Min / Max",
    render: r => `${r.minTemperature ?? "—"}°C / ${r.maxTemperature ?? "—"}°C`
  }, {
    key: "recheckIn",
    label: "Recheck in (hrs)",
    render: r => r.recheckIn
  }, {
    key: "comments",
    label: "Comments",
    render: r => r.comments
  }],
  "fridge/recheck-overdue": [PHARMACY_COL, {
    key: "fridgeName",
    label: "Fridge",
    render: r => r.fridgeName
  }, {
    key: "flaggedAt",
    label: "Flagged at",
    render: r => fmtDateTime(r.flaggedAt)
  }, {
    key: "recheckHours",
    label: "Should recheck within",
    render: r => `${r.recheckHours} hrs`
  }, {
    key: "deadline",
    label: "Deadline was",
    render: r => fmtDateTime(r.deadline)
  }, {
    key: "hoursOverdue",
    label: "Hours overdue",
    render: r => <span style={S.urgent}>{r.hoursOverdue}</span>
  }],
  "fridge/logged-today": [PHARMACY_COL, {
    key: "fridgeName",
    label: "Fridge",
    render: r => r.fridgeName
  }, {
    key: "location",
    label: "Location"
  }],
  "cleaning/overdue": [PHARMACY_COL, {
    key: "areaName",
    label: "Area"
  }, {
    key: "nextDue",
    label: "Was due",
    render: r => fmtDate(r.nextDue)
  }, {
    key: "daysOverdue",
    label: "Days overdue",
    render: r => <span style={S.urgent}>{r.daysOverdue}</span>
  }],
  "cleaning/unlogged": [PHARMACY_COL, {
    key: "areaName",
    label: "Area"
  }, {
    key: "scheduledSince",
    label: "Scheduled since",
    render: r => fmtDate(r.scheduledSince)
  }],
  "cleaning/on-time-pct-30d": [PHARMACY_COL, {
    key: "areaName",
    label: "Area"
  }, {
    key: "actualCleans",
    label: "Cleans (30d)"
  }, {
    key: "onTimeCleans",
    label: "On-time"
  }, {
    key: "lateCleans",
    label: "Late"
  }, {
    key: "onTimePct",
    label: "On-time %",
    render: r => fmtPct(r.onTimePct)
  }, {
    key: "threshold",
    label: "Threshold",
    render: r => fmtPct(r.threshold)
  }],
  "handover/overdue": [PHARMACY_COL, {
    key: "topic",
    label: "Topic"
  }, {
    key: "dueDate",
    label: "Was due",
    render: r => fmtDate(r.dueDate)
  }, {
    key: "daysOverdue",
    label: "Days overdue",
    render: r => <span style={S.urgent}>{r.daysOverdue}</span>
  }, {
    key: "fao",
    label: "FAO"
  }, {
    key: "patientName",
    label: "Patient"
  }],
  "date-checking/overdue": [PHARMACY_COL, {
    key: "areaName",
    label: "Area"
  }, {
    key: "nextDue",
    label: "Was due",
    render: r => fmtDate(r.nextDue)
  }, {
    key: "daysOverdue",
    label: "Days overdue",
    render: r => <span style={S.urgent}>{r.daysOverdue}</span>
  }],
  "date-checking/unlogged": [PHARMACY_COL, {
    key: "areaName",
    label: "Area"
  }, {
    key: "scheduledSince",
    label: "Scheduled since",
    render: r => fmtDate(r.scheduledSince)
  }],
  "cd-register/balance-check-overdue": [PHARMACY_COL, {
    key: "registerName",
    label: "Register",
    render: r => r.registerName || r.drugName || String(r.registerId).slice(-6)
  }, {
    key: "balanceCheckedAt",
    label: "Last checked",
    render: r => r.balanceCheckedAt ? fmtDate(r.balanceCheckedAt) : "never"
  }, {
    key: "intervalDays",
    label: "Interval (days)"
  }, {
    key: "daysOverdue",
    label: "Days overdue",
    render: r => <span style={S.urgent}>{r.daysOverdue}</span>
  }],
  "cd-register/open-discrepancies": [PHARMACY_COL, {
    key: "registerName",
    label: "Register",
    render: r => r.registerName || r.drugName || "—"
  }, {
    key: "createdAt",
    label: "Opened",
    render: r => fmtDate(r.createdAt)
  }, {
    key: "daysOpen",
    label: "Days open",
    render: r => <span style={S.urgent}>{r.daysOpen}</span>
  }, {
    key: "notes",
    label: "Notes"
  }],
  "accident-book/review-overdue": [PHARMACY_COL, {
    key: "accidentDate",
    label: "Accident date",
    render: r => fmtDate(r.accidentDate)
  }, {
    key: "injuredName",
    label: "Person"
  }, {
    key: "daysOverdue",
    label: "Days overdue",
    render: r => <span style={S.urgent}>{r.daysOverdue}</span>
  }, {
    key: "targetDays",
    label: "Target (days)"
  }],
  "rp-signin/not-currently-covered": [PHARMACY_COL, {
    key: "lastSignOutAt",
    label: "Last signed out",
    render: r => fmtDateTime(r.lastSignOutAt)
  }, {
    key: "minutesUncovered",
    label: "Mins uncovered",
    render: r => <span style={S.urgent}>{r.minutesUncovered}</span>
  }],
  "rp-signin/stale-open-sessions": [PHARMACY_COL, {
    key: "signInTime",
    label: "Signed in",
    render: r => fmtDateTime(r.signInTime)
  }, {
    key: "hoursOpen",
    label: "Hrs open",
    render: r => <span style={S.urgent}>{r.hoursOpen}</span>
  }, {
    key: "sessionMaxHours",
    label: "Max (cfg)"
  }]
};

// Snapshot formatters: take the per-pharmacy fleetStats array (or messaging
// multi-snapshot object) and return JSX.

function SnapshotKVRow({
  data,
  keys
}) {
  const masked = useMaskNames();
  // Render a flat object as a label/value table given a key list:
  // [{key, label, fmt?}]
  return <table style={S.table}>
      <thead>
        <tr>
          <th style={S.th}>Pharmacy</th>
          {keys.map(k => <th key={k.key} style={S.th}>{k.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {data.map((row, i) => <tr key={i}>
            <td style={S.td}><b>{displayName(row.pharmacyName, masked)}</b></td>
            {keys.map(k => <td key={k.key} style={S.td}>
                {k.fmt ? k.fmt(row[k.key], row) : row[k.key] ?? "—"}
              </td>)}
          </tr>)}
      </tbody>
    </table>;
}
const SNAPSHOT_FORMATTERS = {
  handover: data => <SnapshotKVRow data={data} keys={[{
    key: "totalPending",
    label: "Pending"
  }, {
    key: "totalOverdue",
    label: "Overdue",
    fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v
  }, {
    key: "completedLast30d",
    label: "Completed (30d)"
  }, {
    key: "completedOnTimeLast30d",
    label: "On-time"
  }, {
    key: "completedLateLast30d",
    label: "Late"
  }, {
    key: "onTimePctLast30d",
    label: "On-time %",
    fmt: v => fmtPct(v)
  }]} />,
  "date-checking": data => <SnapshotKVRow data={data} keys={[{
    key: "currentlyExpired",
    label: "Expired on shelf",
    fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v
  }, {
    key: "expiringWithin30Days",
    label: "Expiring <30d"
  }, {
    key: "expiringWithin90Days",
    label: "Expiring <90d"
  }, {
    key: "drugsLoggedLast30d",
    label: "Logged (30d)"
  }, {
    key: "drugsRemovedLast30d",
    label: "Removed (30d)"
  }, {
    key: "drugsMarkedNotPresentLast30d",
    label: "Not present (30d)"
  }]} />,
  prescriptions: data => <SnapshotKVRow data={data} keys={[{
    key: "onShelf",
    label: "On shelf",
    fmt: v => v ? (v.collection?.total || 0) + (v.delivery?.total || 0) : "—"
  }, {
    key: "onShelf",
    label: ">2 months on shelf",
    fmt: v => v ? (v.collection?.twoMonthsOrMore || 0) + (v.delivery?.twoMonthsOrMore || 0) : "—"
  }, {
    key: "monthToDate",
    label: "Scanned (MTD)",
    fmt: v => v ? v.scanned ?? 0 : "—"
  }, {
    key: "monthToDate",
    label: "Collected",
    fmt: v => v ? v.collected ?? 0 : "—"
  }, {
    key: "monthToDate",
    label: "Delivered via app",
    fmt: v => v ? v.deliveredViaApp ?? 0 : "—"
  }, {
    key: "monthToDate",
    label: "Delivered no app",
    fmt: v => v ? v.deliveredWithoutApp ?? 0 : "—"
  }]} />,
  "cd-register": data => <SnapshotKVRow data={data} keys={[{
    key: "activeRegisters",
    label: "Active registers"
  }, {
    key: "registersWithExpiredStock",
    label: "Expired stock",
    fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v
  }, {
    key: "registersOverdueBalanceCheck",
    label: "Balance overdue",
    fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v ?? 0
  }, {
    key: "openDiscrepancies",
    label: "Open discrepancies",
    fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v
  }]} />,
  "patient-safety": data => <SnapshotKVRow data={data} keys={[{
    key: "last30Days",
    label: "Total (30d)",
    fmt: v => v?.total ?? 0
  }, {
    key: "last30Days",
    label: "LFPSE submitted",
    fmt: v => fmtPct(v?.lfpseSubmittedPct)
  }, {
    key: "last30Days",
    label: "RP notified",
    fmt: v => fmtPct(v?.rpNotifiedPct)
  }]} />,
  "accident-book": data => <SnapshotKVRow data={data} keys={[{
    key: "last30Days",
    label: "Accidents (30d)",
    fmt: v => v?.accidents ?? 0
  }, {
    key: "reviewOverdueCount",
    label: "Reviews overdue",
    fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v ?? 0
  }, {
    key: "last30Days",
    label: "Reviewed %",
    fmt: v => fmtPct(v?.reviewedPct)
  }]} />,
  "rp-signin": data => <SnapshotKVRow data={data} keys={[{
    key: "pharmacyCurrentlyOpen",
    label: "Currently open",
    fmt: v => v ? "Yes" : "No"
  }, {
    key: "currentlySignedIn",
    label: "RP signed in",
    fmt: v => v ? "Yes" : <span style={S.urgent}>No</span>
  }, {
    key: "staleOpenSessionsCount",
    label: "Stale sessions",
    fmt: v => v > 0 ? <span style={S.urgent}>{v}</span> : v ?? 0
  }, {
    key: "last30Days",
    label: "Sign-ins (30d)",
    fmt: v => v?.sessionsCount ?? "—"
  }]} />,
  "visitor-logs": data => <SnapshotKVRow data={data} keys={[{
    key: "currentlyOnSite",
    label: "Currently on site",
    fmt: v => v > 0 ? <b>{v}</b> : v
  }, {
    key: "last30Days",
    label: "Visits (30d)"
  }]} />
};

// Special formatter for messaging which returns multi-snapshot
const MESSAGING_SNAPSHOTS = multi => <div>
    <div style={S.sub}>Patient enrolment</div>
    <SnapshotKVRow data={multi["patient-enrolment"] || []} keys={[{
    key: "current",
    label: "Total patients",
    fmt: v => v?.total ?? 0
  }, {
    key: "current",
    label: "By mode",
    fmt: v => v?.byMode ? Object.entries(v.byMode).map(([k, n]) => `${k || "—"}:${n}`).join("  ") : "—"
  }]} />
    <div style={{
    ...S.sub,
    marginTop: 14
  }}>Outbound activity (30d)</div>
    <SnapshotKVRow data={multi["outbound-activity"] || []} keys={[{
    key: "totalOutbound",
    label: "Outbound (30d)",
    fmt: v => v ?? 0
  }, {
    key: "byMode",
    label: "By mode",
    fmt: v => v ? Object.entries(v).map(([k, n]) => `${k}:${n}`).join("  ") : "—"
  }, {
    key: "simOutcomes",
    label: "Sim outcomes",
    fmt: v => v ? `✓${v.simSuccess ?? 0} rescue:${v.backendRescued ?? 0} ✗${v.failed ?? 0}` : "—"
  }]} />
  </div>;
// ---------------------------------------------------------------------------
// SMS detail grid — opened from the messaging card's SIM line. Per-site:
// successful messages by mode, SIM delivered/failed, SIM card count, and
// whether SIM messaging is enabled. Toggle Today / Last 7 days; search sites.
// Rows where SIM failures exceed 10% of total SIM messages are highlighted.
// ---------------------------------------------------------------------------
function SimDetailPlaceholder({
  mod,
  token,
  onClose
}) {
  const masked = useMaskNames();
  // Tabs: the SMS & SIM detail, and the per-branch shelf message editor
  // (Message configuration — moved here from the left menu, 2026-10).
  const [msgTab, setMsgTab] = useState(mod._tab || "sim"); // "sim" | "config" | "signup"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [win, setWin] = useState("today"); // "today" | "7d"
  const [query, setQuery] = useState("");
  // Column sort. key: "pharmacy" | "simSent" | "simFailed" | "simCards" |
  // "simEnabled" | `mode:<MODE>`. Default worst SIM-fail-rate first.
  const [sort, setSort] = useState({
    key: "simFailed",
    dir: "desc"
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const d = await apiGet(`${mod.path}/sms-detail?window=${win}`, token);
        if (!cancelled) setData(d);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mod, token, win]);
  const allRows = data?.rows || [];

  // Mode columns: union across rows, common ones first.
  const PREFERRED = ["SMS", "EMAIL", "FCM"];
  const seen = new Set();
  for (const r of allRows) for (const k of Object.keys(r.byMode || {})) seen.add(k);
  const modeCols = [...PREFERRED.filter(k => seen.has(k)), ...[...seen].filter(k => !PREFERRED.includes(k)).sort()];
  const q = query.trim().toLowerCase();
  const filtered = q ? allRows.filter(r => (r.pharmacyName || "").toLowerCase().includes(q)) : allRows;

  // Sort getters. SIM failed sorts by fail RATE (then count) so the worst
  // offenders lead regardless of volume.
  const getters = {
    pharmacy: r => (r.pharmacyName || "").toLowerCase(),
    simSent: r => r.sim.delivered ?? 0,
    simFailed: r => (r.sim.failPct ?? -1) * 1e6 + (r.sim.failed ?? 0),
    simCards: r => r.simCards ?? 0,
    simEnabled: r => r.simEnabled ? 1 : 0,
    ...Object.fromEntries(modeCols.map(k => [`mode:${k}`, r => r.byMode[k] ?? 0]))
  };
  const dir = sort.dir === "desc" ? -1 : 1;
  const getter = getters[sort.key] || getters.pharmacy;
  const rows = [...filtered].sort((a, b) => {
    const av = getter(a),
      bv = getter(b);
    let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
    if (cmp === 0) cmp = (a.pharmacyName || "").localeCompare(b.pharmacyName || "");
    return cmp * dir;
  });
  const toggleSort = key => setSort(prev => prev.key === key ? {
    key,
    dir: prev.dir === "asc" ? "desc" : "asc"
  } : {
    key,
    dir: key === "pharmacy" ? "asc" : "desc"
  });
  const arrow = key => sort.key === key ? sort.dir === "asc" ? " ▲" : " ▼" : "";

  // Highlight when SIM failures exceed 10% of total SIM messages.
  const highRisk = r => r.sim.total > 0 && r.sim.failed / r.sim.total > 0.10;
  return <div style={S.drill}>
      {loading && <LoadingOverlay label="Loading SMS detail…" />}
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12
    }}>
        <div style={S.drillTitle}>Messaging</div>
        <button style={S.btnGhost} onClick={onClose}>← Back</button>
      </div>

      <div style={{
      display: "flex",
      gap: 6,
      marginBottom: 12,
      borderBottom: "1px solid #F0F0F0",
      paddingBottom: 8
    }}>
        {[["sim", "SMS & SIM detail"], ["config", "Message configuration"], ["signup", "Patients signed up"]].map(([id, label]) => <button key={id} onClick={() => setMsgTab(id)} style={msgTab === id ? {
        ...S.btnGhost,
        background: "#262626",
        color: "#fff",
        border: "none",
        fontWeight: 600
      } : S.btnGhost}>
            {label}
          </button>)}
      </div>

      {msgTab === "config" ? <MessageConfiguration token={token} /> : msgTab === "signup" ? <ModuleDetail mod={mod} token={token} onClose={onClose} embedded /> : <>

      {error && <div style={S.error}>{error}</div>}

      {/* Window toggle */}
      <div style={{
        display: "flex",
        gap: 8,
        marginBottom: 12,
        alignItems: "center"
      }}>
        <button onClick={() => setWin("today")} style={win === "today" ? {
          ...S.btnGhost,
          background: "#0E8CFF",
          color: "#fff",
          border: "none"
        } : S.btnGhost}>
          Today
        </button>
        <button onClick={() => setWin("7d")} style={win === "7d" ? {
          ...S.btnGhost,
          background: "#0E8CFF",
          color: "#fff",
          border: "none"
        } : S.btnGhost}>
          Last seven days
        </button>
        <span style={{
          fontSize: 12,
          color: "#5D5D5D"
        }}>
          {win === "today" ? "Since midnight (UK time)." : "Trailing 7 days."}
        </span>
      </div>

      {/* Search */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 12
      }}>
        <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
          padding: "6px 10px",
          fontSize: 13,
          border: "1px solid #D9D9D9",
          borderRadius: 6,
          width: 260,
          outline: "none"
        }} />
        {query && <button style={S.btnGhost} onClick={() => setQuery("")}>Clear</button>}
        <span style={{
          fontSize: 12,
          color: "#5D5D5D"
        }}>
          {`${rows.length} site${rows.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 8
      }}>
        Message columns are successful outbound messages by channel. SIM
        delivered = sent straight from the SIM; SIM failed = backend had to
        step in, or the send failed. Rows are highlighted when SIM failures
        exceed 10% of that site's SIM messages.
      </div>

      <div style={{
        overflowX: "auto"
      }}>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("pharmacy")}>
              Pharmacy{arrow("pharmacy")}
            </th>
            {modeCols.map(k => <th key={k} style={{
                ...S.th,
                textAlign: "center",
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort(`mode:${k}`)} title={`Successful ${k} messages`}>{k}{arrow(`mode:${k}`)}</th>)}
            <th style={{
                ...S.th,
                textAlign: "center",
                background: "#F6FFED",
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("simSent")} title="SIM messages sent directly from the SIM card">
              SIM sent{arrow("simSent")}
            </th>
            <th style={{
                ...S.th,
                textAlign: "center",
                background: "#FFF1F0",
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("simFailed")} title="SIM messages that failed or needed backend rescue (sorts by failure rate)">
              SIM failed{arrow("simFailed")}
            </th>
            <th style={{
                ...S.th,
                textAlign: "center",
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("simCards")} title="Distinct devices reporting a SIM in this window">
              No. SIM cards{arrow("simCards")}
            </th>
            <th style={{
                ...S.th,
                textAlign: "center",
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("simEnabled")} title="Whether any device at this site has SIM messaging enabled">
              SIM messaging enabled{arrow("simEnabled")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? <tr>
              <td style={{
                ...S.td,
                color: "#5D5D5D"
              }} colSpan={modeCols.length + 5}>
                {query ? `No pharmacies match “${query}”.` : "No active sites."}
              </td>
            </tr> : rows.map(r => {
              const risk = highRisk(r);
              return <tr key={String(r.pharmacyId)} style={{
                background: risk ? "#fde2e1" : undefined
              }} title={risk ? `${r.sim.failPct}% of SIM messages failed` : ""}>
                <td style={{
                  ...S.td,
                  fontWeight: 600
                }}>{displayName(r.pharmacyName, masked)}</td>
                {modeCols.map(k => <td key={k} style={{
                  ...S.td,
                  textAlign: "center"
                }}>
                    {r.byMode[k] ?? 0}
                  </td>)}
                <td style={{
                  ...S.td,
                  textAlign: "center",
                  color: "#059669"
                }}>
                  {r.sim.delivered}
                </td>
                <td style={{
                  ...S.td,
                  textAlign: "center",
                  color: r.sim.failed > 0 ? "#CF1322" : "#434343",
                  fontWeight: r.sim.failed > 0 ? 600 : 400
                }}>
                  {r.sim.failed}
                  {r.sim.failPct != null && r.sim.total > 0 && <span style={{
                    color: "#5D5D5D",
                    fontSize: 11,
                    marginLeft: 4
                  }}>
                      ({r.sim.failPct}%)
                    </span>}
                </td>
                <td style={{
                  ...S.td,
                  textAlign: "center"
                }}>{r.simCards}</td>
                <td style={{
                  ...S.td,
                  textAlign: "center"
                }}>
                  {r.simEnabled ? <span style={{
                    color: "#059669",
                    fontWeight: 600
                  }}>Yes</span> : <span style={{
                    color: "#5D5D5D"
                  }}>No</span>}
                </td>
              </tr>;
            })}
        </tbody>
      </table>
      </div>
      </>}
    </div>;
}

// ---------------------------------------------------------------------------
// Periodic entries card — title ratio = sites with any entries / total.
// One line "Pharmacies with overdue items" (green 0 / red >0); clicking the
// card opens the periodic-tasks grid, clicking the overdue line opens it
// filtered to overdue.
// ---------------------------------------------------------------------------
function PeriodicEntriesCard({
  token,
  onOpen,
  onOpenOverdue
}) {
  const [summary, setSummary] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const g = await apiGet("periodic-tasks/grid", token);
        if (!cancelled) setSummary(g?.summary || null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);
  const overdue = summary?.sitesOverdue ?? null;
  const missing = summary?.sitesMissing ?? null;
  const withEntries = summary?.sitesWithEntries ?? null;
  const total = summary?.totalSites ?? null;
  const hasOverdue = typeof overdue === "number" && overdue > 0;
  const hasMissing = typeof missing === "number" && missing > 0;
  const borderColor = !loaded ? "#FA8C16" : hasOverdue || hasMissing ? "#F5222D" : "#52C41A";
  return <div style={{
    ...S.card,
    border: `3px solid ${borderColor}`
  }} onClick={() => onOpen()}>
      <div style={S.cardTitle}>
        Periodic entries
        <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }} title="Pharmacies with at least one recorded periodic value, out of all sites.">
          {" "}({withEntries ?? "…"}/{total ?? "…"})
        </span>
      </div>
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginBottom: 8,
      lineHeight: 1.4
    }}>
        Group-defined recurring data items recorded per pharmacy. Click to open
        the grid.
      </div>
      <div style={{
      ...S.metaRow,
      background: hasOverdue ? "#fde2e1" : loaded ? "#F6FFED" : undefined,
      borderRadius: 4,
      padding: "2px 6px",
      margin: "0 -6px",
      cursor: "pointer"
    }} title="Pharmacies with at least one overdue periodic item. Click to view them." onClick={e => {
      e.stopPropagation();
      onOpenOverdue();
    }}>
        <span>Pharmacies with overdue items</span>
        <span style={hasOverdue ? S.urgent : S.ok}>
          {overdue ?? "…"}
        </span>
      </div>
      <div style={{
      ...S.metaRow,
      background: hasMissing ? "#fde2e1" : loaded ? "#F6FFED" : undefined,
      borderRadius: 4,
      padding: "2px 6px",
      margin: "0 -6px"
    }} title="Pharmacies with at least one task never recorded.">
        <span>Sites with missing entries</span>
        <span style={hasMissing ? S.urgent : S.ok}>
          {missing ?? "…"}
        </span>
      </div>
      {error && <div style={S.error}>{error}</div>}
    </div>;
}

// ---------------------------------------------------------------------------
// Messaging card — bespoke summary. Clicking anywhere opens the Messaging
// panel (tabs: SMS & SIM detail, Message configuration, Patients signed up):
//   1) Patients signed up %  → the panel's "Patients signed up" tab (enrolment grid)
//   2) Today's messaging (SMS/EMAIL/FCM) — informational
//   3) Number of sites with SIM issues → the Messaging panel
// Each line carries a styled hover tooltip explaining what it means.
// ---------------------------------------------------------------------------
function MessagingCard({
  mod,
  token,
  onSelect,
  onSelectSim
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        setLoaded(false);
        const d = await apiGet(`${mod.path}/fleet-summary`, token);
        if (!cancelled) setData(d && typeof d === "object" ? d : null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mod, token]);
  const sim = data?.sim;
  const SIM_BG = {
    ok: undefined,
    warn: "#FFF7E6",
    fail: "#fde2e1"
  };
  const simBg = sim ? SIM_BG[sim.tone] : undefined;
  const simNumStyle = sim && sim.tone === "fail" ? {
    color: "#CF1322",
    fontWeight: 700
  } : sim && sim.tone === "warn" ? {
    color: "#b45309",
    fontWeight: 700
  } : {
    color: "#059669",
    fontWeight: 700
  };

  // Border mirrors the SIM severity so the card flags at a glance.
  const borderColor = !loaded ? "#FA8C16" : sim?.tone === "fail" ? "#F5222D" : sim?.tone === "warn" ? "#FA8C16" : "#52C41A";
  const line = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0",
    fontSize: 13,
    color: "#434343"
  };
  const clickable = {
    cursor: "pointer",
    borderRadius: 4
  };
  return <div style={{
    ...S.card,
    border: `3px solid ${borderColor}`,
    cursor: "pointer"
  }} onClick={() => onSelectSim(mod)} title="Open Messaging: SMS & SIM detail and Message configuration">
      <div style={S.cardTitle}>{mod.title}</div>

      {error && <div style={S.error}>{error}</div>}

      {/* 1) Patients signed up — opens enrolment grid */}
      <InfoTip block text="Across all sites: patients who chose SMS, FCM or email, or who declined messaging, as a share of all patients on the books. Click to see the per-pharmacy enrolment breakdown.">
        <div style={{
        ...line,
        ...clickable,
        fontWeight: 600,
        gap: 12
      }} onClick={e => {
        e.stopPropagation();
        onSelectSim({
          ...mod,
          _tab: "signup"
        });
      }} onMouseEnter={e => e.currentTarget.style.background = "#F5F5F5"} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
          <span>Patients signed up</span>
          <span style={{
          color: "#0E8CFF",
          fontWeight: 700
        }}>
            {data?.signedUpPct != null ? `${data.signedUpPct}%` : loaded ? "—" : "…"}
          </span>
        </div>
      </InfoTip>

      <div style={{
      height: 10
    }} />

      {/* 2) Today's messaging — informational, single header tooltip */}
      <InfoTip block text="Outbound messages sent today (since midnight, UK time) across the whole group, broken down by channel.">
        <div style={{
        fontWeight: 600,
        fontSize: 13,
        color: "#262626",
        borderBottom: "1px dotted #D9D9D9",
        paddingBottom: 1,
        display: "inline-block"
      }}>
          Today's messaging
        </div>
      </InfoTip>
      <div style={{
      ...line,
      paddingLeft: 14
    }}>
        <span>SMS</span><span>{data?.today?.SMS ?? (loaded ? 0 : "…")}</span>
      </div>
      <div style={{
      ...line,
      paddingLeft: 14
    }}>
        <span>EMAIL</span><span>{data?.today?.EMAIL ?? (loaded ? 0 : "…")}</span>
      </div>
      <div style={{
      ...line,
      paddingLeft: 14
    }}>
        <span>FCM</span><span>{data?.today?.FCM ?? (loaded ? 0 : "…")}</span>
      </div>

      <div style={{
      height: 10
    }} />

      {/* 3) Sites with SIM issues — opens SIM detail (placeholder) */}
      <InfoTip block text="Sites where today's SIM send problems (the backend had to step in, or the message status is FAILED) exceed 5% of that site's SMS sent today. Background is amber if the worst such site is between 5% and 20%, red if any site exceeds 20%. Click for the SIM detail.">
        <div style={{
        ...line,
        ...clickable,
        background: simBg,
        gap: 12,
        padding: simBg ? "4px 6px" : "4px 0",
        margin: simBg ? "0 -6px" : 0,
        fontWeight: 600
      }} onClick={() => onSelectSim(mod)}>
          <span>Number of sites — SIM issues</span>
          <span style={simNumStyle}>
            {sim ? sim.issueSites : loaded ? 0 : "…"}
          </span>
        </div>
      </InfoTip>
    </div>;
}
function ModuleCard({
  mod,
  token,
  onSelect
}) {
  const [active, setActive] = useState(null);
  const [dormant, setDormant] = useState(null); // v43: configured but quiet
  const [total, setTotal] = useState(null);
  const [urgent, setUrgent] = useState({});
  const [okCount, setOkCount] = useState(null);
  const [figures, setFigures] = useState({});
  const [rollup, setRollup] = useState(null); // single cardRollup payload
  const [rollups, setRollups] = useState({}); // cardRollups: { [endpoint]: payload }
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false); // false until all data is in

  const load = useCallback(async () => {
    try {
      setError(null);
      setLoaded(false);

      // "Ever used / total" ratio (no date window). Defaults to {path}/ever-used,
      // but a module may point it at a combined endpoint via everUsedEndpoint.
      if (mod.everUsedRatio) {
        try {
          const ep = mod.everUsedEndpoint || "ever-used";
          const eu = await apiGet(`${mod.path}/${ep}`, token);
          setActive(typeof eu?.everUsed === "number" ? eu.everUsed : 0);
          setTotal(typeof eu?.total === "number" ? eu.total : null);
        } catch (e) {
          setActive(0);
          setTotal(null);
        }
      } else {
        const a = await apiGet(`${mod.path}/active-pharmacies`, token);
        setActive(Array.isArray(a) ? a.length : 0);
        // v43: sites that are set up but have logged nothing inside the
        // activity window. They stay IN every count above — this row exists
        // so "configured and failing" and "configured and abandoned" can be
        // told apart without either one dropping off the dashboard.
        setDormant(Array.isArray(a) ? a.filter(x => x && x.dormant).length : 0);
        if (mod.showActiveRatioInTitle) {
          try {
            const sz = await apiGet(`fleet-size`, token);
            setTotal(typeof sz?.total === "number" ? sz.total : null);
          } catch (e) {
            setTotal(null);
          }
        }
      }
      const ur = {};
      for (const r of mod.urgentRules) {
        try {
          const failing = await apiGet(`${mod.path}/${r.rule}`, token);
          ur[r.rule] = Array.isArray(failing) ? failing.length : 0;
        } catch (e) {
          ur[r.rule] = "?";
        }
      }
      setUrgent(ur);

      // Custom single-endpoint rollup row (e.g. RP coverage today).
      if (mod.cardRollup) {
        try {
          const r = await apiGet(`${mod.path}/${mod.cardRollup.endpoint}`, token);
          setRollup(r && typeof r === "object" ? r : null);
        } catch (e) {
          setRollup(null);
        }
      }

      // Multiple rollup rows, possibly sharing endpoints — fetch each endpoint once.
      if (Array.isArray(mod.cardRollups) && mod.cardRollups.length) {
        const endpoints = [...new Set(mod.cardRollups.map(c => c.endpoint))];
        const out = {};
        for (const ep of endpoints) {
          try {
            const r = await apiGet(`${mod.path}/${ep}`, token);
            out[ep] = r && typeof r === "object" ? r : null;
          } catch (e) {
            out[ep] = null;
          }
        }
        setRollups(out);
      }

      // Reassurance figure: sites checked today with all fridges OK.
      if (mod.okStatusRule) {
        try {
          const statuses = await apiGet(`${mod.path}/${mod.okStatusRule}`, token);
          const arr = Array.isArray(statuses) ? statuses : [];
          setOkCount(arr.filter(s => s.status === "ok").length);
          // Sum any configured per-pharmacy figures across the fleet.
          if (mod.statusFigures) {
            const sums = {};
            for (const f of mod.statusFigures) {
              sums[f.field] = arr.reduce((t, s) => t + (Number(s[f.field]) || 0), 0);
            }
            setFigures(sums);
          }
        } catch (e) {
          setOkCount("?");
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoaded(true);
    }
  }, [mod, token]);
  useEffect(() => {
    load();
  }, [load]);

  // Severity-ranked border. A rule's `severity` is "fail" (red) or "warn"
  // (amber); unmarked rules default to "fail" so other modules keep their
  // red-on-any-issue behaviour. Worst active severity wins.
  const failFiring = mod.urgentRules.some(r => (r.severity ?? "fail") === "fail" && (urgent[r.rule] ?? 0) > 0);
  const dormantFiring = !!(mod.dormantRow && typeof dormant === "number" && dormant > 0);
  const warnFiring = dormantFiring || mod.urgentRules.some(r => r.severity === "warn" && (urgent[r.rule] ?? 0) > 0);

  // A configured rollup with a non-zero count also pushes the card red.
  const rollupFiring = !!(mod.cardRollup && rollup && Number(rollup[mod.cardRollup.countField]) > 0);

  // Multi-rollup firing: a "fail"-tone rollup >0 is red, "warn"-tone >0 is amber.
  const rollupsFail = Array.isArray(mod.cardRollups) && mod.cardRollups.some(c => (c.tone ?? "fail") === "fail" && Number((rollups[c.endpoint] || {})[c.field]) > 0);
  const rollupsWarn = Array.isArray(mod.cardRollups) && mod.cardRollups.some(c => c.tone === "warn" && Number((rollups[c.endpoint] || {})[c.field]) > 0);

  // Three-state border: amber while loading, then red for a real failure,
  // amber for a warning-only state, green if all clear. Wider + bolder than 1px.
  const borderColor = !loaded ? "#FA8C16" // amber — still loading
  : failFiring || rollupFiring || rollupsFail ? "#F5222D" // red — action needed
  : warnFiring || rollupsWarn ? "#FA8C16" // amber — warning only
  : "#52C41A"; // green — all clear

  return <div style={{
    ...S.card,
    border: `3px solid ${borderColor}`
  }} onClick={() => onSelect(mod)}>
      <div style={S.cardTitle}>
        {mod.title}
        {mod.showActiveRatioInTitle && <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }} title={mod.configuredRatio || "Pharmacies using this module recently, out of all sites in the group."}>
            {" "}({active ?? "…"}/{total ?? "…"})
          </span>}
        {mod.everUsedRatio && <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }} title="Pharmacies that have ever recorded an entry in this module, out of all sites in the group.">
            {" "}({active ?? "…"}/{total ?? "…"})
          </span>}
      </div>
      {mod.blurb && <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginBottom: 8,
      lineHeight: 1.4
    }}>
          {mod.blurb}
        </div>}
      {mod.dormantRow && (() => {
      const known = typeof dormant === "number";
      const has = known && dormant > 0;
      return <div style={{
        ...S.metaRow,
        background: has ? "#FFF7E6" : undefined,
        borderRadius: has ? 4 : undefined,
        padding: has ? "2px 6px" : undefined,
        margin: has ? "0 -6px" : undefined
      }} title={mod.dormantRow.hint || ""}>
            <span>{mod.dormantRow.label}</span>
            <span style={has ? {
          color: "#b45309",
          fontWeight: 600
        } : S.ok}>
              {known ? dormant : "…"}
            </span>
          </div>;
    })()}
      {!mod.showActiveRatioInTitle && !mod.everUsedRatio && <div style={S.metaRow} title="Pharmacies that have used this module in the last 90 days.">
          <span>Active pharmacies</span>
          <span>{active ?? "…"}</span>
        </div>}
      {mod.cardRollup && (() => {
      const cfg = mod.cardRollup;
      const cnt = rollup ? Number(rollup[cfg.countField]) : null;
      const tot = rollup ? Number(rollup[cfg.totalField]) : null;
      const known = typeof cnt === "number" && !Number.isNaN(cnt);
      const has = known && cnt > 0;
      // Green when all covered (0 uncovered), red when any uncovered.
      const rowBg = !known ? undefined : has ? "#fde2e1" : "#F6FFED";
      return <div style={{
        ...S.metaRow,
        background: rowBg,
        borderRadius: rowBg ? 4 : undefined,
        padding: rowBg ? "2px 6px" : undefined,
        margin: rowBg ? "0 -6px" : undefined
      }} title={cfg.hint || ""}>
            <span>{cfg.label}</span>
            <span style={has ? S.urgent : S.ok}>
              {known ? `${cnt} / ${tot ?? "…"}` : "…"}
            </span>
          </div>;
    })()}
      {Array.isArray(mod.cardRollups) && mod.cardRollups.map((cfg, idx) => {
      const payload = rollups[cfg.endpoint];
      const cnt = payload ? Number(payload[cfg.field]) : null;
      const known = typeof cnt === "number" && !Number.isNaN(cnt);
      const has = known && cnt > 0;
      const tone = cfg.tone ?? "fail";
      // warn → amber, fail → pink, only when count > 0.
      const TONE_BG = {
        warn: "#FFF7E6",
        fail: "#fde2e1"
      };
      const rowBg = has ? TONE_BG[tone] : undefined;
      const numStyle = has ? tone === "warn" ? {
        color: "#b45309",
        fontWeight: 600
      } // amber text
      : S.urgent // red text
      : S.ok;
      return <div key={`rollup-${idx}`} style={{
        ...S.metaRow,
        background: rowBg,
        borderRadius: rowBg ? 4 : undefined,
        padding: rowBg ? "2px 6px" : undefined,
        margin: rowBg ? "0 -6px" : undefined
      }} title={cfg.hint || ""}>
            <span>{cfg.label}</span>
            <span style={numStyle}>{known ? cnt : "…"}</span>
          </div>;
    })}
      {mod.urgentRules.map(r => {
      const n = urgent[r.rule] ?? 0;
      const sev = r.severity ?? "fail";
      // Tint the row background when it has entries: red for fail-level,
      // amber for warn-level. Zero-count rows stay plain.
      const rowBg = typeof n === "number" && n > 0 ? sev === "warn" ? "#FFF7E6" : "#fde2e1" : undefined;
      return <div key={r.rule} style={{
        ...S.metaRow,
        background: rowBg,
        borderRadius: rowBg ? 4 : undefined,
        padding: rowBg ? "2px 6px" : undefined,
        margin: rowBg ? "0 -6px" : undefined
      }} title={r.hint || ""}>
            <span>{r.label}</span>
            <span style={n > 0 ? S.urgent : S.ok}>
              {urgent[r.rule] ?? "…"}
            </span>
          </div>;
    })}
      {(mod.statusFigures || []).map(f => {
      const v = figures[f.field];
      const known = typeof v === "number";
      const has = known && v > 0;
      const TONE_BG = {
        fail: "#fde2e1",
        warn: "#FFF7E6",
        ok: "#F6FFED"
      };
      // Pick the tone: value > 0 uses f.tone; value 0 uses f.zeroTone if set.
      const tone = !known ? null : v > 0 ? f.tone : f.zeroTone || null;
      const bg = tone ? TONE_BG[tone] : undefined;
      // Number colour: green when it's a good state, red/urgent when it's a problem.
      const numStyle = tone === "ok" || !has && f.zeroTone === "ok" ? S.ok : has ? S.urgent : S.ok;
      return <div key={f.field} style={{
        ...S.metaRow,
        background: bg,
        borderRadius: bg ? 4 : undefined,
        padding: bg ? "2px 6px" : undefined,
        margin: bg ? "0 -6px" : undefined
      }} title={f.hint || ""}>
            <span>{f.label}</span>
            <span style={numStyle}>{v ?? "…"}</span>
          </div>;
    })}
      {mod.okStatusRule && <div style={{
      ...S.metaRow,
      background: typeof okCount === "number" && okCount > 0 ? "#F6FFED" : undefined,
      borderRadius: typeof okCount === "number" && okCount > 0 ? 4 : undefined,
      padding: typeof okCount === "number" && okCount > 0 ? "2px 6px" : undefined,
      margin: typeof okCount === "number" && okCount > 0 ? "0 -6px" : undefined
    }} title="Pharmacies that are fully up to date on this module right now.">
          <span>Sites checked and OK</span>
          <span style={S.ok}>{okCount ?? "…"}</span>
        </div>}
      {error && <div style={S.error}>{error}</div>}
    </div>;
}

// ---------------------------------------------------------------------------
// Visitor logs inline expand — that pharmacy's visitors, reverse-chronological.
// Stale (on site, before today) = pink; current (on site, today) = amber.
// ---------------------------------------------------------------------------
function VisitorList({
  visitors
}) {
  const list = visitors || [];
  if (list.length === 0) {
    return <div style={{
      fontSize: 12,
      color: "#5D5D5D"
    }}>No visitors recorded.</div>;
  }
  const rowBg = st => st === "stale" ? "#fde2e1" : st === "current" ? "#FFF7E6" : undefined;
  const stateLabel = st => st === "stale" ? "On site (stale)" : st === "current" ? "On site" : "Signed out";
  const currentCount = list.filter(v => v.state === "current").length;
  const staleCount = list.filter(v => v.state === "stale").length;
  return <div>
      <div style={{
      fontWeight: 600,
      fontSize: 13,
      marginBottom: 6,
      color: "#434343"
    }}>
        Visitors{" "}
        <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }}>
          ({list.length}, most recent first)
        </span>
        {currentCount > 0 && <span style={{
        marginLeft: 8,
        fontSize: 11,
        color: "#b45309",
        background: "#FFF7E6",
        borderRadius: 4,
        padding: "1px 6px"
      }}>
            {currentCount} on site
          </span>}
        {staleCount > 0 && <span style={{
        marginLeft: 6,
        fontSize: 11,
        color: "#CF1322",
        background: "#fde2e1",
        borderRadius: 4,
        padding: "1px 6px"
      }}>
            {staleCount} stale
          </span>}
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Signed in</th>
            <th style={S.th}>Name</th>
            <th style={S.th}>Organisation</th>
            <th style={S.th}>Purpose</th>
            <th style={S.th}>Contact</th>
            <th style={S.th}>Signed out</th>
            <th style={{
            ...S.th,
            textAlign: "center"
          }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {list.map(v => <tr key={String(v.visitorId)} style={{
          background: rowBg(v.state)
        }}>
              <td style={S.td}>{fmtDateTime(v.createdAt)}</td>
              <td style={{
            ...S.td,
            fontWeight: 600
          }}>{v.fullName || "—"}</td>
              <td style={S.td}>{v.organization || "—"}</td>
              <td style={S.td}>{v.purposeOfVisit || "—"}</td>
              <td style={S.td}>{v.contactNumber || "—"}</td>
              <td style={S.td}>{v.signOutAt ? fmtDateTime(v.signOutAt) : "—"}</td>
              <td style={{
            ...S.td,
            textAlign: "center"
          }}>{stateLabel(v.state)}</td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}

// ---------------------------------------------------------------------------
// Patient safety inline expand — that pharmacy's incidents, reverse-chrono.
// Columns: date/time, degree of harm, LFPSE, RP reporting.
// ---------------------------------------------------------------------------
function PatientSafetyIncidents({
  incidents
}) {
  const list = incidents || [];

  // harmLevelId is a readable enum (e.g. NEAR_MISS, NO_HARM, DEATH) — prettify.
  const prettyHarm = h => h ? String(h).toLowerCase().replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase()) : "—";
  // Tint by severity keyword so DEATH / SEVERE stand out.
  const harmTone = h => {
    const v = String(h || "").toUpperCase();
    if (v.includes("DEATH") || v.includes("SEVERE")) return "#fde2e1";
    if (v.includes("MODERATE")) return "#FFF7E6";
    return undefined;
  };
  const yesNo = b => b ? <span style={{
    color: "#059669",
    fontWeight: 600
  }}>Yes</span> : <span style={{
    color: "#CF1322",
    fontWeight: 600
  }}>No</span>;
  if (list.length === 0) {
    return <div style={{
      fontSize: 12,
      color: "#5D5D5D"
    }}>No incidents recorded.</div>;
  }
  return <div>
      <div style={{
      fontWeight: 600,
      fontSize: 13,
      marginBottom: 6,
      color: "#434343"
    }}>
        Incidents{" "}
        <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }}>
          ({list.length}, most recent first)
        </span>
      </div>
      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Date / time</th>
            <th style={S.th}>Degree of harm</th>
            <th style={{
            ...S.th,
            textAlign: "center"
          }}>LFPSE</th>
            <th style={S.th}>LFPSE ref</th>
            <th style={{
            ...S.th,
            textAlign: "center"
          }}>RP reported</th>
          </tr>
        </thead>
        <tbody>
          {list.map(i => {
          // Incomplete = LFPSE ticked but no reference → highlight red.
          const incomplete = i.lfpseIncomplete ?? (i.submitLfpse && !(i.lfpseRef && String(i.lfpseRef).trim()));
          const rowBg = incomplete ? "#fde2e1" : harmTone(i.harmLevelId);
          return <tr key={String(i.incidentId)} style={{
            background: rowBg
          }} title={incomplete ? "LFPSE submitted but no reference recorded" : ""}>
                <td style={S.td}>{fmtDateTime(i.incidentDate)}</td>
                <td style={S.td}>{prettyHarm(i.harmLevelId)}</td>
                <td style={{
              ...S.td,
              textAlign: "center"
            }}>{yesNo(i.submitLfpse)}</td>
                <td style={{
              ...S.td,
              color: incomplete ? "#CF1322" : "#262626",
              fontWeight: incomplete ? 600 : 400
            }}>
                  {i.lfpseRef && String(i.lfpseRef).trim() ? i.lfpseRef : i.submitLfpse ? "missing" : "—"}
                </td>
                <td style={{
              ...S.td,
              textAlign: "center"
            }}>{yesNo(i.rpNotified)}</td>
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}

// ---------------------------------------------------------------------------
// CD register inline expand — that pharmacy's individual registers (balance
// checking) plus its open discrepancies. Rendered inside the accordion row.
// ---------------------------------------------------------------------------
function CdRegisterExpand({
  detail
}) {
  const registers = detail.registers || [];
  const discrepancies = detail.discrepancies || [];
  const STATE = {
    ok: {
      bg: "#F6FFED",
      label: "Up to date"
    },
    overdue: {
      bg: "#fde2e1",
      label: "Overdue"
    },
    never: {
      bg: "#FFF7E6",
      label: "Never checked"
    }
  };

  // Worst-state first, then name.
  const rank = {
    overdue: 0,
    never: 1,
    ok: 2
  };
  const regsSorted = [...registers].sort((a, b) => {
    const cmp = (rank[a.state] ?? 3) - (rank[b.state] ?? 3);
    return cmp !== 0 ? cmp : (a.registerName || "").localeCompare(b.registerName || "");
  });
  const panelHead = {
    fontWeight: 600,
    fontSize: 13,
    marginBottom: 6,
    color: "#434343"
  };
  return <div style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    alignItems: "start"
  }}>
      {/* Balance-checking panel */}
      <div>
        <div style={panelHead}>
          Registers — balance checking{" "}
          <span style={{
          fontWeight: 400,
          color: "#5D5D5D"
        }}>
            ({registers.length})
          </span>
        </div>
        {registers.length === 0 ? <div style={{
        fontSize: 12,
        color: "#5D5D5D"
      }}>No active registers.</div> : <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Register</th>
                <th style={S.th}>Balance</th>
                <th style={S.th}>Last checked</th>
                <th style={{
              ...S.th,
              textAlign: "center"
            }}>State</th>
              </tr>
            </thead>
            <tbody>
              {regsSorted.map(r => {
            const sc = STATE[r.state] || {
              bg: undefined,
              label: r.state
            };
            return <tr key={String(r.registerId)} style={{
              background: sc.bg
            }}>
                    <td style={{
                ...S.td,
                fontWeight: 600
              }}>
                      {r.registerName || "—"}
                      {r.category && <span style={{
                  fontWeight: 400,
                  color: "#5D5D5D",
                  marginLeft: 6,
                  fontSize: 11
                }}>
                          {r.category}
                        </span>}
                      {r.totalExpired > 0 && <span style={{
                  marginLeft: 6,
                  fontSize: 11,
                  color: "#b45309",
                  background: "#FFFBE6",
                  border: "1px solid #FFD666",
                  borderRadius: 4,
                  padding: "1px 5px"
                }} title="Expired stock recorded on this register">
                          {r.totalExpired} expired
                        </span>}
                    </td>
                    <td style={S.td}>{r.currentBalance ?? "—"}</td>
                    <td style={S.td}>
                      {r.balanceCheckedAt ? fmtDate(r.balanceCheckedAt) : "never"}
                      {r.state === "overdue" && r.daysOverdue != null && <span style={{
                  ...S.urgent,
                  marginLeft: 6,
                  fontSize: 11
                }}>
                          +{r.daysOverdue}d
                        </span>}
                    </td>
                    <td style={{
                ...S.td,
                textAlign: "center"
              }}>{sc.label}</td>
                  </tr>;
          })}
            </tbody>
          </table>}
      </div>

      {/* Discrepancies panel */}
      <div>
        <div style={panelHead}>
          Open discrepancies{" "}
          <span style={{
          fontWeight: 400,
          color: discrepancies.length > 0 ? "#CF1322" : "#5D5D5D"
        }}>
            ({discrepancies.length})
          </span>
        </div>
        {discrepancies.length === 0 ? <div style={{
        fontSize: 12,
        color: "#059669"
      }}>
            No open discrepancies.
          </div> : <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Register</th>
                <th style={S.th}>Description</th>
                <th style={S.th}>Raised</th>
                <th style={{
              ...S.th,
              textAlign: "center"
            }}>Days open</th>
              </tr>
            </thead>
            <tbody>
              {discrepancies.map(d => <tr key={String(d.discrepancyId)} style={{
            background: "#fde2e1"
          }}>
                  <td style={{
              ...S.td,
              fontWeight: 600
            }}>
                    {d.registerName || "—"}
                  </td>
                  <td style={S.td}>{d.description || "—"}</td>
                  <td style={S.td}>
                    {fmtDate(d.raisedAt)}
                    {d.raisedBy && <span style={{
                color: "#5D5D5D",
                fontSize: 11,
                marginLeft: 4
              }}>
                        by {d.raisedBy}
                      </span>}
                  </td>
                  <td style={{
              ...S.td,
              textAlign: "center"
            }}>
                    <span style={S.urgent}>{d.daysOpen ?? "—"}</span>
                  </td>
                </tr>)}
            </tbody>
          </table>}
      </div>
    </div>;
}

// ---------------------------------------------------------------------------
// Drill-in view — shows urgent rule offenders + raw snapshot per pharmacy
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// AreaFleetGrid — "all branches" grid for area-based modules (cleaning and
// date-checking areas). One row per branch, INCLUDING branches not using the
// module (hidden behind a "Show inactive" toggle, amber when shown). Sortable
// columns: active areas, inactive areas, never cleaned/checked, up to date
// (logged & not overdue), overdue. Row colour: green when nothing overdue and
// every active area has been logged; pale red when anything is overdue or has
// never been logged; amber when the branch has no active areas at all.
// Click a branch → AreaHistoryDialog (areas left, per-area log history right).
// ---------------------------------------------------------------------------
const AF_RED = "#fde2e1",
  AF_AMBER = "#FFF7E6",
  AF_GREEN = "#F6FFED";
function AreaFleetGrid({
  mod,
  token,
  noun
}) {
  const masked = useMaskNames();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [sort, setSort] = useState({
    key: "overdue",
    dir: "desc"
  });
  const [openPharmacy, setOpenPharmacy] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await apiGet(`${mod.path}/area-overview`, token);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mod, token]);
  const all = rows || [];
  const inactiveCount = all.filter(r => !r.inUse).length;
  const q = query.trim().toLowerCase();
  const visible = all.filter(r => showInactive || r.inUse).filter(r => !q || (r.pharmacyName || "").toLowerCase().includes(q));

  // Branch row colour. Amber (inactive) wins so unused branches stand apart;
  // active branches are red on any overdue OR never-logged area, else green.
  const rowBg = r => !r.inUse ? AF_AMBER : r.overdue > 0 || r.neverLogged > 0 ? AF_RED : AF_GREEN;
  const cols = [{
    key: "pharmacy",
    label: "Pharmacy",
    get: r => (r.pharmacyName || "").toLowerCase()
  }, {
    key: "activeAreas",
    label: "Active areas",
    get: r => r.activeAreas ?? 0
  }, {
    key: "inactiveAreas",
    label: "Inactive areas",
    get: r => r.inactiveAreas ?? 0
  }, {
    key: "neverLogged",
    label: `Never ${noun}`,
    get: r => r.neverLogged ?? 0
  }, {
    key: "upToDate",
    label: "Up to date",
    get: r => r.upToDate ?? 0
  }, {
    key: "overdue",
    label: "Overdue",
    get: r => r.overdue ?? 0
  }];
  const dir = sort.dir === "desc" ? -1 : 1;
  const activeCol = cols.find(c => c.key === sort.key) || cols[5];
  const sorted = [...visible].sort((a, b) => {
    const av = activeCol.get(a),
      bv = activeCol.get(b);
    let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
    if (cmp === 0) cmp = (a.pharmacyName || "").localeCompare(b.pharmacyName || "");
    return cmp * dir;
  });
  const toggleSort = key => setSort(prev => prev.key === key ? {
    key,
    dir: prev.dir === "asc" ? "desc" : "asc"
  } : {
    key,
    dir: key === "pharmacy" ? "asc" : "desc"
  });
  const arrow = key => sort.key === key ? sort.dir === "asc" ? " ▲" : " ▼" : "";
  const attention = all.filter(r => r.inUse && (r.overdue > 0 || r.neverLogged > 0)).length;
  const good = all.filter(r => r.inUse && r.overdue === 0 && r.neverLogged === 0).length;
  return <div style={{
    marginTop: 8,
    marginBottom: 18
  }}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 6
    }}>
        <div style={{
        fontWeight: 600
      }}>
          Branch status{" "}
          <span style={{
          fontWeight: 400,
          color: "#5D5D5D",
          fontSize: 13
        }}>
            ({all.length} branch{all.length === 1 ? "" : "es"},{" "}
            {inactiveCount} not using the module)
          </span>
        </div>
        <button style={S.btn} onClick={() => setConfigOpen(true)} title="View every branch's active areas and cascade an area to all branches">
          Configuration
        </button>
      </div>

      {loading && <div style={{
      fontSize: 13,
      color: "#5D5D5D"
    }}>Loading branches…</div>}
      {error && <div style={S.error}>{error}</div>}

      {!loading && !error && <>
          {/* Summary chips */}
          <div style={{
        display: "flex",
        gap: 8,
        flexWrap: "wrap",
        marginBottom: 10
      }}>
            <span style={{
          background: AF_RED,
          borderRadius: 6,
          padding: "3px 10px",
          fontSize: 12,
          fontWeight: 600,
          color: "#434343"
        }}>
              Needs attention: {attention}
            </span>
            <span style={{
          background: AF_GREEN,
          borderRadius: 6,
          padding: "3px 10px",
          fontSize: 12,
          fontWeight: 600,
          color: "#434343"
        }}>
              All up to date: {good}
            </span>
            <span style={{
          background: AF_AMBER,
          borderRadius: 6,
          padding: "3px 10px",
          fontSize: 12,
          fontWeight: 600,
          color: "#434343"
        }}>
              Inactive: {inactiveCount}
            </span>
          </div>

          {/* Search + Show inactive toggle */}
          <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 10
      }}>
            <button style={{
          ...S.btnGhost,
          background: showInactive ? AF_AMBER : "#F5F5F5"
        }} onClick={() => setShowInactive(v => !v)} title="Include branches with no active areas (shown amber)">
              {showInactive ? "Hide inactive" : "Show inactive"}
              {inactiveCount > 0 && ` (${inactiveCount})`}
            </button>
            <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
          padding: "6px 10px",
          fontSize: 13,
          border: "1px solid #D9D9D9",
          borderRadius: 6,
          width: 260,
          outline: "none"
        }} />
            {query && <button style={S.btnGhost} onClick={() => setQuery("")}>Clear</button>}
            <span style={{
          fontSize: 12,
          color: "#5D5D5D"
        }}>
              {query ? `${sorted.length} match${sorted.length === 1 ? "" : "es"}` : `${sorted.length} branch${sorted.length === 1 ? "" : "es"}`}
            </span>
          </div>

          <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 8
      }}>
            One row per branch. "Up to date" = active areas {noun} and not
            overdue. Click a branch to see its areas and each area's log history.
          </div>

          {sorted.length === 0 ? <div style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>
              {query ? `No branches match “${query}”.` : "No branches to show."}
            </div> : <table style={S.table}>
              <thead>
                <tr>
                  {cols.map(c => <th key={c.key} style={{
              ...S.th,
              cursor: "pointer",
              userSelect: "none"
            }} onClick={() => toggleSort(c.key)} title={`Sort by ${c.label.toLowerCase()}`}>
                      {c.label}{arrow(c.key)}
                    </th>)}
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => <tr key={String(r.pharmacyId)} onClick={() => setOpenPharmacy(r)} style={{
            cursor: "pointer",
            background: rowBg(r)
          }} title="Click for this branch's areas and log history">
                    <td style={{
              ...S.td,
              fontWeight: 600
            }}>
                      {displayName(r.pharmacyName, masked)}
                      {!r.inUse && <span style={{
                fontWeight: 400,
                fontSize: 11,
                color: "#AD4E00",
                marginLeft: 6
              }}>
                          (not using module)
                        </span>}
                    </td>
                    <td style={S.td}>{r.activeAreas ?? 0}</td>
                    <td style={S.td}>{r.inactiveAreas ?? 0}</td>
                    <td style={S.td}>{r.neverLogged ?? 0}</td>
                    <td style={S.td}>{r.upToDate ?? 0}</td>
                    <td style={S.td}>{r.overdue ?? 0}</td>
                  </tr>)}
              </tbody>
            </table>}
        </>}

      {openPharmacy && <AreaHistoryDialog mod={mod} token={token} pharmacy={openPharmacy} noun={noun} onClose={() => setOpenPharmacy(null)} />}

      {configOpen && <AreaConfigDialog mod={mod} token={token} noun={noun} onClose={() => setConfigOpen(false)} />}
    </div>;
}

// ---------------------------------------------------------------------------
// AreaHistoryDialog — popup for one branch. Left panel lists every area
// (active first; inactive areas amber, overdue / never-logged pale red,
// up-to-date green). Clicking an area loads that area's log history into the
// right panel (date, by, notes), newest first.
// ---------------------------------------------------------------------------
function AreaHistoryDialog({
  mod,
  token,
  pharmacy,
  noun,
  onClose
}) {
  const masked = useMaskNames();
  const [areas, setAreas] = useState(null);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [selected, setSelected] = useState(null); // area object
  const [logs, setLogs] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [error, setError] = useState(null);
  // Inactive areas are hidden by default; the toggle adds them to the left
  // panel (amber, labelled "Inactive"). They stay clickable — any historic
  // logs for an inactive area still load into the right panel.
  const [showInactive, setShowInactive] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingAreas(true);
      try {
        const data = await apiGet(`${mod.path}/areas?pharmacyId=${encodeURIComponent(String(pharmacy.pharmacyId))}`, token);
        if (!cancelled) setAreas(Array.isArray(data?.areas) ? data.areas : []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoadingAreas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mod, token, pharmacy]);
  const selectArea = useCallback(async area => {
    setSelected(area);
    setLogs(null);
    setLoadingLogs(true);
    setError(null);
    try {
      const data = await apiGet(`${mod.path}/area-logs?pharmacyId=${encodeURIComponent(String(pharmacy.pharmacyId))}` + `&areaId=${encodeURIComponent(area.areaId)}`, token);
      setLogs(Array.isArray(data?.logs) ? data.logs : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingLogs(false);
    }
  }, [mod, token, pharmacy]);
  const areaBg = a => !a.active ? AF_AMBER : a.overdue || a.neverLogged ? AF_RED : AF_GREEN;
  const areaStatusText = a => !a.active ? "Inactive" : a.overdue ? "Overdue" : a.neverLogged ? `Never ${noun}` : "Up to date";
  const verbTitle = noun.charAt(0).toUpperCase() + noun.slice(1);
  const allAreas = areas || [];
  const inactiveAreaCount = allAreas.filter(a => !a.active).length;
  const visibleAreas = allAreas.filter(a => showInactive || a.active);
  return <div className="popup-overlay" onClick={onClose}>
      <div className="popup-dialog" style={{
      maxWidth: 1050
    }} onClick={e => e.stopPropagation()}>
        <div className="popup-header">
          <h3>
            {mod.title} areas — {displayName(pharmacy.pharmacyName, masked)}
          </h3>
          <div style={{
          display: "flex",
          gap: 8,
          alignItems: "center"
        }}>
            <button style={{
            ...S.btnGhost,
            background: showInactive ? AF_AMBER : "#F5F5F5"
          }} onClick={() => {
            setShowInactive(v => {
              // Deselect a hidden area when toggling inactive areas off.
              if (v && selected && !selected.active) {
                setSelected(null);
                setLogs(null);
              }
              return !v;
            });
          }} title="Include inactive areas in the list (shown amber)">
              {showInactive ? "Hide inactive" : "Show inactive"}
              {inactiveAreaCount > 0 && ` (${inactiveAreaCount})`}
            </button>
            <button onClick={onClose}>X</button>
          </div>
        </div>

        {error && <div style={S.error}>{error}</div>}

        <div style={{
        display: "flex",
        gap: 14,
        alignItems: "stretch",
        minHeight: 320
      }}>
          {/* Left: area list */}
          <div style={{
          width: 340,
          flexShrink: 0,
          overflowY: "auto",
          maxHeight: "65vh",
          border: "1px solid #F0F0F0",
          borderRadius: 6,
          padding: 8
        }}>
            <div style={{
            fontWeight: 600,
            fontSize: 13,
            marginBottom: 6
          }}>
              Areas {areas && `(${visibleAreas.length}${!showInactive && inactiveAreaCount > 0 ? ` + ${inactiveAreaCount} inactive` : ""})`}
            </div>
            {loadingAreas && <div style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>Loading areas…</div>}
            {!loadingAreas && areas && visibleAreas.length === 0 && <div style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>
                {allAreas.length === 0 ? "No areas configured at this branch." : "No active areas — use Show inactive to see the rest."}
              </div>}
            {!loadingAreas && visibleAreas.map(a => <div key={a.areaId} onClick={() => selectArea(a)} style={{
            background: areaBg(a),
            borderRadius: 6,
            padding: "8px 10px",
            marginBottom: 6,
            cursor: "pointer",
            border: selected?.areaId === a.areaId ? "2px solid #0E8CFF" : "2px solid transparent"
          }} title="Click for this area's log history">
                <div style={{
              fontWeight: 600,
              fontSize: 13
            }}>{a.areaName}</div>
                <div style={{
              fontSize: 11.5,
              color: "#434343",
              marginTop: 2
            }}>
                  {areaStatusText(a)}
                  {a.frequency != null && ` · every ${a.frequency}d`}
                  {a.nextDue && ` · due ${fmtDate(a.nextDue)}`}
                </div>
                <div style={{
              fontSize: 11.5,
              color: "#434343"
            }}>
                  Last {noun}: {a.lastLogged ? fmtDateTime(a.lastLogged) : "never"}
                </div>
              </div>)}
          </div>

          {/* Right: log history for the selected area */}
          <div style={{
          flex: 1,
          overflowY: "auto",
          maxHeight: "65vh",
          border: "1px solid #F0F0F0",
          borderRadius: 6,
          padding: 8
        }}>
            {!selected ? <div style={{
            fontSize: 13,
            color: "#5D5D5D",
            padding: 8
          }}>
                Select an area on the left to see its log history.
              </div> : <>
                <div style={{
              fontWeight: 600,
              fontSize: 13,
              marginBottom: 6
            }}>
                  {selected.areaName} — log history
                  {logs && ` (${logs.length})`}
                </div>
                {loadingLogs && <div style={{
              fontSize: 12,
              color: "#5D5D5D"
            }}>Loading history…</div>}
                {!loadingLogs && logs && logs.length === 0 && <div style={{
              fontSize: 12,
              color: "#5D5D5D"
            }}>
                    No logs recorded for this area.
                  </div>}
                {!loadingLogs && logs && logs.length > 0 && <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>{verbTitle}</th>
                        <th style={S.th}>By</th>
                        <th style={S.th}>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((l, i) => <tr key={i}>
                          <td style={S.td}>{fmtDateTime(l.date)}</td>
                          <td style={S.td}>{l.by || "—"}</td>
                          <td style={S.td}>{l.notes || "—"}</td>
                        </tr>)}
                    </tbody>
                  </table>}
              </>}
          </div>
        </div>
      </div>
    </div>;
}

// ---------------------------------------------------------------------------
// AreaConfigDialog — "Configuration" view for area-based modules. One
// horizontal panel per branch: pharmacy name on the left, that branch's
// ACTIVE areas on the right (frequency + next due). Each area carries a
// badge like 3/26 = how many branches in scope have an area of exactly the
// same name, plus a Cascade button which — after a confirmation warning —
// inserts-or-updates that area in EVERY branch of the current organisation.
// ---------------------------------------------------------------------------
function AreaConfigDialog({
  mod,
  token,
  noun,
  onClose
}) {
  const masked = useMaskNames();
  const [branches, setBranches] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Inactive areas are hidden by default; the toggle shows them amber (and
  // they stay editable — including flipping them back to active).
  const [showInactive, setShowInactive] = useState(false);
  // Pending cascade confirmation: { areaName, frequency } | null.
  const [confirmCascade, setConfirmCascade] = useState(null);
  const [cascadeBusy, setCascadeBusy] = useState(false);
  // Last cascade result banner: { areaName, updated, created, total, errors }.
  const [cascadeResult, setCascadeResult] = useState(null);
  // "Add area" form: { pharmacyId, pharmacyName, areaName, frequency, nextDue }
  // | null. nextDue defaults to a fortnight from today; created areas are
  // active by default.
  const [addArea, setAddArea] = useState(null);
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState(null);
  const openAddArea = b => {
    setAddError(null);
    setAddArea({
      pharmacyId: String(b.pharmacyId),
      pharmacyName: b.pharmacyName,
      areaName: "",
      frequency: "7",
      nextDue: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10)
    });
  };

  // Edit form (click an area card): rename, change frequency / next due, or
  // set the status inactive. Inactive areas drop out of this active-areas
  // view but stay visible in the branch popup behind its Show inactive toggle.
  const [editArea, setEditArea] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState(null);
  const openEditArea = (b, a) => {
    setEditError(null);
    setEditArea({
      pharmacyId: String(b.pharmacyId),
      pharmacyName: b.pharmacyName,
      areaId: a.areaId,
      areaName: a.areaName || "",
      frequency: a.frequency != null ? String(a.frequency) : "",
      nextDue: a.nextDue ? String(a.nextDue).slice(0, 10) : "",
      status: a.active !== false
    });
  };
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiGet(`${mod.path}/area-config`, token);
      setBranches(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [mod, token]);
  useEffect(() => {
    load();
  }, [load]);

  // Panels listed alphabetically by pharmacy name.
  const all = [...(branches || [])].sort((a, b) => (a.pharmacyName || "").localeCompare(b.pharmacyName || ""));
  const totalBranches = all.length;

  // Badge counts: normalised area name → number of branches with an ACTIVE
  // area of exactly that name (case/whitespace-insensitive).
  const norm = s => String(s || "").trim().toLowerCase();
  const nameCounts = new Map();
  for (const b of all) {
    const seen = new Set();
    for (const a of b.areas || []) {
      if (a.active === false) continue;
      const k = norm(a.areaName);
      if (!k || seen.has(k)) continue;
      seen.add(k);
      nameCounts.set(k, (nameCounts.get(k) || 0) + 1);
    }
  }
  const runCascade = useCallback(async () => {
    if (!confirmCascade) return;
    setCascadeBusy(true);
    setError(null);
    try {
      const res = await apiSend(`${mod.path}/cascade-area`, token, "POST", {
        areaName: confirmCascade.areaName,
        frequency: confirmCascade.frequency
      });
      setCascadeResult({
        areaName: confirmCascade.areaName,
        ...res
      });
      setConfirmCascade(null);
      await load(); // refresh badges + panels
    } catch (e) {
      setError(e.message);
      setConfirmCascade(null);
    } finally {
      setCascadeBusy(false);
    }
  }, [confirmCascade, mod, token, load]);
  const saveNewArea = useCallback(async () => {
    if (!addArea) return;
    const name = (addArea.areaName || "").trim();
    const freq = Number(addArea.frequency);
    if (!name) {
      setAddError("Area name is required.");
      return;
    }
    if (!Number.isFinite(freq) || freq <= 0) {
      setAddError("Frequency must be a number of days greater than 0.");
      return;
    }
    setAddBusy(true);
    setAddError(null);
    try {
      await apiSend(`${mod.path}/create-area`, token, "POST", {
        pharmacyId: addArea.pharmacyId,
        areaName: name,
        frequency: freq,
        nextDue: addArea.nextDue || null
      });
      setAddArea(null);
      await load(); // refresh panels + badges
    } catch (e) {
      setAddError(e.message);
    } finally {
      setAddBusy(false);
    }
  }, [addArea, mod, token, load]);
  const saveEditArea = useCallback(async () => {
    if (!editArea) return;
    const name = (editArea.areaName || "").trim();
    const freq = Number(editArea.frequency);
    if (!name) {
      setEditError("Area name is required.");
      return;
    }
    if (!Number.isFinite(freq) || freq <= 0) {
      setEditError("Frequency must be a number of days greater than 0.");
      return;
    }
    setEditBusy(true);
    setEditError(null);
    try {
      await apiSend(`${mod.path}/update-area`, token, "POST", {
        pharmacyId: editArea.pharmacyId,
        areaId: editArea.areaId,
        areaName: name,
        frequency: freq,
        nextDue: editArea.nextDue || null,
        status: editArea.status
      });
      setEditArea(null);
      await load(); // refresh panels + badges
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditBusy(false);
    }
  }, [editArea, mod, token, load]);
  return <div className="popup-overlay" onClick={onClose}>
      <div className="popup-dialog" style={{
      maxWidth: 1200
    }} onClick={e => e.stopPropagation()}>
        <div className="popup-header">
          <h3>{mod.title} — configuration</h3>
          <div style={{
          display: "flex",
          gap: 8,
          alignItems: "center"
        }}>
            <button style={{
            ...S.btnGhost,
            background: showInactive ? AF_AMBER : "#F5F5F5"
          }} onClick={() => setShowInactive(v => !v)} title="Include inactive areas in every branch's panel (shown amber)">
              {showInactive ? "Hide inactive" : "Show inactive"}
            </button>
            <button onClick={onClose}>X</button>
          </div>
        </div>

        <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginBottom: 10
      }}>
          Areas per branch{showInactive ? " (inactive areas shown amber)" : " (active only — use Show inactive to see the rest)"}.
          The badge shows how many of the{" "}
          {totalBranches} branch{totalBranches === 1 ? "" : "es"} have an
          active area with exactly the same name. Cascade copies an area to
          every branch in your organisation (existing areas of the same name
          are updated, missing ones are created). Click an area to edit it.
        </div>

        {error && <div style={S.error}>{error}</div>}

        {cascadeResult && <div style={{
        background: AF_GREEN,
        borderRadius: 6,
        padding: "8px 12px",
        fontSize: 13,
        marginBottom: 10
      }}>
            Cascaded “{cascadeResult.areaName}” — {cascadeResult.updated} branch
            {cascadeResult.updated === 1 ? "" : "es"} updated,{" "}
            {cascadeResult.created} created
            (of {cascadeResult.total}).
            {Array.isArray(cascadeResult.errors) && cascadeResult.errors.length > 0 && <span style={{
          color: "#CF1322"
        }}>
                {" "}{cascadeResult.errors.length} failed:{" "}
                {cascadeResult.errors.map(e => displayName(e.pharmacyName, masked)).join(", ")}.
              </span>}
            <button style={{
          ...S.btnGhost,
          marginLeft: 10,
          padding: "2px 8px"
        }} onClick={() => setCascadeResult(null)}>Dismiss</button>
          </div>}

        {loading && <div style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>Loading configuration…</div>}

        {!loading && all.length === 0 && !error && <div style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>No branches in scope.</div>}

        {!loading && all.map(b => {
        const branchAreas = b.areas || [];
        const activeCount = branchAreas.filter(a => a.active !== false).length;
        const inactiveCount = branchAreas.length - activeCount;
        const visibleAreas = branchAreas.filter(a => showInactive || a.active !== false);
        return <div key={String(b.pharmacyId)} style={{
          display: "flex",
          gap: 14,
          alignItems: "flex-start",
          border: "1px solid #F0F0F0",
          borderRadius: 6,
          padding: 10,
          marginBottom: 8
        }}>
            {/* Left: pharmacy name */}
            <div style={{
            width: 220,
            flexShrink: 0,
            fontWeight: 600,
            fontSize: 13,
            paddingTop: 4
          }}>
              {displayName(b.pharmacyName, masked)}
              <div style={{
              fontWeight: 400,
              fontSize: 11.5,
              color: "#5D5D5D"
            }}>
                {activeCount} active area{activeCount === 1 ? "" : "s"}
              </div>
              <div style={{
              fontWeight: 400,
              fontSize: 11.5,
              color: inactiveCount > 0 ? "#AD4E00" : "#5D5D5D"
            }}>
                {inactiveCount} inactive
              </div>
              <button style={{
              ...S.btnGhost,
              padding: "3px 10px",
              fontSize: 11,
              marginTop: 6
            }} onClick={() => openAddArea(b)} title="Create a new area at this branch">
                + Add area
              </button>
            </div>

            {/* Right: grid of areas */}
            <div style={{
            flex: 1,
            display: "flex",
            flexWrap: "wrap",
            gap: 8
          }}>
              {visibleAreas.length === 0 && <div style={{
              fontSize: 12,
              color: "#5D5D5D",
              paddingTop: 6
            }}>
                  {branchAreas.length === 0 ? "No areas." : "No active areas — use Show inactive to see the rest."}
                </div>}
              {visibleAreas.map(a => {
              const isInactive = a.active === false;
              const count = nameCounts.get(norm(a.areaName)) || 0;
              const everywhere = count >= totalBranches && totalBranches > 0;
              return <div key={a.areaId} onClick={() => openEditArea(b, a)} style={{
                border: "1px solid #D9D9D9",
                borderRadius: 6,
                padding: "6px 10px",
                fontSize: 12.5,
                background: isInactive ? AF_AMBER : "#FAFAFA",
                minWidth: 200,
                cursor: "pointer"
              }} title="Click to edit this area (name, frequency, next due, status)">
                    <div style={{
                  fontWeight: 600
                }}>
                      {a.areaName}
                      {isInactive && <span style={{
                    fontWeight: 400,
                    fontSize: 10.5,
                    color: "#AD4E00",
                    marginLeft: 6
                  }}>
                          Inactive
                        </span>}
                    </div>
                    <div style={{
                  color: "#434343",
                  marginTop: 2
                }}>
                      {a.frequency != null ? `Every ${a.frequency}d` : "No frequency"}
                      {" · "}
                      {a.nextDue ? `due ${fmtDate(a.nextDue)}` : "no due date"}
                    </div>
                    <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 5
                }}>
                      <span title={`${count} of ${totalBranches} branches have an active area named “${a.areaName}”`} style={{
                    background: everywhere ? AF_GREEN : "#F0F0F0",
                    borderRadius: 10,
                    padding: "1px 8px",
                    fontSize: 11,
                    fontWeight: 600
                  }}>
                        {count}/{totalBranches}
                      </span>
                      <button style={{
                    ...S.btnGhost,
                    padding: "2px 8px",
                    fontSize: 11
                  }} onClick={e => {
                    e.stopPropagation();
                    setConfirmCascade({
                      areaName: a.areaName,
                      frequency: a.frequency
                    });
                  }} title={everywhere ? "Update this area in every branch (syncs frequency and reactivates it)" : "Create or update this area in every branch (reactivates inactive ones)"}>
                        Cascade
                      </button>
                    </div>
                  </div>;
            })}
            </div>
          </div>;
      })}

        {/* "Add area" form — creates one area at one branch. Active by
            default; next due defaults to a fortnight from today. */}
        {addArea && <div style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }} onClick={() => !addBusy && setAddArea(null)}>
            <div style={{
          background: "#fff",
          borderRadius: 8,
          padding: 20,
          maxWidth: 440,
          width: "92%",
          boxShadow: "0 0 15px rgba(0,0,0,0.3)"
        }} onClick={e => e.stopPropagation()}>
              <div style={{
            fontWeight: 600,
            fontSize: 15,
            marginBottom: 4
          }}>
                Add {mod.title.toLowerCase()} area
              </div>
              <div style={{
            fontSize: 12.5,
            color: "#5D5D5D",
            marginBottom: 14
          }}>
                {displayName(addArea.pharmacyName, masked)} — the new area is
                created active.
              </div>

              {addError && <div style={{
            ...S.error,
            marginBottom: 8
          }}>{addError}</div>}

              <label style={{
            display: "block",
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 10
          }}>
                Area name
                <input autoFocus type="text" value={addArea.areaName} onChange={e => setAddArea(prev => prev ? {
              ...prev,
              areaName: e.target.value
            } : prev)} placeholder="e.g. Dispensary shelf A" style={{
              display: "block",
              width: "100%",
              boxSizing: "border-box",
              marginTop: 4,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 400,
              border: "1px solid #D9D9D9",
              borderRadius: 6,
              outline: "none"
            }} />
              </label>

              <label style={{
            display: "block",
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 10
          }}>
                Frequency (days)
                <input type="number" min="1" value={addArea.frequency} onChange={e => setAddArea(prev => prev ? {
              ...prev,
              frequency: e.target.value
            } : prev)} style={{
              display: "block",
              width: 120,
              marginTop: 4,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 400,
              border: "1px solid #D9D9D9",
              borderRadius: 6,
              outline: "none"
            }} />
              </label>

              <label style={{
            display: "block",
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 16
          }}>
                Next due
                <input type="date" value={addArea.nextDue} onChange={e => setAddArea(prev => prev ? {
              ...prev,
              nextDue: e.target.value
            } : prev)} style={{
              display: "block",
              width: 170,
              marginTop: 4,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 400,
              border: "1px solid #D9D9D9",
              borderRadius: 6,
              outline: "none"
            }} />
                <span style={{
              fontWeight: 400,
              fontSize: 11.5,
              color: "#5D5D5D"
            }}>
                  Defaults to a fortnight from today.
                </span>
              </label>

              <div style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8
          }}>
                <button style={S.btnGhost} disabled={addBusy} onClick={() => setAddArea(null)}>
                  Cancel
                </button>
                <button style={{
              ...S.btn,
              opacity: addBusy ? 0.6 : 1,
              cursor: addBusy ? "default" : "pointer"
            }} disabled={addBusy} onClick={saveNewArea}>
                  {addBusy ? "Creating…" : "Create area"}
                </button>
              </div>
            </div>
          </div>}

        {/* "Edit area" form — rename, change frequency / next due, or set
            the status inactive. */}
        {editArea && <div style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }} onClick={() => !editBusy && setEditArea(null)}>
            <div style={{
          background: "#fff",
          borderRadius: 8,
          padding: 20,
          maxWidth: 440,
          width: "92%",
          boxShadow: "0 0 15px rgba(0,0,0,0.3)"
        }} onClick={e => e.stopPropagation()}>
              <div style={{
            fontWeight: 600,
            fontSize: 15,
            marginBottom: 4
          }}>
                Edit {mod.title.toLowerCase()} area
              </div>
              <div style={{
            fontSize: 12.5,
            color: "#5D5D5D",
            marginBottom: 14
          }}>
                {displayName(editArea.pharmacyName, masked)}
              </div>

              {editError && <div style={{
            ...S.error,
            marginBottom: 8
          }}>{editError}</div>}

              <label style={{
            display: "block",
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 10
          }}>
                Area name
                <input autoFocus type="text" value={editArea.areaName} onChange={e => setEditArea(prev => prev ? {
              ...prev,
              areaName: e.target.value
            } : prev)} style={{
              display: "block",
              width: "100%",
              boxSizing: "border-box",
              marginTop: 4,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 400,
              border: "1px solid #D9D9D9",
              borderRadius: 6,
              outline: "none"
            }} />
              </label>

              <label style={{
            display: "block",
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 10
          }}>
                Frequency (days)
                <input type="number" min="1" value={editArea.frequency} onChange={e => setEditArea(prev => prev ? {
              ...prev,
              frequency: e.target.value
            } : prev)} style={{
              display: "block",
              width: 120,
              marginTop: 4,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 400,
              border: "1px solid #D9D9D9",
              borderRadius: 6,
              outline: "none"
            }} />
              </label>

              <label style={{
            display: "block",
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 10
          }}>
                Next due
                <input type="date" value={editArea.nextDue} onChange={e => setEditArea(prev => prev ? {
              ...prev,
              nextDue: e.target.value
            } : prev)} style={{
              display: "block",
              width: 170,
              marginTop: 4,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 400,
              border: "1px solid #D9D9D9",
              borderRadius: 6,
              outline: "none"
            }} />
              </label>

              <label style={{
            display: "block",
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 16
          }}>
                Status
                <select value={editArea.status ? "active" : "inactive"} onChange={e => setEditArea(prev => prev ? {
              ...prev,
              status: e.target.value === "active"
            } : prev)} style={{
              display: "block",
              width: 170,
              marginTop: 4,
              padding: "6px 10px",
              fontSize: 13,
              fontWeight: 400,
              border: "1px solid #D9D9D9",
              borderRadius: 6,
              outline: "none",
              background: "#fff"
            }}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                {!editArea.status && <span style={{
              fontWeight: 400,
              fontSize: 11.5,
              color: "#AD4E00"
            }}>
                    Inactive areas leave this view; find them in the branch's
                    areas popup under Show inactive.
                  </span>}
              </label>

              <div style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8
          }}>
                <button style={S.btnGhost} disabled={editBusy} onClick={() => setEditArea(null)}>
                  Cancel
                </button>
                <button style={{
              ...S.btn,
              opacity: editBusy ? 0.6 : 1,
              cursor: editBusy ? "default" : "pointer"
            }} disabled={editBusy} onClick={saveEditArea}>
                  {editBusy ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </div>}

        {/* Cascade confirmation warning */}
        {confirmCascade && <div style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }} onClick={() => !cascadeBusy && setConfirmCascade(null)}>
            <div style={{
          background: "#fff",
          borderRadius: 8,
          padding: 20,
          maxWidth: 440,
          width: "92%",
          boxShadow: "0 0 15px rgba(0,0,0,0.3)"
        }} onClick={e => e.stopPropagation()}>
              <div style={{
            fontWeight: 600,
            fontSize: 15,
            marginBottom: 8
          }}>
                Cascade “{confirmCascade.areaName}”
              </div>
              <div style={{
            fontSize: 13,
            color: "#434343",
            marginBottom: 16
          }}>
                This will create the same entry in all branches. Continue?
              </div>
              <div style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8
          }}>
                <button style={S.btnGhost} disabled={cascadeBusy} onClick={() => setConfirmCascade(null)}>
                  Cancel
                </button>
                <button style={{
              ...S.btn,
              opacity: cascadeBusy ? 0.6 : 1,
              cursor: cascadeBusy ? "default" : "pointer"
            }} disabled={cascadeBusy} onClick={runCascade}>
                  {cascadeBusy ? "Cascading…" : "Continue"}
                </button>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}
function ModuleDetail({
  mod,
  token,
  onClose,
  embedded = false
}) {
  const masked = useMaskNames();
  const [urgent, setUrgent] = useState({});
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fridge-only: per-pharmacy rolled-up status for the colour-coded grid.
  const [fleetStatus, setFleetStatus] = useState(null);
  const [statusQuery, setStatusQuery] = useState("");
  // Messaging: toggle to show only non-engaged sites (SMS+EMAIL < 50).
  const [nonEngagedOnly, setNonEngagedOnly] = useState(false);
  // Status grid column sort. key: "pharmacy" | "status" | "count". Default
  // sorts by worst status first (handled specially when key === "status").
  const [statusSort, setStatusSort] = useState({
    key: "status",
    dir: "asc"
  });
  // Fridge-only: history popup payload (mirrors Mockupp.js fridge popup).
  const [popupData, setPopupData] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  // Removed-drugs year export (date-checking view).
  const [removedExportYear, setRemovedExportYear] = useState(() => new Date().getFullYear());
  const [removedExportBusy, setRemovedExportBusy] = useState(false);
  // Date-checking detail tabs: "drugs" (existing expiry grid) | "areas"
  // (all-branches date-checking areas grid, same style as cleaning).
  const [dcTab, setDcTab] = useState("drugs");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const next = {};
        for (const r of mod.urgentRules) {
          next[r.rule] = await apiGet(`${mod.path}/${r.rule}`, token);
        }
        if (!cancelled) setUrgent(next);
        if (mod.statusDetail) {
          const fs = await apiGet(`${mod.path}/${mod.statusDetail.endpoint}`, token);
          if (!cancelled) setFleetStatus(Array.isArray(fs) ? fs : []);
        }
        if (mod.snapshot) {
          const snap = await apiGet(`${mod.path}/${mod.snapshot}`, token);
          if (!cancelled) setSnapshot(snap);
        } else if (mod.snapshots) {
          const multi = {};
          for (const s of mod.snapshots) {
            multi[s] = await apiGet(`${mod.path}/${s}`, token);
          }
          if (!cancelled) setSnapshot(multi);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mod, token]);

  // Open the history popup for a site. Mirrors the Mockupp.js wiring exactly,
  // parameterised by the module's statusDetail config so fridge and cleaning
  // (and any future module) share one implementation.
  const openStatusPopup = useCallback(async pharmacyName => {
    const sd = mod.statusDetail;
    if (!sd) return;
    setPopupLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/${sd.fetchPath}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          pharmacyName
        })
      });
      if (!res.ok) {
        // Surface the server message (e.g. name lookup 404) instead of failing silently.
        let msg = `Could not load details for ${pharmacyName} (HTTP ${res.status}).`;
        try {
          const j = await res.json();
          if (j?.message) msg += ` ${j.message}`;
        } catch (_) {}
        throw new Error(msg);
      }
      const result = await res.json();
      // PopupContent's cleaning branch maps the array unconditionally, so make
      // sure we always hand it an array even if the site has no items.
      const payload = Array.isArray(result?.[sd.logsKey]) ? result[sd.logsKey] : [];
      setPopupData({
        column: sd.popupColumn,
        pharmacyName,
        [sd.popupDataKey]: payload,
        [sd.popupFlag]: true
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setPopupLoading(false);
    }
  }, [token, mod]);

  // Handover-specific grouped popup (overdue → pending → completed today).
  // Fetched by pharmacyId from the head-office items endpoint.
  const [groupedPopup, setGroupedPopup] = useState(null);
  const openHandoverPopup = useCallback(async row => {
    setPopupLoading(true);
    setError(null);
    try {
      const data = await apiGet(`handover/items?pharmacyId=${encodeURIComponent(String(row.pharmacyId))}`, token);
      setGroupedPopup({
        pharmacyName: row.pharmacyName,
        overdue: data?.overdue || [],
        pending: data?.pending || [],
        completedToday: data?.completedToday || []
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setPopupLoading(false);
    }
  }, [token]);

  // Date-checking: drugs behind one clicked cell (name/location/expiry).
  const [drugPopup, setDrugPopup] = useState(null);
  // Removed-drugs paged history popup: ALL removed drugs, fetched two months
  // at a time; scrolling near the bottom pulls the next two months.
  const [removedPopup, setRemovedPopup] = useState(null);
  const openDrugCell = useCallback(async (row, cell, cellLabel, tone) => {
    setPopupLoading(true);
    setError(null);
    try {
      if (cell === "removed") {
        const data = await apiGet(`date-checking/removed-paged?pharmacyId=${encodeURIComponent(String(row.pharmacyId))}`, token);
        setRemovedPopup({
          pharmacyId: String(row.pharmacyId),
          pharmacyName: row.pharmacyName,
          tone,
          rows: data?.rows || [],
          nextBefore: data?.windowStart || null,
          // cursor for the next (older) window
          hasMore: !!data?.hasMore,
          loadingMore: false,
          sort: {
            key: "date",
            dir: -1
          },
          editing: null // { drugId, text } — unit price edit
        });
        return;
      }
      const data = await apiGet(`date-checking/drugs?pharmacyId=${encodeURIComponent(String(row.pharmacyId))}&cell=${cell}`, token);
      setDrugPopup({
        pharmacyName: row.pharmacyName,
        cellLabel,
        tone,
        drugs: Array.isArray(data) ? data : []
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setPopupLoading(false);
    }
  }, [token]);

  // Pull the next two-month window when the list is scrolled near its end.
  const loadMoreRemoved = useCallback(async () => {
    // Read the current popup state without holding it in a ref.
    const cur = await new Promise(res => setRemovedPopup(p => {
      res(p);
      return p;
    }));
    if (!cur || !cur.hasMore || cur.loadingMore || !cur.nextBefore) return;
    setRemovedPopup(prev => prev ? {
      ...prev,
      loadingMore: true
    } : prev);
    try {
      const data = await apiGet(`date-checking/removed-paged?pharmacyId=${encodeURIComponent(cur.pharmacyId)}` + `&before=${encodeURIComponent(cur.nextBefore)}`, token);
      setRemovedPopup(prev => prev ? {
        ...prev,
        rows: [...prev.rows, ...(data?.rows || [])],
        nextBefore: data?.windowStart || null,
        hasMore: !!data?.hasMore,
        loadingMore: false
      } : prev);
    } catch (e) {
      setError(e.message);
      setRemovedPopup(prev => prev ? {
        ...prev,
        loadingMore: false
      } : prev);
    }
  }, [token]);

  // Save a manual price for one drug, then refresh every window already
  // loaded (one call spanning [earliest windowStart, now]) so the popup
  // reflects the stored value.
  const saveRemovedPrice = useCallback(async (drugId, pricePence) => {
    const cur = await new Promise(res => setRemovedPopup(p => {
      res(p);
      return p;
    }));
    if (!cur) return;
    try {
      await apiSend("date-checking/drug-price", token, "POST", {
        pharmacyId: cur.pharmacyId,
        drugId,
        pricePence
      });
      const from = cur.nextBefore; // earliest loaded window start (null → default 2 months)
      const data = await apiGet(`date-checking/removed-paged?pharmacyId=${encodeURIComponent(cur.pharmacyId)}` + (from ? `&from=${encodeURIComponent(from)}` : ""), token);
      setRemovedPopup(prev => prev ? {
        ...prev,
        rows: data?.rows || [],
        nextBefore: data?.windowStart || prev.nextBefore,
        hasMore: !!data?.hasMore,
        editing: null
      } : prev);
    } catch (e) {
      setError(e.message);
      setRemovedPopup(prev => prev ? {
        ...prev,
        editing: null
      } : prev);
    }
  }, [token]);

  // Removed-drugs year export: pull the whole year for every in-scope pharmacy
  // from the backend (price-enriched from dmddata), then build the workbook
  // client-side (summary grid + one tab per pharmacy).
  const runRemovedExport = useCallback(async year => {
    setRemovedExportBusy(true);
    setError(null);
    try {
      const payload = await apiGet(`date-checking/removed-export?year=${encodeURIComponent(year)}`, token);
      await exportRemovedDrugs(payload, {
        displayName,
        masked
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setRemovedExportBusy(false);
    }
  }, [token, masked]);

  // caches its per-register + discrepancy detail keyed by pharmacyId.
  const [cdExpanded, setCdExpanded] = useState(null); // pharmacyId | null
  const [cdDetail, setCdDetail] = useState({}); // { [pharmacyId]: { registers, discrepancies } }
  const [cdLoadingId, setCdLoadingId] = useState(null); // pharmacyId being fetched
  const toggleCdRow = useCallback(async row => {
    const id = String(row.pharmacyId);
    // Collapse if already open.
    if (cdExpanded === id) {
      setCdExpanded(null);
      return;
    }
    setCdExpanded(id);
    if (cdDetail[id]) return; // already cached
    setCdLoadingId(id);
    setError(null);
    try {
      const data = await apiGet(`cd-register/registers?pharmacyId=${encodeURIComponent(id)}`, token);
      setCdDetail(prev => ({
        ...prev,
        [id]: {
          registers: Array.isArray(data?.registers) ? data.registers : [],
          discrepancies: Array.isArray(data?.discrepancies) ? data.discrepancies : []
        }
      }));
    } catch (e) {
      setError(e.message);
      setCdExpanded(null);
    } finally {
      setCdLoadingId(null);
    }
  }, [token, cdExpanded, cdDetail]);

  // Patient safety: inline accordion expand. Tracks the open pharmacy row and
  // caches its incident list (reverse-chronological) keyed by pharmacyId.
  const [psExpanded, setPsExpanded] = useState(null);
  const [psDetail, setPsDetail] = useState({});
  const [psLoadingId, setPsLoadingId] = useState(null);
  const togglePsRow = useCallback(async row => {
    const id = String(row.pharmacyId);
    if (psExpanded === id) {
      setPsExpanded(null);
      return;
    }
    setPsExpanded(id);
    if (psDetail[id]) return;
    setPsLoadingId(id);
    setError(null);
    try {
      const data = await apiGet(`patient-safety/incidents?pharmacyId=${encodeURIComponent(id)}`, token);
      setPsDetail(prev => ({
        ...prev,
        [id]: Array.isArray(data) ? data : []
      }));
    } catch (e) {
      setError(e.message);
      setPsExpanded(null);
    } finally {
      setPsLoadingId(null);
    }
  }, [token, psExpanded, psDetail]);

  // Visitor logs: inline accordion expand. Caches each pharmacy's visitor list
  // (reverse-chronological) keyed by pharmacyId.
  const [vlExpanded, setVlExpanded] = useState(null);
  const [vlDetail, setVlDetail] = useState({});
  const [vlLoadingId, setVlLoadingId] = useState(null);
  const toggleVlRow = useCallback(async row => {
    const id = String(row.pharmacyId);
    if (vlExpanded === id) {
      setVlExpanded(null);
      return;
    }
    setVlExpanded(id);
    if (vlDetail[id]) return;
    setVlLoadingId(id);
    setError(null);
    try {
      const data = await apiGet(`visitor-logs/visitors?pharmacyId=${encodeURIComponent(id)}`, token);
      setVlDetail(prev => ({
        ...prev,
        [id]: Array.isArray(data) ? data : []
      }));
    } catch (e) {
      setError(e.message);
      setVlExpanded(null);
    } finally {
      setVlLoadingId(null);
    }
  }, [token, vlExpanded, vlDetail]);

  // RP sign-in has its own dedicated view (RPSignIn.js): today's cover grid
  // plus the pharmacy / pharmacist / unpaid session tabs. Placed after all
  // hooks so the hook order stays stable.
  if (mod.key === "rpSignIn") {
    return <div style={S.drill}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 12
      }}>
          <div style={S.drillTitle}>{mod.title}</div>
          <button style={S.btnGhost} onClick={onClose}>← Back</button>
        </div>
        <RPSignIn token={token} />
      </div>;
  }
  return <div style={embedded ? undefined : S.drill}>
      {!embedded && <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12
    }}>
        <div style={S.drillTitle}>{mod.title}</div>
        <button style={S.btnGhost} onClick={onClose}>← Back</button>
      </div>}

      {loading && <LoadingOverlay label={`Loading ${mod.title.toLowerCase()}…`} />}
      {error && <div style={S.error}>{error}</div>}

      {/* Cleaning: all-branches area grid (replaces the shared status grid). */}
      {mod.key === "cleaning" && mod.areaFleet && <AreaFleetGrid mod={mod} token={token} noun={mod.areaFleet.noun} />}

      {!mod.statusDetail && !mod.areaFleet && mod.urgentRules.map(r => {
      const results = urgent[r.rule] || [];
      const rows = flattenOffenders(results);
      const formatter = URGENT_FORMATTERS[`${mod.path}/${r.rule}`];
      return <div key={r.rule} style={{
        marginBottom: 18
      }}>
            <div style={{
          fontWeight: 600,
          marginBottom: 6
        }}>
              {r.label}{" "}
              <span style={rows.length > 0 ? S.urgent : S.ok}>
                ({results.length} pharmacy{results.length === 1 ? "" : "ies"}, {rows.length} item{rows.length === 1 ? "" : "s"})
              </span>
            </div>
            {formatter ? <GenericTable rows={rows} columns={formatter} /> : rows.length > 0 && <pre style={{
          background: "#FAFAFA",
          padding: 8,
          fontSize: 11,
          borderRadius: 4
        }}>
                  {JSON.stringify(rows, null, 2)}
                </pre>}
          </div>;
    })}

      {/* Colour-coded per-pharmacy status grid (worst-state wins).
          Green = checked & OK, amber = not yet checked, pink = needs attention.
          Click a row for that site's history popup. Shared by fridge + cleaning
          (and any module declaring statusDetail). */}
      {mod.statusDetail && mod.key !== "handover" && mod.key !== "dateChecking" && mod.key !== "cdRegister" && (() => {
      const sd = mod.statusDetail;
      const STATUS = {
        ok: {
          bg: "#F6FFED",
          label: "Checked & OK"
        },
        // light green
        unchecked: {
          bg: "#FFF7E6",
          label: "Not yet checked"
        },
        // amber
        fail: {
          bg: "#fde2e1",
          label: "Needs attention"
        } // light pink/ochre
      };
      const all = fleetStatus || [];
      const q = statusQuery.trim().toLowerCase();
      const rows = q ? all.filter(s => (s.pharmacyName || "").toLowerCase().includes(q)) : all;

      // Count column: handover shows outstanding counts; others show a
      // "checked / total" ratio. countValue is the numeric key for sorting.
      const isHandover = sd.countMode === "handover";
      const countLabel = isHandover ? "Outstanding" : sd.checkedLabel || "Checked";
      const countDisplay = s => isHandover ? `${s.overdueCount ?? 0} overdue, ${s.pendingCount ?? 0} pending` : `${s[sd.checkedField] ?? 0} / ${s[sd.totalField] ?? 0}`;
      const countValue = s => isHandover ? (s.overdueCount ?? 0) * 1000 + (s.pendingCount ?? 0) // overdue dominates
      : s[sd.checkedField] ?? 0;

      // Sort. Default "status" key uses worst-state-first ranking; explicit
      // column sorts use the chosen field + direction.
      const rank = {
        fail: 0,
        unchecked: 1,
        ok: 2
      };
      const dir = statusSort.dir === "desc" ? -1 : 1;
      const sorted = [...rows].sort((a, b) => {
        let cmp = 0;
        if (statusSort.key === "pharmacy") {
          cmp = (a.pharmacyName || "").localeCompare(b.pharmacyName || "");
        } else if (statusSort.key === "count") {
          cmp = countValue(a) - countValue(b);
        } else {
          // "status"
          cmp = (rank[a.status] ?? 3) - (rank[b.status] ?? 3);
        }
        // Stable tiebreak on pharmacy name.
        if (cmp === 0) cmp = (a.pharmacyName || "").localeCompare(b.pharmacyName || "");
        return cmp * dir;
      });
      const toggleSort = key => setStatusSort(prev => prev.key === key ? {
        key,
        dir: prev.dir === "asc" ? "desc" : "asc"
      } : {
        key,
        dir: "asc"
      });
      const arrow = key => statusSort.key === key ? statusSort.dir === "asc" ? " ▲" : " ▼" : "";
      const count = st => all.filter(s => s.status === st).length;
      return <div style={{
        marginTop: 8,
        marginBottom: 18
      }}>
          <div style={{
          fontWeight: 600,
          marginBottom: 6
        }}>
            Site status{" "}
            <span style={{
            fontWeight: 400,
            color: "#5D5D5D",
            fontSize: 13
          }}>
              ({all.length} active site{all.length === 1 ? "" : "s"})
            </span>
          </div>

          {/* Summary chips */}
          <div style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 10
        }}>
            {["fail", "unchecked", "ok"].map(st => <span key={st} style={{
            background: STATUS[st].bg,
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 12,
            fontWeight: 600,
            color: "#434343"
          }}>
                {STATUS[st].label}: {count(st)}
              </span>)}
          </div>

          {/* Search */}
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10
        }}>
            <input type="text" value={statusQuery} onChange={e => setStatusQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
            padding: "6px 10px",
            fontSize: 13,
            border: "1px solid #D9D9D9",
            borderRadius: 6,
            width: 260,
            outline: "none"
          }} />
            {statusQuery && <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>}
            <span style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>
              {statusQuery ? `${sorted.length} match${sorted.length === 1 ? "" : "es"}` : `${sorted.length} site${sorted.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginBottom: 8
        }}>
            One row per pharmacy. Click a row to see that site's full detail
            history.
          </div>

          {sorted.length === 0 ? <div style={{
          fontSize: 13,
          color: "#5D5D5D"
        }}>
              {statusQuery ? `No pharmacies match “${statusQuery}”.` : "No active sites."}
            </div> : <table style={S.table}>
              <thead>
                <tr>
                  <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("pharmacy")} title="Sort by pharmacy name">
                    Pharmacy{arrow("pharmacy")}
                  </th>
                  <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("status")} title="Sort by status (worst first)">
                    Status{arrow("status")}
                  </th>
                  <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("count")} title={`Sort by ${countLabel.toLowerCase()}`}>
                    {countLabel}{arrow("count")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => {
              const sc = STATUS[s.status] || {
                bg: "#fff",
                label: s.status
              };
              const note = s.reasons && s.reasons.length ? s.reasons.join("; ") : "All up to date.";
              return <tr key={String(s.pharmacyId)} onClick={() => openStatusPopup(s.pharmacyName)} style={{
                cursor: "pointer",
                background: sc.bg
              }} title={`${note} — click for history`}>
                      <td style={{
                  ...S.td,
                  fontWeight: 600
                }}>{displayName(s.pharmacyName, masked)}</td>
                      <td style={S.td}>{sc.label}</td>
                      <td style={S.td}>{countDisplay(s)}</td>
                    </tr>;
            })}
              </tbody>
            </table>}
          {popupLoading && <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginTop: 6
        }}>
              Loading history…
            </div>}
        </div>;
    })()}

      {/* Handover: dedicated per-pharmacy grid with sortable columns, row
          colouring (overdue→red, pending→amber, else green) and a grouped
          click-through popup. Replaces both the shared status grid and the
          snapshot KV table for this module. */}
      {mod.key === "handover" && (() => {
      const all = fleetStatus || [];
      const q = statusQuery.trim().toLowerCase();
      const rows = q ? all.filter(s => (s.pharmacyName || "").toLowerCase().includes(q)) : all;
      const STATUS_LABEL = {
        ok: "Checked & OK",
        unchecked: "Pending",
        fail: "Needs attention"
      };
      // Row background: overdue>0 → red, else pending>0 → amber, else green.
      const rowBg = s => s.overdueCount > 0 ? "#fde2e1" : s.pendingCount > 0 ? "#FFF7E6" : "#F6FFED";
      const cols = [{
        key: "pharmacy",
        label: "Pharmacy",
        get: s => (s.pharmacyName || "").toLowerCase(),
        align: "left"
      }, {
        key: "status",
        label: "Status",
        get: s => ({
          fail: 0,
          unchecked: 1,
          ok: 2
        })[s.status] ?? 3
      }, {
        key: "overdue",
        label: "Overdue",
        get: s => s.overdueCount ?? 0
      }, {
        key: "pending",
        label: "Pending",
        get: s => s.pendingCount ?? 0
      }, {
        key: "completed",
        label: "Completed (30d)",
        get: s => s.completedLast30d ?? 0
      }, {
        key: "ontime",
        label: "On-time (%)",
        get: s => s.onTimeLast30d ?? 0
      }];
      const dir = statusSort.dir === "desc" ? -1 : 1;
      const activeCol = cols.find(c => c.key === statusSort.key) || cols[1];
      const sorted = [...rows].sort((a, b) => {
        const av = activeCol.get(a),
          bv = activeCol.get(b);
        let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
        if (cmp === 0) cmp = (a.pharmacyName || "").localeCompare(b.pharmacyName || "");
        return cmp * dir;
      });
      const toggleSort = key => setStatusSort(prev => prev.key === key ? {
        key,
        dir: prev.dir === "asc" ? "desc" : "asc"
      } : {
        key,
        dir: "asc"
      });
      const arrow = key => statusSort.key === key ? statusSort.dir === "asc" ? " ▲" : " ▼" : "";
      const count = st => all.filter(s => s.status === st).length;
      return <div style={{
        marginTop: 8,
        marginBottom: 18
      }}>
          <div style={{
          fontWeight: 600,
          marginBottom: 6
        }}>
            Site status{" "}
            <span style={{
            fontWeight: 400,
            color: "#5D5D5D",
            fontSize: 13
          }}>
              ({all.length} active site{all.length === 1 ? "" : "s"})
            </span>
          </div>

          {/* Search */}
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10
        }}>
            <input type="text" value={statusQuery} onChange={e => setStatusQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
            padding: "6px 10px",
            fontSize: 13,
            border: "1px solid #D9D9D9",
            borderRadius: 6,
            width: 260,
            outline: "none"
          }} />
            {statusQuery && <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>}
            <span style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>
              {statusQuery ? `${sorted.length} match${sorted.length === 1 ? "" : "es"}` : `${sorted.length} site${sorted.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginBottom: 8
        }}>
            One row per pharmacy. Click a pharmacy to see its handover items
            grouped overdue → pending → completed today.
          </div>

          {sorted.length === 0 ? <div style={{
          fontSize: 13,
          color: "#5D5D5D"
        }}>
              {statusQuery ? `No pharmacies match “${statusQuery}”.` : "No active sites."}
            </div> : <table style={S.table}>
              <thead>
                <tr>
                  {cols.map(c => <th key={c.key} style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort(c.key)} title={`Sort by ${c.label.toLowerCase()}`}>
                      {c.label}{arrow(c.key)}
                    </th>)}
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => <tr key={String(s.pharmacyId)} onClick={() => openHandoverPopup(s)} style={{
              cursor: "pointer",
              background: rowBg(s)
            }} title="Click for this site's handover items">
                    <td style={{
                ...S.td,
                fontWeight: 600
              }}>{displayName(s.pharmacyName, masked)}</td>
                    <td style={S.td}>{STATUS_LABEL[s.status] || s.status}</td>
                    <td style={S.td}>{s.overdueCount ?? 0}</td>
                    <td style={S.td}>{s.pendingCount ?? 0}</td>
                    <td style={S.td}>{s.completedLast30d ?? 0}</td>
                    <td style={S.td}>
                      {s.onTimeLast30d ?? 0}
                      {s.onTimePctLast30d != null && ` (${s.onTimePctLast30d}%)`}
                    </td>
                  </tr>)}
              </tbody>
            </table>}
          {popupLoading && <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginTop: 6
        }}>
              Loading handover items…
            </div>}
        </div>;
    })()}

      {/* Date checking: two tabs — the existing drug expiry grid and the
          all-branches date-checking AREAS grid (same style as cleaning). */}
      {mod.key === "dateChecking" && <div style={{
      display: "flex",
      gap: 6,
      marginBottom: 12,
      borderBottom: "1px solid #F0F0F0"
    }}>
          {[{
        key: "drugs",
        label: "Drugs"
      }, {
        key: "areas",
        label: "Date checking areas"
      }].map(t => <button key={t.key} onClick={() => setDcTab(t.key)} style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        fontSize: 14,
        padding: "8px 14px",
        fontWeight: dcTab === t.key ? 600 : 400,
        color: dcTab === t.key ? "#0E8CFF" : "#434343",
        borderBottom: dcTab === t.key ? "2px solid #0E8CFF" : "2px solid transparent",
        marginBottom: -1
      }}>
              {t.label}
            </button>)}
        </div>}

      {/* Tab 2: all-branches date-checking areas grid. */}
      {mod.key === "dateChecking" && dcTab === "areas" && mod.areaFleet && <AreaFleetGrid mod={mod} token={token} noun={mod.areaFleet.noun} />}

      {/* Tab 1 — Date checking drugs: per-pharmacy grid with sortable columns
          + search. Columns: Pharmacy, Status, Expired, Expiring <1m/<2m/<3m.
          Row bg: expired>0 → red, else expiring<1m>0 → amber, else none. */}
      {mod.key === "dateChecking" && dcTab === "drugs" && (() => {
      const all = fleetStatus || [];
      const q = statusQuery.trim().toLowerCase();
      const rows = q ? all.filter(s => (s.pharmacyName || "").toLowerCase().includes(q)) : all;
      const RED = "#fde2e1",
        AMBER = "#FFF7E6",
        GREEN = "#F6FFED";
      // Worst severity for the pharmacy-name cell: expired → red,
      // else expiring<1m → amber, else green.
      const nameBg = s => s.expired > 0 ? RED : s.expiring1m > 0 ? AMBER : GREEN;

      // Data cell definitions: field, label, urgency tone, cell key for the
      // drug popup. Expired=red, <1m=amber, <2m/<3m=none, removed/notPresent=none.
      const dataCols = [{
        key: "expired",
        field: "expired",
        label: "Expired",
        tone: RED,
        cell: "expired"
      }, {
        key: "exp1m",
        field: "expiring1m",
        label: "Expiring <1mth",
        tone: AMBER,
        cell: "exp1m"
      }, {
        key: "exp2m",
        field: "expiring2m",
        label: "Expiring <2mth",
        tone: null,
        cell: "exp2m"
      }, {
        key: "exp3m",
        field: "expiring3m",
        label: "Expiring <3mth",
        tone: null,
        cell: "exp3m"
      }, {
        key: "removed",
        field: "removedLast60d",
        label: "Removed (60d)",
        tone: null,
        cell: "removed"
      }, {
        key: "notPresent",
        field: "notPresentLast60d",
        label: "Not present (60d)",
        tone: null,
        cell: "notPresent"
      }];

      // Sort: pharmacy name or any data column.
      const sortGetters = {
        pharmacy: s => (s.pharmacyName || "").toLowerCase(),
        ...Object.fromEntries(dataCols.map(c => [c.key, s => s[c.field] ?? 0]))
      };
      const dir = statusSort.dir === "desc" ? -1 : 1;
      const getter = sortGetters[statusSort.key] || sortGetters.expired;
      const sorted = [...rows].sort((a, b) => {
        const av = getter(a),
          bv = getter(b);
        let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
        if (cmp === 0) cmp = (a.pharmacyName || "").localeCompare(b.pharmacyName || "");
        return cmp * dir;
      });
      const toggleSort = key => setStatusSort(prev => prev.key === key ? {
        key,
        dir: prev.dir === "asc" ? "desc" : "asc"
      } : {
        key,
        dir: "asc"
      });
      const arrow = key => statusSort.key === key ? statusSort.dir === "asc" ? " ▲" : " ▼" : "";
      return <div style={{
        marginTop: 8,
        marginBottom: 18
      }}>
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
          flexWrap: "wrap"
        }}>
            <div style={{
            fontWeight: 600
          }}>
              Site status{" "}
              <span style={{
              fontWeight: 400,
              color: "#5D5D5D",
              fontSize: 13
            }}>
                ({all.length} active site{all.length === 1 ? "" : "s"})
              </span>
            </div>
            {/* Monthly removed/expired drugs email — moved here from Settings. */}
            <div style={{
            marginLeft: "auto"
          }}>
              <ExpiredEmailPanel token={token} />
            </div>
          </div>

          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10
        }}>
            <input type="text" value={statusQuery} onChange={e => setStatusQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
            padding: "6px 10px",
            fontSize: 13,
            border: "1px solid #D9D9D9",
            borderRadius: 6,
            width: 260,
            outline: "none"
          }} />
            {statusQuery && <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>}
            <span style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>
              {statusQuery ? `${sorted.length} match${sorted.length === 1 ? "" : "es"}` : `${sorted.length} site${sorted.length === 1 ? "" : "s"}`}
            </span>

            {/* Removed-drugs year export. Pick a year, then export a workbook
                with a summary grid + one tab per pharmacy for the whole year. */}
            <div style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
              <select value={removedExportYear} onChange={e => setRemovedExportYear(Number(e.target.value))} disabled={removedExportBusy} title="Year to export" style={{
              padding: "6px 8px",
              fontSize: 13,
              border: "1px solid #D9D9D9",
              borderRadius: 6,
              outline: "none",
              background: "#fff"
            }}>
                {Array.from({
                length: new Date().getFullYear() - 2021 + 1
              }, (_, k) => new Date().getFullYear() - k).map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <button style={{
              ...S.btn,
              opacity: removedExportBusy ? 0.6 : 1,
              cursor: removedExportBusy ? "default" : "pointer"
            }} disabled={removedExportBusy} onClick={() => runRemovedExport(removedExportYear)} title="Export removed drugs for the selected year (one tab per pharmacy)">
                {removedExportBusy ? "Exporting…" : "Export list of removed drugs"}
              </button>
            </div>
          </div>

          <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginBottom: 8
        }}>
            Expiry bands are cumulative (under 2 months includes under 1 month).
            Click any number to see the drugs behind it.
          </div>

          {sorted.length === 0 ? <div style={{
          fontSize: 13,
          color: "#5D5D5D"
        }}>
              {statusQuery ? `No pharmacies match “${statusQuery}”.` : "No active sites."}
            </div> : <table style={S.table}>
              <thead>
                <tr>
                  <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("pharmacy")} title="Sort by pharmacy name">
                    Pharmacy{arrow("pharmacy")}
                  </th>
                  {dataCols.map(c => <th key={c.key} style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort(c.key)} title={`Sort by ${c.label.toLowerCase()}`}>
                      {c.label}{arrow(c.key)}
                    </th>)}
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => <tr key={String(s.pharmacyId)}>
                    <td style={{
                ...S.td,
                fontWeight: 600,
                background: nameBg(s)
              }} title={(s.reasons || []).join("; ")}>
                      {displayName(s.pharmacyName, masked)}
                    </td>
                    {dataCols.map(c => {
                const v = s[c.field] ?? 0;
                // Tone only tints the cell when it has entries.
                const bg = v > 0 && c.tone ? c.tone : undefined;
                return <td key={c.key} onClick={e => {
                  e.stopPropagation();
                  if (v > 0) openDrugCell(s, c.cell, c.label, c.tone);
                }} style={{
                  ...S.td,
                  background: bg,
                  cursor: v > 0 ? "pointer" : "default",
                  textDecoration: v > 0 ? "underline" : "none"
                }} title={v > 0 ? "Click to see drugs" : ""}>
                          {v}
                        </td>;
              })}
                  </tr>)}
              </tbody>
            </table>}
          {popupLoading && <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginTop: 6
        }}>
              Loading drugs…
            </div>}
        </div>;
    })()}

      {/* CD register: one row per pharmacy with two parameter groups —
          balance checking (checked / total + overdue) and discrepancies
          (open count). Worst-state row colour. Click a row to expand inline
          and see that site's individual registers and open discrepancies. */}
      {mod.key === "cdRegister" && (() => {
      const all = fleetStatus || [];
      const q = statusQuery.trim().toLowerCase();
      const rows = q ? all.filter(s => (s.pharmacyName || "").toLowerCase().includes(q)) : all;
      const RED = "#fde2e1",
        AMBER = "#FFF7E6",
        GREEN = "#F6FFED";
      // Row tone: any overdue or open discrepancy → red; else never-checked
      // → amber; else green.
      const rowBg = s => s.overdueCount > 0 || s.openDiscrepancyCount > 0 ? RED : s.neverCheckedCount > 0 ? AMBER : GREEN;
      const STATUS_LABEL = {
        ok: "All up to date",
        unchecked: "Checks pending",
        fail: "Needs attention"
      };

      // Sort getters across both parameter groups.
      const sortGetters = {
        pharmacy: s => (s.pharmacyName || "").toLowerCase(),
        status: s => ({
          fail: 0,
          unchecked: 1,
          ok: 2
        })[s.status] ?? 3,
        checked: s => s.checkedCount ?? 0,
        overdue: s => s.overdueCount ?? 0,
        registers: s => s.registerCount ?? 0,
        discrep: s => s.openDiscrepancyCount ?? 0
      };
      const dir = statusSort.dir === "desc" ? -1 : 1;
      const getter = sortGetters[statusSort.key] || sortGetters.status;
      const sorted = [...rows].sort((a, b) => {
        const av = getter(a),
          bv = getter(b);
        let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
        if (cmp === 0) cmp = (a.pharmacyName || "").localeCompare(b.pharmacyName || "");
        return cmp * dir;
      });
      const toggleSort = key => setStatusSort(prev => prev.key === key ? {
        key,
        dir: prev.dir === "asc" ? "desc" : "asc"
      } : {
        key,
        dir: "asc"
      });
      const arrow = key => statusSort.key === key ? statusSort.dir === "asc" ? " ▲" : " ▼" : "";
      const count = st => all.filter(s => s.status === st).length;

      // Sub-header styling for the two grouped parameter bands.
      const groupTh = (bg, color) => ({
        ...S.th,
        background: bg,
        color,
        textAlign: "center",
        fontWeight: 700,
        fontSize: 12
      });
      return <div style={{
        marginTop: 8,
        marginBottom: 18
      }}>
          <div style={{
          fontWeight: 600,
          marginBottom: 6
        }}>
            Site status{" "}
            <span style={{
            fontWeight: 400,
            color: "#5D5D5D",
            fontSize: 13
          }}>
              ({all.length} active site{all.length === 1 ? "" : "s"})
            </span>
          </div>

          {/* Summary chips */}
          <div style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 10
        }}>
            {["fail", "unchecked", "ok"].map(st => <span key={st} style={{
            background: st === "fail" ? RED : st === "unchecked" ? AMBER : GREEN,
            borderRadius: 6,
            padding: "3px 10px",
            fontSize: 12,
            fontWeight: 600,
            color: "#434343"
          }}>
                {STATUS_LABEL[st]}: {count(st)}
              </span>)}
          </div>

          {/* Search */}
          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10
        }}>
            <input type="text" value={statusQuery} onChange={e => setStatusQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
            padding: "6px 10px",
            fontSize: 13,
            border: "1px solid #D9D9D9",
            borderRadius: 6,
            width: 260,
            outline: "none"
          }} />
            {statusQuery && <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>}
            <span style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>
              {statusQuery ? `${sorted.length} match${sorted.length === 1 ? "" : "es"}` : `${sorted.length} site${sorted.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginBottom: 8
        }}>
            One row per pharmacy. Click a row to expand its individual registers
            and any open discrepancies.
          </div>

          {sorted.length === 0 ? <div style={{
          fontSize: 13,
          color: "#5D5D5D"
        }}>
              {statusQuery ? `No pharmacies match “${statusQuery}”.` : "No active sites."}
            </div> : <table style={S.table}>
              <thead>
                {/* Grouped parameter bands */}
                <tr>
                  <th style={{
                ...S.th,
                background: "#FAFAFA"
              }} rowSpan={2} onClick={() => toggleSort("pharmacy")} title="Sort by pharmacy name">
                    <span style={{
                  cursor: "pointer",
                  userSelect: "none"
                }}>
                      Pharmacy{arrow("pharmacy")}
                    </span>
                  </th>
                  <th style={{
                ...S.th,
                background: "#FAFAFA"
              }} rowSpan={2} onClick={() => toggleSort("status")} title="Sort by status (worst first)">
                    <span style={{
                  cursor: "pointer",
                  userSelect: "none"
                }}>
                      Status{arrow("status")}
                    </span>
                  </th>
                  <th colSpan={3} style={groupTh("#eef5ff", "#1e3a5f")}>
                    Balance checking
                  </th>
                  <th colSpan={1} style={groupTh("#fff4ed", "#9a3412")}>
                    Discrepancies
                  </th>
                </tr>
                <tr>
                  <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none",
                background: "#f5f9ff",
                textAlign: "center"
              }} onClick={() => toggleSort("checked")} title="Registers checked within the interval, out of total active registers">
                    Checked / total{arrow("checked")}
                  </th>
                  <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none",
                background: "#f5f9ff",
                textAlign: "center"
              }} onClick={() => toggleSort("overdue")} title="Registers past their balance-check interval">
                    Overdue{arrow("overdue")}
                  </th>
                  <th style={{
                ...S.th,
                background: "#f5f9ff",
                textAlign: "center"
              }} title="Registers never balance-checked">
                    Never checked
                  </th>
                  <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none",
                background: "#fff8f3",
                textAlign: "center"
              }} onClick={() => toggleSort("discrep")} title="Unresolved discrepancies open right now">
                    Open{arrow("discrep")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => {
              const id = String(s.pharmacyId);
              const isOpen = cdExpanded === id;
              const detail = cdDetail[id];
              const checkedRatio = `${s.checkedCount ?? 0} / ${s.registerCount ?? 0}`;
              return <React.Fragment key={id}>
                      <tr onClick={() => toggleCdRow(s)} style={{
                  cursor: "pointer",
                  background: rowBg(s)
                }} title={(s.reasons || []).join("; ") || "All up to date — click to view registers"}>
                        <td style={{
                    ...S.td,
                    fontWeight: 600
                  }}>
                          <span style={{
                      display: "inline-block",
                      width: 14,
                      color: "#5D5D5D"
                    }}>
                            {isOpen ? "▾" : "▸"}
                          </span>
                          {displayName(s.pharmacyName, masked)}
                        </td>
                        <td style={S.td}>{STATUS_LABEL[s.status] || s.status}</td>
                        <td style={{
                    ...S.td,
                    textAlign: "center"
                  }}>{checkedRatio}</td>
                        <td style={{
                    ...S.td,
                    textAlign: "center",
                    color: s.overdueCount > 0 ? "#CF1322" : "#434343",
                    fontWeight: s.overdueCount > 0 ? 600 : 400
                  }}>
                          {s.overdueCount ?? 0}
                        </td>
                        <td style={{
                    ...S.td,
                    textAlign: "center",
                    color: s.neverCheckedCount > 0 ? "#b45309" : "#434343"
                  }}>
                          {s.neverCheckedCount ?? 0}
                        </td>
                        <td style={{
                    ...S.td,
                    textAlign: "center",
                    color: s.openDiscrepancyCount > 0 ? "#CF1322" : "#434343",
                    fontWeight: s.openDiscrepancyCount > 0 ? 600 : 400
                  }}>
                          {s.openDiscrepancyCount ?? 0}
                        </td>
                      </tr>
                      {isOpen && <tr>
                          <td colSpan={6} style={{
                    ...S.td,
                    background: "#fafafa",
                    padding: 12
                  }}>
                            {cdLoadingId === id ? <div style={{
                      fontSize: 13,
                      color: "#5D5D5D"
                    }}>
                                Loading registers…
                              </div> : !detail ? <div style={{
                      fontSize: 13,
                      color: "#5D5D5D"
                    }}>
                                No detail available.
                              </div> : <CdRegisterExpand detail={detail} />}
                          </td>
                        </tr>}
                    </React.Fragment>;
            })}
              </tbody>
            </table>}
        </div>;
    })()}

      {/* Visitor logs: per-pharmacy snapshot. Currently on site (amber if >0),
          all-time Total visitors, and Stale (on site but signed in before
          today). Click a row to expand visitors reverse-chronologically:
          stale = pink, current-today = amber. */}
      {mod.key === "visitorLogs" && Array.isArray(snapshot) && (() => {
      const all = snapshot || [];
      const q = statusQuery.trim().toLowerCase();
      const rows = q ? all.filter(s => (s.pharmacyName || "").toLowerCase().includes(q)) : all;
      const AMBER = "#FFF7E6",
        PINK = "#fde2e1";
      const sortGetters = {
        pharmacy: s => (s.pharmacyName || "").toLowerCase(),
        current: s => s.currentOnSite ?? 0,
        total: s => s.totalVisitors ?? 0,
        stale: s => s.staleOnSite ?? 0
      };
      const dir = statusSort.dir === "desc" ? -1 : 1;
      const getter = sortGetters[statusSort.key] || sortGetters.pharmacy;
      const sorted = [...rows].sort((a, b) => {
        const av = getter(a),
          bv = getter(b);
        let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
        if (cmp === 0) cmp = (a.pharmacyName || "").localeCompare(b.pharmacyName || "");
        return cmp * dir;
      });
      const toggleSort = key => setStatusSort(prev => prev.key === key ? {
        key,
        dir: prev.dir === "asc" ? "desc" : "asc"
      } : {
        key,
        dir: "asc"
      });
      const arrow = key => statusSort.key === key ? statusSort.dir === "asc" ? " ▲" : " ▼" : "";
      return <div style={{
        marginTop: 16
      }}>
          <div style={{
          fontWeight: 600,
          marginBottom: 6
        }}>Snapshot</div>

          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10
        }}>
            <input type="text" value={statusQuery} onChange={e => setStatusQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
            padding: "6px 10px",
            fontSize: 13,
            border: "1px solid #D9D9D9",
            borderRadius: 6,
            width: 260,
            outline: "none"
          }} />
            {statusQuery && <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>}
          </div>

          <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginBottom: 8
        }}>
            Click a pharmacy to expand its visitor history. Stale = on site but
            signed in before today (likely not signed out).
          </div>

          <table style={S.table}>
            <thead>
              <tr>
                <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("pharmacy")}>
                  Pharmacy{arrow("pharmacy")}
                </th>
                <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none",
                textAlign: "center"
              }} onClick={() => toggleSort("current")} title="Visitors signed in today and not yet signed out">
                  Currently on site{arrow("current")}
                </th>
                <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none",
                textAlign: "center"
              }} onClick={() => toggleSort("total")} title="All-time count of visitor entries">
                  Total visitors{arrow("total")}
                </th>
                <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none",
                textAlign: "center"
              }} onClick={() => toggleSort("stale")} title="On site but signed in before today — likely not signed out">
                  Stale{arrow("stale")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => {
              const id = String(s.pharmacyId);
              const isOpen = vlExpanded === id;
              const visitors = vlDetail[id];
              const cur = s.currentOnSite ?? 0;
              const stale = s.staleOnSite ?? 0;
              return <React.Fragment key={id}>
                    <tr onClick={() => toggleVlRow(s)} style={{
                  cursor: "pointer"
                }} title="Click to view visitor history">
                      <td style={{
                    ...S.td,
                    fontWeight: 600
                  }}>
                        <span style={{
                      display: "inline-block",
                      width: 14,
                      color: "#5D5D5D"
                    }}>
                          {isOpen ? "▾" : "▸"}
                        </span>
                        {displayName(s.pharmacyName, masked)}
                      </td>
                      <td style={{
                    ...S.td,
                    textAlign: "center",
                    background: cur > 0 ? AMBER : undefined,
                    fontWeight: cur > 0 ? 600 : 400,
                    color: cur > 0 ? "#b45309" : "#434343"
                  }}>
                        {cur}
                      </td>
                      <td style={{
                    ...S.td,
                    textAlign: "center"
                  }}>{s.totalVisitors ?? 0}</td>
                      <td style={{
                    ...S.td,
                    textAlign: "center",
                    background: stale > 0 ? PINK : undefined,
                    fontWeight: stale > 0 ? 600 : 400,
                    color: stale > 0 ? "#CF1322" : "#434343"
                  }}>
                        {stale}
                      </td>
                    </tr>
                    {isOpen && <tr>
                        <td colSpan={4} style={{
                    ...S.td,
                    background: "#fafafa",
                    padding: 12
                  }}>
                          {vlLoadingId === id ? <div style={{
                      fontSize: 13,
                      color: "#5D5D5D"
                    }}>
                              Loading visitors…
                            </div> : !visitors ? <div style={{
                      fontSize: 13,
                      color: "#5D5D5D"
                    }}>
                              No detail available.
                            </div> : <VisitorList visitors={visitors} />}
                        </td>
                      </tr>}
                  </React.Fragment>;
            })}
            </tbody>
          </table>
        </div>;
    })()}

      {/* Patient safety: per-pharmacy snapshot with an all-time "Total
          submitted" column. Click a row to expand its incidents inline
          (reverse-chronological): date/time, harm, LFPSE, RP reporting. */}
      {mod.key === "patientSafety" && Array.isArray(snapshot) && (() => {
      const all = snapshot || [];
      const q = statusQuery.trim().toLowerCase();
      const rows = q ? all.filter(s => (s.pharmacyName || "").toLowerCase().includes(q)) : all;

      // Sortable columns across the snapshot figures.
      const sortGetters = {
        pharmacy: s => (s.pharmacyName || "").toLowerCase(),
        total30: s => s.last30Days?.total ?? 0,
        submitted: s => s.lfpseSubmittedTotal ?? 0,
        lfpse: s => s.last30Days?.lfpseSubmittedPct ?? -1,
        rp: s => s.last30Days?.rpNotifiedPct ?? -1
      };
      const dir = statusSort.dir === "desc" ? -1 : 1;
      const getter = sortGetters[statusSort.key] || sortGetters.pharmacy;
      const sorted = [...rows].sort((a, b) => {
        const av = getter(a),
          bv = getter(b);
        let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
        if (cmp === 0) cmp = (a.pharmacyName || "").localeCompare(b.pharmacyName || "");
        return cmp * dir;
      });
      const toggleSort = key => setStatusSort(prev => prev.key === key ? {
        key,
        dir: prev.dir === "asc" ? "desc" : "asc"
      } : {
        key,
        dir: "asc"
      });
      const arrow = key => statusSort.key === key ? statusSort.dir === "asc" ? " ▲" : " ▼" : "";
      return <div style={{
        marginTop: 16
      }}>
          <div style={{
          fontWeight: 600,
          marginBottom: 6
        }}>Snapshot</div>

          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10
        }}>
            <input type="text" value={statusQuery} onChange={e => setStatusQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
            padding: "6px 10px",
            fontSize: 13,
            border: "1px solid #D9D9D9",
            borderRadius: 6,
            width: 260,
            outline: "none"
          }} />
            {statusQuery && <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>}
          </div>

          <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginBottom: 8
        }}>
            Click a pharmacy to expand its incident history.
          </div>

          <table style={S.table}>
            <thead>
              <tr>
                <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("pharmacy")}>
                  Pharmacy{arrow("pharmacy")}
                </th>
                <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("total30")} title="Incidents recorded in the last 30 days">
                  Total (30d){arrow("total30")}
                </th>
                <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("submitted")} title="All-time count of incidents submitted to LFPSE">
                  Total submitted{arrow("submitted")}
                </th>
                <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("lfpse")} title="Percentage of last-30-day incidents submitted to LFPSE">
                  LFPSE submitted{arrow("lfpse")}
                </th>
                <th style={{
                ...S.th,
                cursor: "pointer",
                userSelect: "none"
              }} onClick={() => toggleSort("rp")} title="Percentage of last-30-day incidents where the RP was notified">
                  RP notified{arrow("rp")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(s => {
              const id = String(s.pharmacyId);
              const isOpen = psExpanded === id;
              const incidents = psDetail[id];
              const d = s.last30Days || {};
              const incomplete = (s.lfpseIncompleteCount ?? 0) > 0;
              return <React.Fragment key={id}>
                    <tr onClick={() => togglePsRow(s)} style={{
                  cursor: "pointer",
                  background: incomplete ? "#fde2e1" : undefined
                }} title={incomplete ? `${s.lfpseIncompleteCount} incident(s) with LFPSE ticked but no reference — click to view` : "Click to view incident history"}>
                      <td style={{
                    ...S.td,
                    fontWeight: 600
                  }}>
                        <span style={{
                      display: "inline-block",
                      width: 14,
                      color: "#5D5D5D"
                    }}>
                          {isOpen ? "▾" : "▸"}
                        </span>
                        {displayName(s.pharmacyName, masked)}
                        {incomplete && <span style={{
                      marginLeft: 6,
                      fontSize: 11,
                      color: "#CF1322",
                      fontWeight: 600
                    }} title="Incomplete LFPSE entries">
                            ⚠ {s.lfpseIncompleteCount}
                          </span>}
                      </td>
                      <td style={S.td}>{d.total ?? 0}</td>
                      <td style={S.td}>{s.lfpseSubmittedTotal ?? 0}</td>
                      <td style={S.td}>{fmtPct(d.lfpseSubmittedPct)}</td>
                      <td style={S.td}>{fmtPct(d.rpNotifiedPct)}</td>
                    </tr>
                    {isOpen && <tr>
                        <td colSpan={5} style={{
                    ...S.td,
                    background: "#fafafa",
                    padding: 12
                  }}>
                          {psLoadingId === id ? <div style={{
                      fontSize: 13,
                      color: "#5D5D5D"
                    }}>
                              Loading incidents…
                            </div> : !incidents ? <div style={{
                      fontSize: 13,
                      color: "#5D5D5D"
                    }}>
                              No detail available.
                            </div> : <PatientSafetyIncidents incidents={incidents} />}
                        </td>
                      </tr>}
                  </React.Fragment>;
            })}
            </tbody>
          </table>
        </div>;
    })()}

      {/* Messaging: per-pharmacy patient enrolment. Fixed mode columns
          (SMS, FCM, EMAIL, DECLINED) — None/unset modes are ignored. Total
          patients is shaded by sign-up rate (signed-up modes / total); the
          on-shelf-messaged % is shaded green→red. Non-engaged sites
          (SMS+EMAIL < 50) are highlighted red and can be filtered to. */}
      {mod.key === "messaging" && snapshot && typeof snapshot === "object" && (() => {
      const enrol = snapshot["patient-enrolment"] || [];
      const rate = snapshot["prescription-message-rate"] || [];
      const rateById = {};
      for (const r of rate) rateById[String(r.pharmacyId)] = r;

      // Fixed columns and the keys that count as "signed up".
      const MODE_COLS = [{
        key: "SMS",
        label: "SMS"
      }, {
        key: "FCM",
        label: "FCM"
      }, {
        key: "EMAIL",
        label: "EMAIL"
      }, {
        key: "DECLINED",
        label: "DECLINED"
      }];
      const SIGNED_UP_KEYS = ["SMS", "FCM", "EMAIL", "DECLINED"];
      const modeVal = (e, k) => e.current?.byMode?.[k] ?? 0;
      const signedUp = e => SIGNED_UP_KEYS.reduce((t, k) => t + modeVal(e, k), 0);
      const signUpPct = e => {
        const tot = e.current?.total ?? 0;
        return tot > 0 ? Math.round(signedUp(e) / tot * 10000) / 100 : null;
      };
      // Non-engaged = SMS + EMAIL < 50.
      const isNonEngaged = e => modeVal(e, "SMS") + modeVal(e, "EMAIL") < 50;
      const q = statusQuery.trim().toLowerCase();
      let rows = q ? enrol.filter(e => (e.pharmacyName || "").toLowerCase().includes(q)) : enrol;
      if (nonEngagedOnly) rows = rows.filter(isNonEngaged);
      const sortGetters = {
        pharmacy: e => (e.pharmacyName || "").toLowerCase(),
        total: e => e.current?.total ?? 0,
        pct: e => {
          const r = rateById[String(e.pharmacyId)];
          return r && r.messagePct != null ? r.messagePct : -1;
        },
        ...Object.fromEntries(MODE_COLS.map(c => [`mode:${c.key}`, e => modeVal(e, c.key)]))
      };
      const dir = statusSort.dir === "desc" ? -1 : 1;
      const getter = sortGetters[statusSort.key] || sortGetters.pharmacy;
      const sorted = [...rows].sort((a, b) => {
        const av = getter(a),
          bv = getter(b);
        let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
        if (cmp === 0) cmp = (a.pharmacyName || "").localeCompare(b.pharmacyName || "");
        return cmp * dir;
      });
      const toggleSort = key => setStatusSort(prev => prev.key === key ? {
        key,
        dir: prev.dir === "asc" ? "desc" : "asc"
      } : {
        key,
        dir: "asc"
      });
      const arrow = key => statusSort.key === key ? statusSort.dir === "asc" ? " ▲" : " ▼" : "";
      const NON_ENGAGED_BG = "#fde2e1";
      const nonEngagedCount = enrol.filter(isNonEngaged).length;
      return <div style={{
        marginTop: 16
      }}>
          <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 6
        }}>
            <div style={{
            fontWeight: 600
          }}>
              Patient enrolment by preferred contact method
            </div>
            <button style={nonEngagedOnly ? S.btn : S.btnGhost} onClick={() => setNonEngagedOnly(v => !v)} title="Non-engaged = fewer than 50 patients on SMS + EMAIL combined">
              {nonEngagedOnly ? "Show all sites" : `Show non-engaged sites only${nonEngagedCount ? ` (${nonEngagedCount})` : ""}`}
            </button>
          </div>

          <div style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 10
        }}>
            <input type="text" value={statusQuery} onChange={e => setStatusQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
            padding: "6px 10px",
            fontSize: 13,
            border: "1px solid #D9D9D9",
            borderRadius: 6,
            width: 260,
            outline: "none"
          }} />
            {statusQuery && <button style={S.btnGhost} onClick={() => setStatusQuery("")}>Clear</button>}
            <span style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>
              {`${sorted.length} site${sorted.length === 1 ? "" : "s"}`}
            </span>
          </div>

          <div style={{
          fontSize: 12,
          color: "#5D5D5D",
          marginBottom: 8
        }}>
            Each mode column is the <b>number of patients on the books who have
            chosen that contact method</b> as their preference — not messages
            sent, and not time-bounded. Total patients is shaded by sign-up rate
            (SMS + FCM + EMAIL + DECLINED, over all patients). "On-shelf messaged"
            is bags on shelf with a message sent, as a share of all bags on shelf,
            shaded green (high) to red (low). Rows in red are non-engaged sites
            (under 50 patients on SMS + EMAIL).
          </div>

          <div style={{
          overflowX: "auto"
        }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{
                  ...S.th,
                  background: "#FAFAFA"
                }} rowSpan={2} onClick={() => toggleSort("pharmacy")}>
                  <span style={{
                    cursor: "pointer",
                    userSelect: "none"
                  }}>
                    Pharmacy{arrow("pharmacy")}
                  </span>
                </th>
                <th style={{
                  ...S.th,
                  background: "#FAFAFA",
                  textAlign: "center"
                }} rowSpan={2} onClick={() => toggleSort("total")} title="Total enrolled patients (shaded by sign-up rate)">
                  <span style={{
                    cursor: "pointer",
                    userSelect: "none"
                  }}>
                    Total patients{arrow("total")}
                  </span>
                </th>
                <th colSpan={MODE_COLS.length} style={{
                  ...S.th,
                  background: "#eef5ff",
                  color: "#1e3a5f",
                  textAlign: "center",
                  fontWeight: 700,
                  fontSize: 12
                }} title="Number of patients on the books who chose each method as their preference — not messages sent">
                  Patients enrolled, by preferred contact method
                </th>
                <th style={{
                  ...S.th,
                  background: "#f0f6ff",
                  color: "#1e3a5f",
                  textAlign: "center"
                }} rowSpan={2} onClick={() => toggleSort("pct")} title="Bags currently on shelf with a message sent, as a % of all bags on shelf">
                  <span style={{
                    cursor: "pointer",
                    userSelect: "none"
                  }}>
                    Bags on shelf — messages sent{arrow("pct")}
                  </span>
                </th>
              </tr>
              <tr>
                {MODE_COLS.map(c => <th key={c.key} style={{
                  ...S.th,
                  cursor: "pointer",
                  userSelect: "none",
                  textAlign: "center",
                  background: "#f5f9ff"
                }} onClick={() => toggleSort(`mode:${c.key}`)} title={`Patients who prefer ${c.label}`}>
                    {c.label}{arrow(`mode:${c.key}`)}
                  </th>)}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? <tr>
                  <td style={{
                  ...S.td,
                  color: "#5D5D5D"
                }} colSpan={MODE_COLS.length + 3}>
                    {nonEngagedOnly ? "No non-engaged sites." : statusQuery ? `No pharmacies match “${statusQuery}”.` : "No data."}
                  </td>
                </tr> : sorted.map(e => {
                const id = String(e.pharmacyId);
                const r = rateById[id];
                const pct = r && r.messagePct != null ? r.messagePct : null;
                const sPct = signUpPct(e);
                const nonEng = isNonEngaged(e);
                const rowBg = nonEng ? NON_ENGAGED_BG : undefined;
                return <tr key={id} style={{
                  background: rowBg
                }} title={nonEng ? "Non-engaged: under 50 patients on SMS + EMAIL" : ""}>
                    <td style={{
                    ...S.td,
                    fontWeight: 600
                  }}>{displayName(e.pharmacyName, masked)}</td>
                    <td style={{
                    ...S.td,
                    textAlign: "center",
                    // Sign-up-rate shade; non-engaged red wins.
                    background: nonEng ? NON_ENGAGED_BG : pctBg(sPct)
                  }} title={sPct != null ? `${signedUp(e)} signed up of ${e.current?.total ?? 0} (${sPct}%)` : ""}>
                      {e.current?.total ?? 0}
                    </td>
                    {MODE_COLS.map(c => <td key={c.key} style={{
                    ...S.td,
                    textAlign: "center"
                  }}>
                        {modeVal(e, c.key)}
                      </td>)}
                    <td style={{
                    ...S.td,
                    textAlign: "center",
                    fontWeight: 600,
                    background: nonEng ? NON_ENGAGED_BG : pctBg(pct) || "#f7faff"
                  }} title={r ? `${r.messaged ?? 0} of ${r.onShelf ?? 0} on-shelf bags messaged` : ""}>
                      {pct != null ? `${pct}%` : "—"}
                    </td>
                  </tr>;
              })}
            </tbody>
          </table>
          </div>
        </div>;
    })()}

      {snapshot && mod.key !== "handover" && mod.key !== "dateChecking" && mod.key !== "cdRegister" && mod.key !== "patientSafety" && mod.key !== "visitorLogs" && mod.key !== "messaging" && <div style={{
      marginTop: 16
    }}>
          <div style={{
        fontWeight: 600,
        marginBottom: 6
      }}>Snapshot</div>
          {SNAPSHOT_FORMATTERS[mod.path] ? SNAPSHOT_FORMATTERS[mod.path](snapshot) : <pre style={{
        background: "#FAFAFA",
        padding: 10,
        borderRadius: 4,
        fontSize: 11,
        overflow: "auto",
        maxHeight: 480
      }}>
              {JSON.stringify(snapshot, null, 2)}
            </pre>}
        </div>}

      {/* Fridge temperature history popup — same shell + PopupContent the
          main Mockupp dashboard uses, so the graphs render identically. */}
      {popupData && <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setPopupData(null)}>X</button>
            <PopupContent popupData={popupData} />
          </div>
        </div>}

      {/* Handover grouped popup: overdue (red) → pending (amber) →
          completed today (green). */}
      {groupedPopup && <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setGroupedPopup(null)}>X</button>
            <h3 style={{
          marginTop: 0
        }}>Handover items — {displayName(groupedPopup.pharmacyName, masked)}</h3>
            {[{
          key: "overdue",
          title: "Overdue",
          bg: "#fde2e1",
          showDue: true
        }, {
          key: "pending",
          title: "Pending",
          bg: "#FFF7E6",
          showDue: true
        }, {
          key: "completedToday",
          title: "Completed today",
          bg: "#F6FFED",
          showCompleted: true
        }].map(group => {
          const items = groupedPopup[group.key] || [];
          return <div key={group.key} style={{
            marginBottom: 16
          }}>
                  <div style={{
              fontWeight: 600,
              marginBottom: 4
            }}>
                    {group.title} ({items.length})
                  </div>
                  {items.length === 0 ? <div style={{
              fontSize: 12,
              color: "#5D5D5D"
            }}>None.</div> : <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Topic</th>
                          <th style={S.th}>Description</th>
                          <th style={S.th}>For</th>
                          {group.showDue && <th style={S.th}>Due</th>}
                          {group.showCompleted && <th style={S.th}>Completed</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it, i) => <tr key={i} style={{
                  background: group.bg
                }}>
                            <td style={S.td}>{it.topic}</td>
                            <td style={S.td}>{it.description}</td>
                            <td style={S.td}>{it.fao}</td>
                            {group.showDue && <td style={S.td}>
                                {it.dueDate}
                                {group.key === "overdue" && it.daysOverdue != null && ` (${it.daysOverdue}d)`}
                              </td>}
                            {group.showCompleted && <td style={S.td}>{it.completedAt}</td>}
                          </tr>)}
                      </tbody>
                    </table>}
                </div>;
        })}
          </div>
        </div>}

      {/* Date-checking drug list for one clicked cell. Header tinted by the
          cell's urgency; rows share that tint. Most urgent (soonest expiry)
          first comes pre-sorted from the server. */}
      {/* Removed-drugs full history: paged (2 months per fetch), sortable
          price/value, inline price editing. */}
      {removedPopup && (() => {
      const rp = removedPopup;
      const qtyOf = d => {
        const n = Number(d.quantity);
        return Number.isFinite(n) && n > 0 ? n : 1;
      };
      const valueOf = d => d.price != null ? d.price * qtyOf(d) : null;
      const sorted = [...rp.rows];
      if (rp.sort.key === "price" || rp.sort.key === "value") {
        const get = rp.sort.key === "price" ? d => d.price : valueOf;
        sorted.sort((a, b) => {
          const av = get(a),
            bv = get(b);
          if (av == null && bv == null) return 0;
          if (av == null) return 1; // unpriced rows always last
          if (bv == null) return -1;
          return (av - bv) * rp.sort.dir;
        });
      } // "date" keeps the fetch order (removedAt desc)
      const setSort = key => setRemovedPopup(prev => prev ? {
        ...prev,
        sort: {
          key,
          dir: prev.sort.key === key ? -prev.sort.dir : key === "date" ? -1 : 1
        }
      } : prev);
      const arrow = key => rp.sort.key === key ? rp.sort.dir === 1 ? " ▲" : " ▼" : "";
      const sortableTh = (key, label, title) => <th style={{
        ...S.th,
        cursor: "pointer",
        userSelect: "none"
      }} title={title} onClick={() => setSort(key)}>{label}{arrow(key)}</th>;
      const startEdit = d => setRemovedPopup(prev => prev ? {
        ...prev,
        editing: {
          drugId: d.drugId,
          text: d.price != null ? (d.price / 100).toFixed(2) : ""
        }
      } : prev);
      const commitEdit = d => {
        const pounds = Number(rp.editing.text);
        if (!Number.isFinite(pounds) || pounds < 0) {
          setRemovedPopup(prev => prev ? {
            ...prev,
            editing: null
          } : prev);
          return;
        }
        saveRemovedPrice(d.drugId, Math.round(pounds * 100));
      };
      // Only the unit PRICE is editable — the value column is derived
      // (price × qty) and recomputes automatically.
      const priceCell = (d, display) => {
        const isEditing = rp.editing && rp.editing.drugId === d.drugId;
        if (isEditing) {
          return <td style={{
            ...S.td,
            background: "#E6F7FF"
          }}>
                <input autoFocus value={rp.editing.text} onChange={e => setRemovedPopup(prev => prev ? {
              ...prev,
              editing: {
                ...prev.editing,
                text: e.target.value
              }
            } : prev)} onKeyDown={e => {
              if (e.key === "Enter") commitEdit(d);
              if (e.key === "Escape") setRemovedPopup(prev => prev ? {
                ...prev,
                editing: null
              } : prev);
            }} onBlur={() => commitEdit(d)} style={{
              width: 70,
              fontSize: 12,
              padding: "2px 4px",
              border: "1px solid #91D5FF",
              borderRadius: 3
            }} />
              </td>;
        }
        return <td style={{
          ...S.td,
          cursor: "pointer",
          background: d.price === null ? "#FFF7E6" : d.priceSource === "manual" ? "#ecfdf5" : undefined
        }} title={d.price === null ? "No price found in dmddata — click to enter one" : d.priceSource === "manual" ? "Manually entered price — click to change" : "Click to override the dmddata price"} onClick={() => startEdit(d)}>
              {display}
            </td>;
      };
      return <div className="popup">
            <div className="popup-content" style={{
          maxWidth: 980
        }}>
              <button className="close-btn" onClick={() => setRemovedPopup(null)}>X</button>
              <h3 style={{
            marginTop: 0,
            background: rp.tone || undefined,
            padding: rp.tone ? "4px 8px" : 0,
            borderRadius: 4,
            display: "inline-block"
          }}>
                Removed drugs — {displayName(rp.pharmacyName, masked)} ({rp.rows.length} loaded{rp.hasMore ? ", more available" : ""})
              </h3>
              <div style={{
            fontSize: 12,
            color: "#5D5D5D",
            marginBottom: 8
          }}>
                Full removal history, loaded two months at a time — scroll to the bottom to
                fetch older entries. Click a price to enter a manual price — the value
                column recomputes automatically (green = manually priced, amber = no
                dmddata price found).
              </div>
              {rp.rows.length === 0 && !rp.hasMore ? <div style={{
            fontSize: 13,
            color: "#5D5D5D"
          }}>No removed drugs recorded.</div> : <div style={{
            maxHeight: 460,
            overflowY: "auto"
          }} onScroll={e => {
            const el = e.currentTarget;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) loadMoreRemoved();
          }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Drug</th>
                        <th style={S.th}>Location</th>
                        {sortableTh("date", "Removed", "Sort by removal date")}
                        <th style={S.th}>Expiry</th>
                        <th style={S.th}>Qty</th>
                        <th style={S.th}>Batch</th>
                        {sortableTh("price", "Price (£)", "Sort by unit price")}
                        {sortableTh("value", "Value (£)", "Sort by line value (price × qty)")}
                      </tr>
                    </thead>
                    <tbody>
                      {sorted.map(d => {
                  const val = valueOf(d);
                  return <tr key={d.drugId}>
                            <td style={S.td}>{d.drugName}</td>
                            <td style={S.td}>{d.location}</td>
                            <td style={S.td}>{d.removedAt}</td>
                            <td style={S.td}>{d.expiryDate}</td>
                            <td style={S.td}>{d.quantity}</td>
                            <td style={S.td}>{d.batchNumber}</td>
                            {priceCell(d, d.price != null ? `£${(d.price / 100).toFixed(2)}` : "—")}
                            <td style={S.td} title="Computed automatically (price × qty)">
                              {val != null ? `£${(val / 100).toFixed(2)}` : "—"}
                            </td>
                          </tr>;
                })}
                    </tbody>
                  </table>
                  {rp.loadingMore && <div style={{
              textAlign: "center",
              padding: 10,
              fontSize: 12,
              color: "#5D5D5D"
            }}>
                      Loading older entries…
                    </div>}
                  {!rp.hasMore && rp.rows.length > 0 && <div style={{
              textAlign: "center",
              padding: 10,
              fontSize: 11,
              color: "#8C8C8C"
            }}>
                      Full history loaded.
                    </div>}
                </div>}
            </div>
          </div>;
    })()}

      {drugPopup && <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setDrugPopup(null)}>X</button>
            <h3 style={{
          marginTop: 0,
          background: drugPopup.tone || undefined,
          padding: drugPopup.tone ? "4px 8px" : 0,
          borderRadius: 4,
          display: "inline-block"
        }}>
              {drugPopup.cellLabel} — {displayName(drugPopup.pharmacyName, masked)} ({drugPopup.drugs.length})
            </h3>
            {drugPopup.drugs.length === 0 ? <div style={{
          fontSize: 13,
          color: "#5D5D5D"
        }}>No drugs.</div> : (() => {
          // Show a Value (£) column only when these rows carry price data
          // (the removed / not-present cells enrich from dmddata; price is
          // in pence, null = looked up but no priced match).
          const hasPrice = drugPopup.drugs.some(d => d.price !== undefined);
          return <div style={{
            maxHeight: 420,
            overflowY: "auto"
          }}>
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Drug</th>
                      <th style={S.th}>Location</th>
                      <th style={S.th}>Expiry</th>
                      <th style={S.th}>Qty</th>
                      <th style={S.th}>Batch</th>
                      {hasPrice && <th style={S.th}>Price (£)</th>}
                      {hasPrice && <th style={S.th}>Value (£)</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {drugPopup.drugs.map((d, i) => {
                  const qty = Number(d.quantity) || 0;
                  const priced = d.price !== undefined && d.price !== null;
                  const value = priced ? d.price * qty / 100 : null;
                  return <tr key={i} style={{
                    background: drugPopup.tone || undefined
                  }}>
                        <td style={S.td}>{d.drugName}</td>
                        <td style={S.td}>{d.location}</td>
                        <td style={S.td}>{d.expiryDate}</td>
                        <td style={S.td}>{d.quantity}</td>
                        <td style={S.td}>{d.batchNumber}</td>
                        {hasPrice && <td style={{
                      ...S.td,
                      background: d.price === null ? "#FFF7E6" : undefined
                    }} title={d.price === null ? "No price found in dmddata" : ""}>
                            {priced ? `£${(d.price / 100).toFixed(2)}` : "—"}
                          </td>}
                        {hasPrice && <td style={{
                      ...S.td,
                      background: d.price === null ? "#FFF7E6" : undefined
                    }} title={d.price === null ? "No price found in dmddata" : ""}>
                            {priced ? `£${value.toFixed(2)}` : "—"}
                          </td>}
                      </tr>;
                })}
                  </tbody>
                </table>
              </div>;
        })()}
          </div>
        </div>}
    </div>;
}
// ---------------------------------------------------------------------------
function JobRunsView({
  token,
  onClose
}) {
  const [filter, setFilter] = useState({
    module: "",
    kind: "",
    since: ""
  });
  const [runs, setRuns] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.module) params.set("module", filter.module);
      if (filter.kind) params.set("kind", filter.kind);
      if (filter.since) params.set("since", filter.since);
      params.set("limit", "500");
      const [runsRes, summaryRes] = await Promise.all([apiGet(`job-runs?${params.toString()}`, token), apiGet("job-runs/summary", token)]);
      setRuns(runsRes.runs);
      setSummary(summaryRes.rows);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filter, token]);
  useEffect(() => {
    load();
  }, [load]);
  const moduleNames = [...new Set(summary.map(r => r.module))].sort();
  return <div style={S.drill}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12
    }}>
        <div style={S.drillTitle}>Job runs</div>
        <button style={S.btnGhost} onClick={onClose}>← Back</button>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {/* Most-recent-per-(module, kind) summary */}
      <div style={{
      marginBottom: 18
    }}>
        <div style={{
        fontWeight: 600,
        marginBottom: 6
      }}>Most recent runs</div>
        {summary.length === 0 ? <div style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>
            No runs logged yet. They appear after the monthly cron fires
            (04:00 on the 1st) or after a backfill.
          </div> : <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Module</th>
                <th style={S.th}>Kind</th>
                <th style={S.th}>Month</th>
                <th style={S.th}>Run at</th>
                <th style={S.th}>Written</th>
                <th style={S.th}>Errors</th>
                <th style={S.th}>Duration</th>
                <th style={S.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((r, i) => <tr key={i}>
                  <td style={S.td}>{r.module}</td>
                  <td style={S.td}>{r.kind}</td>
                  <td style={S.td}>{r.monthKey}</td>
                  <td style={S.td}>{new Date(r.runAt).toLocaleString("en-GB", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit"
              })}</td>
                  <td style={S.td}>{r.summariesWritten}</td>
                  <td style={S.td}>{r.errors?.length || 0}</td>
                  <td style={S.td}>{(r.durationMs / 1000).toFixed(1)}s</td>
                  <td style={S.td}>
                    {r.success ? <span style={S.ok}>✓ OK</span> : <span style={S.urgent}>✗ failed</span>}
                  </td>
                </tr>)}
            </tbody>
          </table>}
      </div>

      {/* Filtered run history */}
      <div style={{
      marginBottom: 8,
      display: "flex",
      gap: 8,
      alignItems: "center"
    }}>
        <span style={{
        fontWeight: 600
      }}>History</span>
        <select value={filter.module} onChange={e => setFilter({
        ...filter,
        module: e.target.value
      })} style={{
        padding: "4px 6px",
        border: "1px solid #D9D9D9",
        borderRadius: 4
      }}>
          <option value="">All modules</option>
          {(moduleNames.length > 0 ? moduleNames : MODULES.map(m => m.key)).map(name => <option key={name} value={name}>{name}</option>)}
        </select>
        <select value={filter.kind} onChange={e => setFilter({
        ...filter,
        kind: e.target.value
      })} style={{
        padding: "4px 6px",
        border: "1px solid #D9D9D9",
        borderRadius: 4
      }}>
          <option value="">All kinds</option>
          <option value="cron">cron</option>
          <option value="backfill">backfill</option>
          <option value="manual">manual</option>
        </select>
        <input type="date" value={filter.since} onChange={e => setFilter({
        ...filter,
        since: e.target.value
      })} style={{
        padding: "4px 6px",
        border: "1px solid #D9D9D9",
        borderRadius: 4
      }} />
        <button style={S.btnGhost} onClick={() => setFilter({
        module: "",
        kind: "",
        since: ""
      })}>
          Clear
        </button>
      </div>

      {loading && <div>Loading…</div>}

      <table style={S.table}>
        <thead>
          <tr>
            <th style={S.th}>Run at</th>
            <th style={S.th}>Kind</th>
            <th style={S.th}>Module</th>
            <th style={S.th}>Month</th>
            <th style={S.th}>Written</th>
            <th style={S.th}>Errors</th>
            <th style={S.th}>Duration</th>
            <th style={S.th}>Status</th>
          </tr>
        </thead>
        <tbody>
          {runs.length === 0 && !loading && <tr><td style={S.td} colSpan={8}>No runs match the filter.</td></tr>}
          {runs.map(r => <tr key={r._id}>
              <td style={S.td}>{new Date(r.runAt).toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              hour: "2-digit",
              minute: "2-digit"
            })}</td>
              <td style={S.td}>{r.kind}</td>
              <td style={S.td}>{r.module}</td>
              <td style={S.td}>{r.monthKey}</td>
              <td style={S.td}>{r.summariesWritten}</td>
              <td style={S.td}>
                {r.errors?.length > 0 ? <span style={S.urgent} title={r.errors.join("\n")}>
                    {r.errors.length}
                  </span> : 0}
              </td>
              <td style={S.td}>{(r.durationMs / 1000).toFixed(1)}s</td>
              <td style={S.td}>
                {r.success ? <span style={S.ok}>✓</span> : <span style={S.urgent}>✗</span>}
              </td>
            </tr>)}
        </tbody>
      </table>
    </div>;
}

// ---------------------------------------------------------------------------
// Inactive Sites view — pharmacies that barely used anything last month
// ---------------------------------------------------------------------------
function InactiveSitesView({
  token,
  onClose
}) {
  const masked = useMaskNames();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [monthKey, setMonthKey] = useState("");
  const [threshold, setThreshold] = useState(5);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (monthKey) params.set("monthKey", monthKey);
      params.set("threshold", String(threshold));
      const r = await apiGet(`inactive-sites?${params.toString()}`, token);
      setData(r);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token, monthKey, threshold]);
  useEffect(() => {
    load();
  }, [load]);
  return <div style={S.drill}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12
    }}>
        <div style={S.drillTitle}>Inactive sites</div>
        <button style={S.btnGhost} onClick={onClose}>← Back</button>
      </div>

      <div style={{
      fontSize: 13,
      color: "#5D5D5D",
      marginBottom: 12
    }}>
        Sites where every module's activity was below the threshold for the
        month — effectively, nothing meaningfully used anywhere.
      </div>

      {/* Controls */}
      <div style={{
      display: "flex",
      gap: 10,
      alignItems: "center",
      marginBottom: 14,
      flexWrap: "wrap"
    }}>
        <span style={{
        fontSize: 12,
        color: "#434343"
      }}>Month</span>
        <input type="month" value={monthKey} onChange={e => setMonthKey(e.target.value)} style={{
        padding: "4px 6px",
        border: "1px solid #D9D9D9",
        borderRadius: 4
      }} />
        <span style={{
        fontSize: 12,
        color: "#434343"
      }}>Threshold (less than)</span>
        <input type="number" min={1} value={threshold} onChange={e => setThreshold(Number(e.target.value))} style={{
        padding: "4px 6px",
        border: "1px solid #D9D9D9",
        borderRadius: 4,
        width: 70
      }} />
        {data && <span style={{
        fontSize: 12,
        color: "#5D5D5D"
      }}>
            showing {data.monthKey}
          </span>}
      </div>

      {loading && <div>Loading…</div>}
      {error && <div style={S.error}>{error}</div>}

      {data && !loading && <>
          {/* Headline number — click to expand */}
          <div onClick={() => setExpanded(!expanded)} style={{
        cursor: "pointer",
        border: "1px solid",
        borderColor: data.inactiveCount > 0 ? "#FFA39E" : "#a7f3d0",
        background: data.inactiveCount > 0 ? "#FFF1F0" : "#F6FFED",
        borderRadius: 6,
        padding: 16,
        marginBottom: 12,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
            <div>
              <div style={{
            fontSize: 32,
            fontWeight: 700,
            color: data.inactiveCount > 0 ? "#CF1322" : "#059669"
          }}>
                {data.inactiveCount}
              </div>
              <div style={{
            fontSize: 13,
            color: "#434343"
          }}>
                inactive {data.inactiveCount === 1 ? "site" : "sites"} of {data.totalSites} total
                {" · "}{data.activeCount} active
              </div>
            </div>
            <div style={{
          fontSize: 13,
          color: "#5D5D5D"
        }}>
              {expanded ? "Hide names ▲" : "Show names ▼"}
            </div>
          </div>

          {/* Expanded list of names */}
          {expanded && (data.inactive.length === 0 ? <div style={{
        fontSize: 13,
        color: "#059669"
      }}>
                Every site had activity above the threshold. Nothing to show.
              </div> : <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Pharmacy</th>
                    <th style={S.th}>Total activity</th>
                    <th style={S.th}>Breakdown (module: count)</th>
                  </tr>
                </thead>
                <tbody>
                  {data.inactive.map(s => <tr key={String(s.pharmacyId)}>
                      <td style={S.td}><b>{displayName(s.pharmacyName, masked)}</b></td>
                      <td style={S.td}>
                        {s.totalActivity === 0 ? <span style={S.urgent}>0 (silent)</span> : s.totalActivity}
                      </td>
                      <td style={S.td}>
                        <span style={{
                fontSize: 11,
                color: "#5D5D5D"
              }}>
                          {s.moduleCounts.filter(m => m.count > 0).map(m => `${m.module}:${m.count}`).join("  ") || "no activity in any module"}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>)}
        </>}
    </div>;
}

// ---------------------------------------------------------------------------
// Prescription activity cards — "Bag activity" and "Deliveries"
// ---------------------------------------------------------------------------
// Both read the prescriptions snapshot (one fleetStats call covering all
// active pharmacies, each row carrying { today, monthToDate }). The card face
// shows TODAY's fleet-wide totals; clicking opens a per-pharmacy grid that
// defaults to today with a toggle for month-to-date.
//
// variant: "retrievals" → scanned + collected
//          "deliveries"  → deliveredViaApp + deliveredWithoutApp

const PRESCRIPTION_CARDS = {
  retrievals: {
    title: "Bag activity",
    blurb: "Bags stacked and handed out across the group. Card shows TODAY's totals; click for a per-pharmacy grid.",
    metrics: [{
      key: "scanned",
      label: "Scanned",
      hint: "Collection bags scanned onto a shelf (excludes walk-ins)."
    }, {
      key: "walkIn",
      label: "Walk-in",
      hint: "Bags handed straight to a walk-in patient — never placed on a shelf."
    }, {
      key: "collected",
      label: "Collected",
      hint: "Collection bags handed out to patients from the shelf (excludes walk-ins)."
    }]
  },
  deliveries: {
    title: "Deliveries",
    blurb: "Bags sent out for delivery across the group. Card shows TODAY's totals; click for a per-pharmacy grid.",
    metrics: [{
      key: "deliveredViaApp",
      label: "Via app",
      hint: "Delivery bags handed to a driver through the app (driver assigned)."
    }, {
      key: "deliveredWithoutApp",
      label: "Not via app",
      hint: "Delivery bags marked delivered without a driver assigned in the app."
    }]
  }
};
function PrescriptionActivityCard({
  variant,
  snapshotData,
  onSelect
}) {
  const cfg = PRESCRIPTION_CARDS[variant];

  // snapshotData is the array of per-pharmacy snapshot rows (or null while loading)
  const loading = snapshotData == null;

  // Sum TODAY across all pharmacies for the card face
  const totals = {};
  if (!loading) {
    for (const m of cfg.metrics) totals[m.key] = 0;
    for (const row of snapshotData) {
      const t = row.today || {};
      for (const m of cfg.metrics) totals[m.key] += Number(t[m.key]) || 0;
    }
  }
  const grandTotal = cfg.metrics.reduce((s, m) => s + (totals[m.key] || 0), 0);
  return <div style={{
    ...S.card,
    borderColor: "#BAE7FF",
    background: "#f8fbff"
  }} onClick={() => onSelect(variant)}>
      <div style={S.cardTitle}>{cfg.title}</div>
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginBottom: 8,
      lineHeight: 1.4
    }}>
        {cfg.blurb}
      </div>
      <div style={{
      fontSize: 26,
      fontWeight: 700,
      color: "#0958D9",
      marginBottom: 6
    }}>
        {loading ? "…" : grandTotal}
        <span style={{
        fontSize: 12,
        fontWeight: 400,
        color: "#5D5D5D"
      }}> today</span>
      </div>
      {cfg.metrics.map(m => <div key={m.key} style={S.metaRow} title={m.hint}>
          <span>{m.label}</span>
          <span style={{
        fontWeight: 600
      }}>{loading ? "…" : totals[m.key] || 0}</span>
        </div>)}
    </div>;
}

// Detail grid for a prescription-activity card. Defaults to today; toggle MTD.
function PrescriptionActivityDetail({
  variant,
  snapshotData,
  onClose,
  token
}) {
  const masked = useMaskNames();
  const cfg = PRESCRIPTION_CARDS[variant];
  // Deliveries has more tabs: one pharmacy's activity over a date range, and
  // the route analysis / activity / payments pages (once in the left menu).
  const [detailTab, setDetailTab] = useState("totals"); // "totals" | "review" | "route" | "activity" | "payments"
  const [geosortingDirty, setGeosortingDirty] = useState(false);
  const leaveGeosorting = async () => !geosortingDirty || (await appConfirm("Discard unsaved Geosorting changes?"));
  const [period, setPeriod] = useState("today"); // "today" | "mtd"
  const [query, setQuery] = useState(""); // free-text pharmacy filter
  // Sort: key is "pharmacyName" | "total" | a metric key. dir "asc" | "desc".
  const [sort, setSort] = useState({
    key: "total",
    dir: "desc"
  });

  // Build base rows for the selected period.
  const allRows = (snapshotData || []).map(row => {
    const src = period === "today" ? row.today || {} : row.monthToDate || {};
    // A row with no period payload usually means the tenant snapshot errored
    // (e.g. order types not configured) — flag it rather than show false zeros.
    const noData = !(period === "today" ? row.today : row.monthToDate);
    const vals = {};
    let total = 0;
    for (const m of cfg.metrics) {
      const v = Number(src[m.key]) || 0;
      vals[m.key] = v;
      total += v;
    }
    return {
      pharmacyName: row.pharmacyName,
      pharmacyId: row.pharmacyId,
      vals,
      total,
      noData,
      error: row.error || null
    };
  });

  // Free-text filter on pharmacy name (case-insensitive substring).
  const q = query.trim().toLowerCase();
  const filtered = q ? allRows.filter(r => (r.pharmacyName || "").toLowerCase().includes(q)) : allRows;

  // Sort by the chosen column. Pharmacy name sorts alphabetically; numeric
  // columns sort by value. Direction toggles per the header click.
  const rows = [...filtered].sort((a, b) => {
    let av, bv;
    if (sort.key === "pharmacyName") {
      av = (a.pharmacyName || "").toLowerCase();
      bv = (b.pharmacyName || "").toLowerCase();
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === "asc" ? cmp : -cmp;
    }
    av = sort.key === "total" ? a.total : a.vals[sort.key];
    bv = sort.key === "total" ? b.total : b.vals[sort.key];
    return sort.dir === "asc" ? av - bv : bv - av;
  });

  // Clicking a header sorts by it; clicking the active column flips direction.
  // Name defaults to A→Z; numeric columns default to high→low.
  const toggleSort = key => {
    setSort(prev => {
      if (prev.key === key) return {
        key,
        dir: prev.dir === "asc" ? "desc" : "asc"
      };
      return {
        key,
        dir: key === "pharmacyName" ? "asc" : "desc"
      };
    });
  };
  const sortArrow = key => sort.key === key ? sort.dir === "asc" ? " ▲" : " ▼" : "";

  // Adoption stats + fleet totals are computed over the FILTERED set so the
  // figures stay consistent with what's actually shown.
  const fleetTotals = {};
  for (const m of cfg.metrics) fleetTotals[m.key] = rows.reduce((s, r) => s + r.vals[m.key], 0);
  const fleetGrand = cfg.metrics.reduce((s, m) => s + fleetTotals[m.key], 0);
  const siteCount = rows.length;
  const usingCount = {}; // per-metric count of sites with value > 0
  for (const m of cfg.metrics) {
    usingCount[m.key] = rows.filter(r => r.vals[m.key] > 0).length;
  }
  const anyCount = rows.filter(r => r.total > 0).length; // sites with any activity
  const pct = n => siteCount > 0 ? Math.round(n / siteCount * 100) : 0;
  return <div style={S.drill}>
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12
    }}>
        <div style={S.drillTitle}>{cfg.title}</div>
        <button style={S.btnGhost} onClick={async () => {
        if ((await leaveGeosorting())) onClose();
      }}>← Back</button>
      </div>

      {variant === "deliveries" && <div style={{
      display: "flex",
      gap: 6,
      marginBottom: 12,
      borderBottom: "1px solid #F0F0F0",
      paddingBottom: 8
    }}>
          {[["totals", "Pharmacy totals"], ["review", "Activity Review"], ["route", "Route analysis"], ["payments", "Payments"], ["geosorting", "Geosorting"]].map(([id, label]) => <button key={id} onClick={async () => {
        if (id !== detailTab && (await leaveGeosorting())) setDetailTab(id);
      }} style={detailTab === id ? {
        ...S.btnGhost,
        background: "#262626",
        color: "#fff",
        border: "none",
        fontWeight: 600
      } : S.btnGhost}>
              {label}
            </button>)}
        </div>}

      {variant === "deliveries" && detailTab === "geosorting" ? <Geosorting token={token} onDirtyChange={setGeosortingDirty} /> : variant === "deliveries" && detailTab === "review" ? <DeliveryActivityReview masked={masked} token={token} /> : variant === "deliveries" && detailTab === "route" ? <RouteAnalysis /> : variant === "deliveries" && detailTab === "payments" ? <Payments token={token} /> : <>
      <div style={{
        fontSize: 13,
        color: "#5D5D5D",
        marginBottom: 12
      }}>
        {cfg.blurb}
      </div>

      {/* Period toggle */}
      <div style={{
        display: "flex",
        gap: 8,
        marginBottom: 14
      }}>
        <button onClick={() => setPeriod("today")} style={period === "today" ? {
          ...S.btnGhost,
          background: "#0E8CFF",
          color: "#fff",
          border: "none"
        } : S.btnGhost}>
          Today
        </button>
        <button onClick={() => setPeriod("mtd")} style={period === "mtd" ? {
          ...S.btnGhost,
          background: "#0E8CFF",
          color: "#fff",
          border: "none"
        } : S.btnGhost}>
          Month to date
        </button>
        <span style={{
          fontSize: 12,
          color: "#5D5D5D",
          alignSelf: "center"
        }}>
          {period === "today" ? "Activity since midnight (UK time)." : "Activity since the 1st of this month."}
        </span>
      </div>

      {/* Free-text pharmacy search */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginBottom: 12
      }}>
        <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
          padding: "6px 10px",
          fontSize: 13,
          border: "1px solid #D9D9D9",
          borderRadius: 6,
          width: 260,
          outline: "none"
        }} />
        {query && <button style={S.btnGhost} onClick={() => setQuery("")}>Clear</button>}
        <span style={{
          fontSize: 12,
          color: "#5D5D5D"
        }}>
          {query ? `${siteCount} match${siteCount === 1 ? "" : "es"}` : `${siteCount} site${siteCount === 1 ? "" : "s"}`}
        </span>
      </div>

      {snapshotData == null ? <div>Loading…</div> : <table style={S.table}>
          <thead>
            <tr>
              <th style={{
              ...S.th,
              cursor: "pointer",
              userSelect: "none"
            }} onClick={() => toggleSort("pharmacyName")} title="Sort by pharmacy name">
                Pharmacy{sortArrow("pharmacyName")}
              </th>
              {cfg.metrics.map(m => <th key={m.key} style={{
              ...S.th,
              cursor: "pointer",
              userSelect: "none"
            }} onClick={() => toggleSort(m.key)} title={`${m.hint} — click to sort`}>
                  {m.label}{sortArrow(m.key)}
                </th>)}
              <th style={{
              ...S.th,
              cursor: "pointer",
              userSelect: "none"
            }} onClick={() => toggleSort("total")} title="Sort by total">
                Total{sortArrow("total")}
              </th>
            </tr>
            {/* Adoption sub-header: sites with non-zero values in each column */}
            <tr>
              <th style={{
              ...S.th,
              background: "#f0f6ff",
              fontWeight: 600,
              color: "#1e3a5f"
            }}>
                Sites using ({siteCount} total)
              </th>
              {cfg.metrics.map(m => <th key={m.key} style={{
              ...S.th,
              background: "#f0f6ff",
              color: "#1e3a5f",
              fontWeight: 600
            }} title={`Sites with at least one ${m.label.toLowerCase()} in this period`}>
                  {usingCount[m.key]} ({pct(usingCount[m.key])}%)
                </th>)}
              <th style={{
              ...S.th,
              background: "#f0f6ff",
              color: "#1e3a5f",
              fontWeight: 600
            }} title="Sites with any activity in this period">
                {anyCount} ({pct(anyCount)}%)
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Fleet total row — reflects the filtered set when searching */}
            <tr style={{
            background: "#f0f6ff",
            fontWeight: 700
          }}>
              <td style={S.td}>{q ? "Matching pharmacies (totals)" : "All pharmacies (totals)"}</td>
              {cfg.metrics.map(m => <td key={m.key} style={S.td}>{fleetTotals[m.key]}</td>)}
              <td style={S.td}>{fleetGrand}</td>
            </tr>
            {rows.length === 0 ? <tr>
                <td style={{
              ...S.td,
              color: "#5D5D5D"
            }} colSpan={cfg.metrics.length + 2}>
                  No pharmacies match “{query}”.
                </td>
              </tr> : rows.map(r => <tr key={String(r.pharmacyId)}>
                <td style={S.td}>
                  {displayName(r.pharmacyName, masked)}
                  {r.noData && <span style={{
                marginLeft: 6,
                fontSize: 11,
                color: "#b45309",
                background: "#FFFBE6",
                border: "1px solid #FFD666",
                borderRadius: 4,
                padding: "1px 5px"
              }} title={r.error ? `Snapshot error: ${r.error}` : "No activity data returned for this site — order types may not be configured."}>
                      not reporting
                    </span>}
                </td>
                {cfg.metrics.map(m => <td key={m.key} style={S.td}>{r.noData ? "—" : r.vals[m.key]}</td>)}
                <td style={S.td}>{r.noData ? "—" : <b>{r.total}</b>}</td>
              </tr>)}
          </tbody>
        </table>}
      </>}
    </div>;
}

// ---------------------------------------------------------------------------
// Periodic tasks — group dictionary + pharmacies×tasks grid + history/graph.
// Cells are colour-coded by due-state (green ok / amber due-soon / red overdue
// / grey never / "N/A" opted out). Click a cell to record a value or view its
// history (with a sparkline for numeric tasks).
// ---------------------------------------------------------------------------
const PT_FREQS = ["weekly", "monthly", "quarterly", "yearly"];
const PT_TYPES = [{
  key: "numeric",
  label: "Number"
}, {
  key: "text",
  label: "Text"
}, {
  key: "boolean",
  label: "Yes/No"
}, {
  key: "date",
  label: "Date"
}, {
  key: "choice",
  label: "Multiple choice"
}];

// How a task is answered — decides how it is requested from the branch:
// a number or a file by EMAIL (a one-task link), a photo or a simple
// acknowledgement by a task in the STAFF APP.
const PT_RESPONSES = [{
  key: "numeric",
  label: "Number",
  via: "email",
  hint: "e.g. items dispensed"
}, {
  key: "file",
  label: "File",
  via: "email",
  hint: "e.g. a signed document"
}, {
  key: "image",
  label: "Photo",
  via: "app",
  hint: "taken in the staff app"
}, {
  key: "ack",
  label: "Acknowledgement",
  via: "app",
  hint: "ticked off in the staff app"
}];
const PT_REQUESTABLE = ["monthly", "quarterly", "yearly"];
const ptResponseOf = d => d?.responseType || (d?.dataType === "numeric" ? "numeric" : null);
const ptResponseLabel = d => {
  const r = PT_RESPONSES.find(x => x.key === ptResponseOf(d));
  return r ? `${r.label} (${r.via === "email" ? "email" : "staff app"})` : null;
};
function ptFmtValue(dataType, value) {
  if (value == null || value === "") return "—";
  switch (dataType) {
    case "boolean":
      return value === true || value === "true" ? "Yes" : "No";
    case "date":
      return fmtDate(value);
    case "numeric":
      return String(value);
    default:
      return String(value);
  }
}

// Tiny dependency-free sparkline for numeric history (oldest→newest).
function Sparkline({
  points,
  width = 480,
  height = 140
}) {
  if (!points.length) return null;
  const xs = points.map((_, i) => i);
  const ys = points.map(p => p.y);
  const minY = Math.min(...ys),
    maxY = Math.max(...ys);
  const pad = 24;
  const span = maxY - minY || 1;
  const px = i => pad + (xs.length === 1 ? 0 : i / (xs.length - 1) * (width - 2 * pad));
  const py = y => height - pad - (y - minY) / span * (height - 2 * pad);
  const path = points.map((p, i) => `${i === 0 ? "M" : "L"} ${px(i).toFixed(1)} ${py(p.y).toFixed(1)}`).join(" ");
  return <svg width={width} height={height} style={{
    maxWidth: "100%",
    border: "1px solid #F0F0F0",
    borderRadius: 6,
    background: "#fff"
  }}>
      <line x1={pad} y1={height - pad} x2={width - pad} y2={height - pad} stroke="#F0F0F0" />
      <line x1={pad} y1={pad} x2={pad} y2={height - pad} stroke="#F0F0F0" />
      <text x={pad - 4} y={py(maxY) + 4} textAnchor="end" fontSize="10" fill="#5D5D5D">{maxY}</text>
      <text x={pad - 4} y={py(minY) + 4} textAnchor="end" fontSize="10" fill="#5D5D5D">{minY}</text>
      <path d={path} fill="none" stroke="#0E8CFF" strokeWidth="2" />
      {points.map((p, i) => <circle key={i} cx={px(i)} cy={py(p.y)} r="3" fill="#0E8CFF" />)}
    </svg>;
}
const PT_STATE = {
  ok: {
    bg: "#F6FFED",
    label: "In date"
  },
  due: {
    bg: "#FFF7E6",
    label: "Due soon"
  },
  overdue: {
    bg: "#fde2e1",
    label: "Overdue"
  },
  never: {
    bg: "#F5F5F5",
    label: "Never recorded"
  },
  na: {
    bg: "#fafafa",
    label: "Not relevant"
  }
};

// One editable row in the periodic-tasks manage list.
function ManageTaskRow({
  def,
  inactive,
  onSave,
  onToggle,
  onDelete,
  onRequests
}) {
  const [editing, setEditing] = useState(false);
  const [resp, setResp] = useState(ptResponseOf(def) || "");
  const [desc, setDesc] = useState(def.description || "");
  const [freq, setFreq] = useState(def.frequency || "monthly");
  const [opts, setOpts] = useState((def.options || []).join(", "));

  // System-managed tasks are the ones linked to a Regulatory Compliance element
  // (they carry complianceElementId). These must not be edited, deactivated, or
  // deleted from here — doing so would break the compliance dashboard wiring.
  // User-added tasks have no complianceElementId and remain fully editable.
  const isSystem = !!def.complianceElementId;
  // The item count is a CORE task: Workforce, Regulatory Compliance (EPS
  // operational), Business Insights and the monthly report all find it by its
  // biKey, so its name, frequency and active state are fixed (the backend
  // refuses those changes too). Automatic requests can still be set up.
  const isCore = def.biKey === "item_count";
  const save = () => {
    const patch = {
      description: desc,
      frequency: freq
    };
    if (resp && resp !== ptResponseOf(def)) patch.responseType = resp;
    if (def.dataType === "choice") patch.options = opts.split(",").map(s => s.trim()).filter(Boolean);
    onSave(patch);
    setEditing(false);
  };
  if (editing && !isSystem && !isCore) {
    return <tr style={{
      background: "#eef5ff"
    }}>
        <td style={S.td}>
          <input type="text" value={desc} onChange={e => setDesc(e.target.value)} style={{
          padding: "4px 6px",
          border: "1px solid #D9D9D9",
          borderRadius: 4,
          width: 240
        }} />
          {def.dataType === "choice" && <input type="text" value={opts} onChange={e => setOpts(e.target.value)} placeholder="options, comma-separated" style={{
          marginTop: 4,
          padding: "4px 6px",
          border: "1px solid #D9D9D9",
          borderRadius: 4,
          width: 240
        }} />}
        </td>
        <td style={S.td}>{PT_TYPES.find(x => x.key === def.dataType)?.label || def.dataType}</td>
        <td style={S.td}>
          {/* A number stays a number: the stored values would not fit any other answer. */}
          <select value={resp} onChange={e => setResp(e.target.value)} aria-label="How is it answered?" style={{
          padding: "4px 6px",
          border: "1px solid #D9D9D9",
          borderRadius: 4
        }}>
            {!resp && <option value="">— choose —</option>}
            {PT_RESPONSES.filter(r => r.key === "numeric" === (def.dataType === "numeric")).map(r => <option key={r.key} value={r.key}>{r.label} ({r.via === "email" ? "email" : "staff app"})</option>)}
          </select>
        </td>
        <td style={S.td}>
          <select value={freq} onChange={e => setFreq(e.target.value)} style={{
          padding: "4px 6px",
          border: "1px solid #D9D9D9",
          borderRadius: 4
        }}>
            {PT_FREQS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
        </td>
        <td style={{
        ...S.td,
        textAlign: "center"
      }}>{inactive ? "Inactive" : "Active"}</td>
        <td style={{
        ...S.td,
        textAlign: "center"
      }}>
          <button style={S.btn} onClick={save} disabled={!desc.trim()}>Save</button>
          <button style={{
          ...S.btnGhost,
          marginLeft: 6
        }} onClick={() => setEditing(false)}>Cancel</button>
        </td>
      </tr>;
  }
  return <tr style={{
    background: inactive ? "#F5F5F5" : undefined,
    color: inactive ? "#8C8C8C" : undefined
  }}>
      <td style={{
      ...S.td,
      fontWeight: 600
    }}>
        {def.description}
        {isSystem && <span title="Required for Regulatory Compliance — managed by the system and cannot be edited or deleted here." style={{
        marginLeft: 8,
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        padding: "1px 6px",
        borderRadius: 4,
        background: "#FFF1F0",
        color: "#A8071A",
        border: "1px solid #FFCCC7",
        verticalAlign: "middle"
      }}>
            🔒 Compliance
          </span>}
        {isCore && <span title="Core task — used by Workforce, Regulatory Compliance (EPS operational), Business Insights and the monthly report. Its name and frequency cannot be changed and it cannot be deleted or made inactive." style={{
        marginLeft: 8,
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.4,
        padding: "1px 6px",
        borderRadius: 4,
        background: "#E6F7FF",
        color: "#0958D9",
        border: "1px solid #91D5FF",
        verticalAlign: "middle"
      }}>
            🔒 Core
          </span>}
      </td>
      <td style={S.td}>{PT_TYPES.find(x => x.key === def.dataType)?.label || def.dataType}</td>
      <td style={S.td}>{ptResponseLabel(def) || <span style={{
        color: "#8C8C8C"
      }}>—</span>}</td>
      <td style={S.td}>{def.frequency}</td>
      <td style={{
      ...S.td,
      textAlign: "center"
    }}>
        {inactive ? <span style={{
        color: "#8C8C8C"
      }}>Inactive</span> : <span style={{
        color: "#059669",
        fontWeight: 600
      }}>Active</span>}
      </td>
      <td style={{
      ...S.td,
      textAlign: "center",
      whiteSpace: "nowrap"
    }}>
        {isSystem ? <span style={{
        fontSize: 12,
        color: "#8C8C8C",
        fontStyle: "italic"
      }}>
            Locked
          </span> : isCore ? <>
            <button style={S.btnGhost} onClick={onRequests} title="Name a member of staff at each branch and the day of the month to send them this task automatically">
              Requests
            </button>
            <span style={{
          marginLeft: 8,
          fontSize: 12,
          color: "#8C8C8C",
          fontStyle: "italic"
        }}>Core — locked</span>
          </> : <>
            <button style={S.btnGhost} onClick={() => setEditing(true)}>Edit</button>
            {!inactive && PT_REQUESTABLE.includes(def.frequency) && <button style={{
          ...S.btnGhost,
          marginLeft: 6
        }} onClick={onRequests} title="Name a member of staff at each branch and the day of the month to send them this task automatically">
                Requests
              </button>}
            <button style={{
          ...S.btnGhost,
          marginLeft: 6
        }} onClick={onToggle}>
              {inactive ? "Activate" : "Make inactive"}
            </button>
            <button style={{
          ...S.btnGhost,
          marginLeft: 6,
          color: "#CF1322"
        }} onClick={onDelete}>
              Delete
            </button>
          </>}
      </td>
    </tr>;
}

// Automatic requests for one task: per branch, the named member of staff and
// the day of the month to send it. Requests go out by themselves each period
// (hourly check); "Send now" sends the current period's request straight away.
const PT_DAYS = [...Array.from({
  length: 31
}, (_, i) => String(i + 1)), "last"];
function ptRequestStatus(site) {
  const r = site.request;
  if (site.recorded) return {
    text: "Received",
    color: "#059669"
  };
  if (!r) return {
    text: site.staffId ? "Not sent yet this period" : "—",
    color: "#8C8C8C"
  };
  if (r.status === "no_address") return {
    text: "Not sent — no email address on the staff record",
    color: "#CF1322"
  };
  if (r.status === "failed") return {
    text: `Not sent — ${r.error || "email failed"}`,
    color: "#CF1322"
  };
  if (r.status === "withdrawn") return {
    text: "App task deleted — not answered",
    color: "#CF1322"
  };
  const when = r.sentAt ? fmtDate(r.sentAt) : "";
  if (r.channel === "app") {
    return r.returnedCount ? {
      text: `Returned for a photo (${r.returnedCount}×) — waiting`,
      color: "#D46B08"
    } : {
      text: `App task sent ${when} — waiting`,
      color: "#0958D9"
    };
  }
  return r.reminderAt ? {
    text: `Emailed ${when}, reminded ${fmtDate(r.reminderAt)} — waiting`,
    color: "#D46B08"
  } : {
    text: `Emailed ${when} — waiting`,
    color: "#0958D9"
  };
}
function PeriodicRequestsDialog({
  token,
  def,
  onClose
}) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({}); // dbName -> { staffId, sendDay }
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const load = useCallback(async () => {
    try {
      const d = await apiSend("periodic-requests/sites", token, "POST", {
        taskId: String(def._id)
      });
      setData(d);
      setRows(Object.fromEntries((d.sites || []).map(s => [s.dbName, {
        staffId: s.staffId || "",
        sendDay: s.sendDay != null ? String(s.sendDay) : "1"
      }])));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [token, def]);
  useEffect(() => {
    load();
  }, [load]);
  const savedOf = s => ({
    staffId: s.staffId || "",
    sendDay: s.sendDay != null ? String(s.sendDay) : "1"
  });
  const changed = (data?.sites || []).filter(s => {
    const a = rows[s.dbName] || {},
      b = savedOf(s);
    return a.staffId !== b.staffId || a.staffId && a.sendDay !== b.sendDay;
  });
  const dirty = changed.length > 0;
  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      await apiSend("periodic-requests/sites/save", token, "POST", {
        taskId: String(def._id),
        sites: changed.map(s => ({
          dbName: s.dbName,
          staffId: rows[s.dbName].staffId || null,
          sendDay: rows[s.dbName].sendDay === "last" ? "last" : Number(rows[s.dbName].sendDay)
        }))
      });
      await load();
      setNote("Saved.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const sendNow = async s => {
    setBusy(true);
    setNote(null);
    try {
      const r = await apiSend("periodic-requests/send-now", token, "POST", {
        taskId: String(def._id),
        dbName: s.dbName
      });
      await load();
      setNote(r?.status === "sent" ? `Sent to ${s.staffName || "the named staff member"} at ${s.pharmacyName}.` : r?.status === "no_address" ? "Not sent — that staff member has no email address." : r?.skipped ? `Not sent — ${r.skipped}.` : "Done.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const close = async () => {
    if (dirty && !(await appConfirm("You have unsaved changes. Close without saving?"))) return;
    onClose();
  };
  const viaEmail = data?.channel === "email";
  const sel = {
    padding: "4px 6px",
    border: "1px solid #D9D9D9",
    borderRadius: 4,
    fontSize: 13
  };
  return <div style={{
    position: "fixed",
    inset: 0,
    zIndex: 1100,
    background: "rgba(0,0,0,0.35)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16
  }} onClick={close}>
      <div role="dialog" aria-label={`Requests for ${def.description}`} onClick={e => e.stopPropagation()} style={{
      background: "#fff",
      borderRadius: 8,
      padding: 18,
      width: "min(980px, 100%)",
      maxHeight: "90vh",
      overflow: "auto",
      boxShadow: "0 8px 30px rgba(0,0,0,0.2)"
    }}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 12
      }}>
          <div style={{
          fontSize: 17,
          fontWeight: 700
        }}>Requests — {def.description}</div>
          <button style={S.btnGhost} onClick={close}>Close</button>
        </div>
        {data && <div style={{
        fontSize: 13,
        color: "#5D5D5D",
        margin: "6px 0 12px"
      }}>
            {data.frequency} · this period: <b>{data.period?.label}</b> ·{" "}
            {viaEmail ? <>sent by <b>email</b> with a link to a one-task form ({data.responseType === "file" ? "they upload the file" : "they enter the number"}); one reminder 3 days later if still missing.</> : <>sent as a task in the <b>staff app</b> ({data.responseType === "image" ? "they attach a photo — a task ticked off without one is sent back" : "they tick it off to acknowledge"}).</>}
            {" "}Nothing is sent to a branch that has already recorded the period or has the task switched off (N/A).
          </div>}
        {error && <div style={S.error}>{error}</div>}
        {!data ? <div style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>Loading…</div> : !data.channel ? <div style={{
        fontSize: 13,
        color: "#CF1322"
      }}>
            Choose how this task is answered first (Manage tasks → Edit → Answered by).
          </div> : <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Branch</th>
                <th style={S.th}>Named staff member</th>
                <th style={S.th}>Day of the month</th>
                <th style={S.th}>This period</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {data.sites.map(s => {
            const r = rows[s.dbName] || {
              staffId: "",
              sendDay: "1"
            };
            const pick = (s.staff || []).find(x => x.staffId === r.staffId);
            const st = ptRequestStatus(s);
            const savedName = s.staffId && !(s.staff || []).some(x => x.staffId === s.staffId);
            return <tr key={s.dbName}>
                    <td style={{
                ...S.td,
                fontWeight: 600
              }}>{s.pharmacyName}</td>
                    <td style={S.td}>
                      <select value={r.staffId} style={sel} aria-label={`Staff member at ${s.pharmacyName}`} onChange={e => setRows(p => ({
                  ...p,
                  [s.dbName]: {
                    ...r,
                    staffId: e.target.value
                  }
                }))}>
                        <option value="">— no automatic request —</option>
                        {savedName && <option value={s.staffId}>{s.staffName} (no longer listed)</option>}
                        {(s.staff || []).map(x => <option key={x.staffId} value={x.staffId}>
                            {x.name}{viaEmail && !x.hasEmail ? " — no email address" : ""}
                          </option>)}
                      </select>
                      {viaEmail && pick && !pick.hasEmail && <div style={{
                  fontSize: 11,
                  color: "#CF1322",
                  marginTop: 3
                }}>
                          No email address on their staff record — the request cannot be sent.
                        </div>}
                    </td>
                    <td style={S.td}>
                      <select value={r.sendDay} style={sel} disabled={!r.staffId} aria-label={`Day of the month for ${s.pharmacyName}`} onChange={e => setRows(p => ({
                  ...p,
                  [s.dbName]: {
                    ...r,
                    sendDay: e.target.value
                  }
                }))}>
                        {PT_DAYS.map(d => <option key={d} value={d}>{d === "last" ? "Last day" : d}</option>)}
                      </select>
                    </td>
                    <td style={{
                ...S.td,
                fontSize: 12,
                color: st.color
              }}>{st.text}</td>
                    <td style={{
                ...S.td,
                whiteSpace: "nowrap"
              }}>
                      {s.staffId && !s.recorded && <button style={S.btnGhost} disabled={busy || dirty} title={dirty ? "Save your changes first" : "Send this period's request now"} onClick={() => sendNow(s)}>
                          {s.request?.sentAt ? "Send again" : "Send now"}
                        </button>}
                    </td>
                  </tr>;
          })}
              {!data.sites.length && <tr><td style={S.td} colSpan={5}>No branches in scope.</td></tr>}
            </tbody>
          </table>}
        {data?.channel && <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginTop: 12
      }}>
            <button style={S.btn} disabled={!dirty || busy} onClick={save}>Save changes</button>
            {dirty && <button style={S.btnGhost} disabled={busy} onClick={load}>Cancel changes</button>}
            <span style={{
          fontSize: 12,
          color: "#5D5D5D"
        }}>
              {note || "Days 29–31 send on the last day in shorter months."}
            </span>
          </div>}
      </div>
    </div>;
}
function PeriodicTasksView({
  token,
  onClose,
  initialOverdue = false
}) {
  const masked = useMaskNames();
  const [grid, setGrid] = useState(null); // { tasks, rows, summary }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(initialOverdue);
  const [eventSearch, setEventSearch] = useState(""); // "Search for event" box
  const [allDefs, setAllDefs] = useState(null); // incl. inactive, for manage
  const [requestsFor, setRequestsFor] = useState(null); // def whose automatic requests are being set up

  // New-task form
  const [form, setForm] = useState({
    description: "",
    responseType: "numeric",
    frequency: "monthly"
  });

  // Cell entry / history popups. Entry now opens the SHARED
  // PeriodicTaskEntryDialog (value + month/date + notes) — the same popup
  // Regulatory Compliance and the Single-pharmacy page use.
  const [entryCell, setEntryCell] = useState(null); // { row, task }
  const [historyCell, setHistoryCell] = useState(null); // { row, task, def, entries }
  // Estate-wide monthly trend popup (numeric column header click).
  const [estateTrend, setEstateTrend] = useState(null); // { task, points }

  const openEstateTrend = async task => {
    if (task.dataType !== "numeric") return;
    try {
      const t = await apiGet(`periodic-tasks/estate-trend?taskId=${encodeURIComponent(String(task.taskId))}`, token);
      setEstateTrend({
        task,
        points: t.points || []
      });
    } catch (e) {
      setError(e.message);
    }
  };
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const g = await apiGet("periodic-tasks/grid", token);
      setGrid(g);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Manage list includes inactive tasks.
  const loadManage = useCallback(async () => {
    try {
      const d = await apiGet("periodic-tasks/defs?includeInactive=true", token);
      setAllDefs(Array.isArray(d) ? d : []);
    } catch (e) {
      setError(e.message);
    }
  }, [token]);
  useEffect(() => {
    reload();
  }, [reload]);
  useEffect(() => {
    if (showManage) loadManage();
  }, [showManage, loadManage]);
  const setTaskActive = async (taskId, isActive) => {
    try {
      await apiSend(`periodic-tasks/defs/${taskId}`, token, "PUT", {
        isActive
      });
      loadManage();
      reload();
    } catch (e) {
      setError(e.message);
    }
  };
  const saveTaskEdit = async (taskId, patch) => {
    try {
      await apiSend(`periodic-tasks/defs/${taskId}`, token, "PUT", patch);
      loadManage();
      reload();
    } catch (e) {
      setError(e.message);
    }
  };
  const addTask = async () => {
    try {
      const body = {
        description: form.description,
        responseType: form.responseType,
        frequency: form.frequency
      };
      await apiSend("periodic-tasks/defs", token, "POST", body);
      setForm({
        description: "",
        responseType: "numeric",
        frequency: "monthly"
      });
      setShowAdd(false);
      reload();
    } catch (e) {
      setError(e.message);
    }
  };
  const deleteTask = async taskId => {
    if (!(await appConfirm("Delete this task for the whole group? Existing values are kept but hidden."))) return;
    try {
      await apiSend(`periodic-tasks/defs/${taskId}`, token, "DELETE");
      reload();
    } catch (e) {
      setError(e.message);
    }
  };
  const toggleOptout = async (row, task, optedOut) => {
    try {
      await apiSend("periodic-tasks/optout", token, "POST", {
        taskId: String(task.taskId),
        dbName: row.dbName,
        pharmacyId: String(row.pharmacyId),
        optedOut
      });
      reload();
    } catch (e) {
      setError(e.message);
    }
  };
  const openEntry = (row, task) => setEntryCell({
    row,
    task
  });
  const openHistory = async (row, task) => {
    try {
      const h = await apiGet(`periodic-tasks/history?taskId=${encodeURIComponent(String(task.taskId))}&dbName=${encodeURIComponent(row.dbName)}`, token);
      setHistoryCell({
        row,
        task,
        def: h.def,
        entries: h.entries || []
      });
    } catch (e) {
      setError(e.message);
    }
  };
  const allTasks = grid?.tasks || [];
  const q = query.trim().toLowerCase();
  const rows = (grid?.rows || []).filter(r => (!q || (r.pharmacyName || "").toLowerCase().includes(q)) && (!overdueOnly || r.isOverdue));

  // "Search for event": match the typed text against ANY text of an event —
  // the task's description/frequency/type, or any pharmacy's cell for it
  // (value, formatted date, note, entered-by). Non-matching task columns hide.
  const eq = eventSearch.trim().toLowerCase();
  const tasks = !eq ? allTasks : allTasks.filter(t => {
    const head = `${t.description || ""} ${t.frequency || ""} ${t.dataType || ""}`.toLowerCase();
    if (head.includes(eq)) return true;
    return (grid?.rows || []).some(r => {
      const c = r.cells?.[String(t.taskId)];
      if (!c) return false;
      const parts = [c.value != null ? ptFmtValue(t.dataType, c.value) : "", c.value != null ? String(c.value) : "", c.recordedAt ? fmtDate(c.recordedAt) : "", c.note || "", c.recordedBy || "", PT_STATE[c.state]?.label || ""].join(" ").toLowerCase();
      return parts.includes(eq);
    });
  });
  const summary = grid?.summary || {};
  return <div style={S.drill}>
      {loading && <LoadingOverlay label="Loading periodic tasks…" />}
      <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12
    }}>
        <div style={S.drillTitle}>Periodic tasks</div>
        <div>
          <button onClick={async () => {
          if (!window.__ptCascadeBusy) {
            window.__ptCascadeBusy = true;
            try {
              const d = await apiSend("periodic-tasks/cascade", token, "POST", {});
              if (d.sent) {
                (await appAlert(`Cascade email sent to ${d.to} with ${d.links.length} capture link${d.links.length === 1 ? "" : "s"}:\n\n` + d.links.map(l => `• ${l.pharmacyName} — ${l.outstanding} outstanding`).join("\n")));
              } else {
                (await appAlert(d.message || "Nothing outstanding to cascade, or the email could not be sent."));
              }
            } catch (e) {
              (await appAlert("Cascade failed: " + (e.message || e)));
            } finally {
              window.__ptCascadeBusy = false;
            }
          }
        }} title="Email a mobile capture-form link for every pharmacy with overdue or never-recorded tasks. Staff record results (with photos) from their device; completed tasks disappear from everyone's list." style={{
          padding: "6px 12px",
          fontSize: 12.5,
          borderRadius: 6,
          cursor: "pointer",
          border: "1px solid #91D5FF",
          background: "#E6F7FF",
          color: "#0958D9",
          fontWeight: 700,
          lineHeight: 1.2
        }}>
            Cascade overdue
          </button>
          <button style={overdueOnly ? S.btn : S.btnGhost} onClick={() => setOverdueOnly(v => !v)} title="Filter to pharmacies with at least one overdue task">
            {overdueOnly ? "Show all" : "Show overdue"}
          </button>
          <button style={{
          ...S.btnGhost,
          marginLeft: 8
        }} onClick={() => setShowManage(v => !v)}>
            {showManage ? "Hide manage" : "Manage tasks"}
          </button>
          <button style={{
          ...S.btn,
          marginLeft: 8
        }} onClick={() => setShowAdd(v => !v)}>
            {showAdd ? "Cancel" : "+ Add task"}
          </button>
          <button style={{
          ...S.btnGhost,
          marginLeft: 8
        }} onClick={onClose}>← Back</button>
        </div>
      </div>

      {error && <div style={S.error}>{error}</div>}

      {/* Manage list — edit description/frequency, activate/deactivate, delete */}
      {showManage && <div style={{
      border: "1px solid #F0F0F0",
      borderRadius: 6,
      padding: 12,
      marginBottom: 14,
      background: "#FAFAFA"
    }}>
          <div style={{
        fontWeight: 600,
        marginBottom: 8
      }}>
            All tasks{" "}
            <span style={{
          fontWeight: 400,
          color: "#5D5D5D",
          fontSize: 12
        }}>
              (including inactive)
            </span>
          </div>
          {!allDefs ? <div style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>Loading…</div> : allDefs.length === 0 ? <div style={{
        fontSize: 13,
        color: "#5D5D5D"
      }}>No tasks defined.</div> : <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Description</th>
                  <th style={S.th}>Type</th>
                  <th style={S.th}>Answered by</th>
                  <th style={S.th}>Frequency</th>
                  <th style={{
              ...S.th,
              textAlign: "center"
            }}>Status</th>
                  <th style={{
              ...S.th,
              textAlign: "center"
            }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {allDefs.map(d => {
            const inactive = d.isActive === false;
            return <ManageTaskRow key={String(d._id)} def={d} inactive={inactive} onSave={patch => saveTaskEdit(String(d._id), patch)} onToggle={() => setTaskActive(String(d._id), inactive)} onDelete={() => deleteTask(String(d._id))} onRequests={() => setRequestsFor(d)} />;
          })}
              </tbody>
            </table>}
          <div style={{
        fontSize: 12,
        color: "#5D5D5D",
        marginTop: 8
      }}>
            <b>Requests</b> (monthly, quarterly and yearly tasks): name a member of staff at each branch and a day of the month —
            the task is then sent to them automatically each period. A number or a file is requested by email with a link;
            a photo or an acknowledgement arrives as a task in their staff app.
          </div>
        </div>}

      {requestsFor && <PeriodicRequestsDialog token={token} def={requestsFor} onClose={() => setRequestsFor(null)} />}

      {/* Add-task form */}
      {showAdd && <div style={{
      border: "1px solid #F0F0F0",
      borderRadius: 6,
      padding: 12,
      marginBottom: 14,
      background: "#FAFAFA"
    }}>
          <div style={{
        display: "flex",
        gap: 10,
        flexWrap: "wrap",
        alignItems: "flex-end"
      }}>
            <label style={{
          fontSize: 12,
          color: "#434343"
        }}>
              Description<br />
              <input type="text" value={form.description} onChange={e => setForm({
            ...form,
            description: e.target.value
          })} placeholder="e.g. Calibrate fridge thermometer" style={{
            padding: "6px 8px",
            border: "1px solid #D9D9D9",
            borderRadius: 4,
            width: 280
          }} />
            </label>
            <div role="radiogroup" aria-label="How is it answered?" style={{
          fontSize: 12,
          color: "#434343"
        }}>
              How is it answered?<br />
              <div style={{
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            marginTop: 4
          }}>
                {PT_RESPONSES.map(r => <label key={r.key} title={r.hint} style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              cursor: "pointer",
              fontSize: 13
            }}>
                    <input type="radio" name="pt-response" value={r.key} checked={form.responseType === r.key} onChange={() => setForm({
                ...form,
                responseType: r.key
              })} />
                    {r.label} <span style={{
                color: "#8C8C8C",
                fontSize: 11
              }}>({r.via === "email" ? "email" : "staff app"})</span>
                  </label>)}
              </div>
            </div>
            <label style={{
          fontSize: 12,
          color: "#434343"
        }}>
              Frequency<br />
              <select value={form.frequency} onChange={e => setForm({
            ...form,
            frequency: e.target.value
          })} style={{
            padding: "6px 8px",
            border: "1px solid #D9D9D9",
            borderRadius: 4
          }}>
                {PT_FREQS.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            <button style={S.btn} onClick={addTask} disabled={!form.description.trim()}>Save task</button>
          </div>
        </div>}

      {/* Legend + search */}
      <div style={{
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      marginBottom: 10,
      alignItems: "center"
    }}>
        {["ok", "due", "overdue", "never", "na"].map(st => <span key={st} style={{
        background: PT_STATE[st].bg,
        borderRadius: 6,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 600,
        color: "#434343"
      }}>
            {PT_STATE[st].label}
          </span>)}
      </div>
      <div style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 10,
      flexWrap: "wrap"
    }}>
        <input type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search pharmacy name…" style={{
        padding: "6px 10px",
        fontSize: 13,
        border: "1px solid #D9D9D9",
        borderRadius: 6,
        width: 240,
        outline: "none"
      }} />
        {query && <button style={S.btnGhost} onClick={() => setQuery("")}>Clear</button>}
        <label style={{
        fontSize: 13,
        fontWeight: 600,
        color: "#434343",
        marginLeft: 10
      }}>
          Search for event:
        </label>
        <input type="text" value={eventSearch} onChange={e => setEventSearch(e.target.value)} placeholder="Any text — task, value, note, staff, date…" title="Matches against every part of an event: task name, frequency, recorded value, date, note, entered-by and state. Non-matching columns are hidden." style={{
        padding: "6px 10px",
        fontSize: 13,
        border: "1px solid #D9D9D9",
        borderRadius: 6,
        width: 260,
        outline: "none"
      }} />
        {eventSearch && <button style={S.btnGhost} onClick={() => setEventSearch("")}>Clear</button>}
        <span style={{
        fontSize: 12,
        color: "#5D5D5D"
      }}>
          {tasks.length}{eq ? ` of ${allTasks.length}` : ""} task{tasks.length === 1 ? "" : "s"} · {rows.length} site{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      {tasks.length === 0 ? <div style={{
      fontSize: 13,
      color: "#5D5D5D"
    }}>
          No tasks yet. Click "+ Add task" to create the first one.
        </div> : <div style={{
      overflowX: "auto"
    }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={{
              ...S.th,
              position: "sticky",
              left: 0,
              background: "#fff"
            }}>
                  Pharmacy
                </th>
                {tasks.map(t => {
              const isNumeric = t.dataType === "numeric";
              return <th key={String(t.taskId)} style={{
                ...S.th,
                textAlign: "center",
                minWidth: 120
              }}>
                    <span onClick={() => isNumeric && openEstateTrend(t)} style={{
                  cursor: isNumeric ? "pointer" : "default",
                  textDecoration: isNumeric ? "underline" : "none",
                  color: isNumeric ? "#1e3a5f" : undefined
                }} title={isNumeric ? "Click for the estate-wide monthly trend" : ""}>
                      {t.description}
                    </span>
                    <div style={{
                  fontWeight: 400,
                  fontSize: 11,
                  color: "#5D5D5D"
                }}>
                      {t.frequency} · {PT_TYPES.find(x => x.key === t.dataType)?.label || t.dataType}
                    </div>
                    {t.complianceElementId ? <span title="Required for Regulatory Compliance — cannot be deleted." style={{
                  fontSize: 10,
                  color: "#A8071A",
                  fontWeight: 700,
                  marginTop: 2,
                  display: "inline-block"
                }}>
                        🔒 Compliance
                      </span> : <button style={{
                  ...S.btnGhost,
                  fontSize: 10,
                  padding: "1px 6px",
                  marginTop: 2
                }} onClick={() => deleteTask(String(t.taskId))} title="Delete this task for the group">delete</button>}
                  </th>;
            })}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => <tr key={String(r.pharmacyId)}>
                  <td style={{
              ...S.td,
              fontWeight: 600,
              position: "sticky",
              left: 0,
              background: "#fff"
            }}>
                    {displayName(r.pharmacyName, masked)}
                  </td>
                  {tasks.map(t => {
              const cell = r.cells[String(t.taskId)] || {
                state: "never"
              };
              const sc = PT_STATE[cell.state] || PT_STATE.never;
              if (cell.state === "na") {
                return <td key={String(t.taskId)} style={{
                  ...S.td,
                  textAlign: "center",
                  background: sc.bg,
                  color: "#8C8C8C",
                  cursor: "pointer"
                }} title="Marked not relevant — click to re-enable" onClick={() => toggleOptout(r, t, false)}>
                          N/A
                        </td>;
              }
              return <td key={String(t.taskId)} style={{
                ...S.td,
                textAlign: "center",
                background: sc.bg,
                cursor: "pointer",
                position: "relative"
              }} title={`${sc.label}${cell.recordedAt ? ` — last ${fmtDate(cell.recordedAt)}` : ""}${cell.recordedBy ? `\nEntered by ${cell.recordedBy}` : ""}${cell.note ? `\n“${cell.note}”` : ""}\nClick to ${cell.entryId ? "edit this entry" : "record"}`} onClick={() => openEntry(r, t)}>
                        <div style={{
                  fontWeight: 600
                }}>
                          {ptFmtValue(t.dataType, cell.value)}
                        </div>
                        <div style={{
                  fontSize: 11,
                  color: "#5D5D5D"
                }}>
                          {cell.recordedAt ? fmtDate(cell.recordedAt) : "—"}
                        </div>
                        {cell.note && <div style={{
                  fontSize: 10,
                  color: "#5D5D5D",
                  fontStyle: "italic",
                  maxWidth: 130,
                  margin: "0 auto",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}>
                            “{cell.note}”
                          </div>}
                        <span title="View full history" onClick={e => {
                  e.stopPropagation();
                  openHistory(r, t);
                }} style={{
                  position: "absolute",
                  top: 2,
                  right: 4,
                  fontSize: 10,
                  color: "#8C8C8C",
                  cursor: "pointer"
                }}>⌚</span>
                      </td>;
            })}
                </tr>)}
            </tbody>
          </table>
        </div>}

      {/* History / graph popup */}
      {historyCell && (() => {
      const {
        row,
        task,
        def,
        entries
      } = historyCell;
      const dataType = def?.dataType || task.dataType;
      const numericPoints = dataType === "numeric" ? [...entries].reverse().map(e => ({
        x: e.recordedAt,
        y: Number(e.value)
      })) : [];
      return <div className="popup">
            <div className="popup-content">
              <button className="close-btn" onClick={() => setHistoryCell(null)}>X</button>
              <h3 style={{
            marginTop: 0,
            marginBottom: 2
          }}>{displayName(row.pharmacyName, masked)}</h3>
              <div style={{
            fontSize: 13,
            color: "#434343",
            marginBottom: 12
          }}>
                {task.description}{" "}
                <span style={{
              color: "#5D5D5D"
            }}>
                  ({task.frequency} · {PT_TYPES.find(x => x.key === dataType)?.label || dataType})
                </span>
              </div>

              {/* Record-new + opt-out actions */}
              <div style={{
            display: "flex",
            gap: 8,
            marginBottom: 14,
            flexWrap: "wrap"
          }}>
                <button style={S.btn} onClick={() => {
              setHistoryCell(null);
              openEntry(row, task);
            }}>
                  + Record value
                </button>
                <button style={S.btnGhost} onClick={() => {
              toggleOptout(row, task, true);
              setHistoryCell(null);
            }}>
                  Mark not relevant
                </button>
              </div>

              {numericPoints.length > 1 && <div style={{
            marginBottom: 14
          }}>
                  <Sparkline points={numericPoints} />
                </div>}

              {entries.length === 0 ? <div style={{
            fontSize: 13,
            color: "#5D5D5D"
          }}>No values recorded yet.</div> : <div style={{
            maxHeight: 320,
            overflowY: "auto"
          }}>
                  <table style={S.table}>
                    <thead>
                      <tr>
                        <th style={S.th}>Recorded</th>
                        <th style={S.th}>Value</th>
                        <th style={S.th}>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map(e => <tr key={String(e._id)}>
                          <td style={S.td}>{fmtDateTime(e.recordedAt)}</td>
                          <td style={{
                    ...S.td,
                    fontWeight: 600
                  }}>
                            {ptFmtValue(dataType, e.value)}
                          </td>
                          <td style={S.td}>{e.recordedBy || "—"}</td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </div>
          </div>;
    })()}

      {/* Estate-wide monthly trend popup (numeric task, column-header click) */}
      {estateTrend && (() => {
      const pts = estateTrend.points || [];
      const graphPoints = pts.map(p => ({
        x: p.month,
        y: p.sum
      }));
      return <div className="popup">
            <div className="popup-content">
              <button className="close-btn" onClick={() => setEstateTrend(null)}>X</button>
              <h3 style={{
            marginTop: 0,
            marginBottom: 2
          }}>
                {estateTrend.task.description}
              </h3>
              <div style={{
            fontSize: 13,
            color: "#5D5D5D",
            marginBottom: 12
          }}>
                Estate-wide total by month (all sites combined)
              </div>
              {graphPoints.length === 0 ? <div style={{
            fontSize: 13,
            color: "#5D5D5D"
          }}>No entries recorded yet.</div> : <>
                  <Sparkline points={graphPoints} width={560} height={180} />
                  {/* Month axis labels */}
                  <div style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 10,
              color: "#5D5D5D",
              margin: "2px 24px 14px"
            }}>
                    <span>{pts[0].month}</span>
                    {pts.length > 2 && <span>{pts[Math.floor(pts.length / 2)].month}</span>}
                    <span>{pts[pts.length - 1].month}</span>
                  </div>
                  <div style={{
              maxHeight: 240,
              overflowY: "auto"
            }}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Month</th>
                          <th style={{
                      ...S.th,
                      textAlign: "center"
                    }}>Total</th>
                          <th style={{
                      ...S.th,
                      textAlign: "center"
                    }}>Readings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...pts].reverse().map(p => <tr key={p.month}>
                            <td style={S.td}>{p.month}</td>
                            <td style={{
                      ...S.td,
                      textAlign: "center",
                      fontWeight: 600
                    }}>{p.sum}</td>
                            <td style={{
                      ...S.td,
                      textAlign: "center",
                      color: "#5D5D5D"
                    }}>{p.count}</td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </>}
            </div>
          </div>;
    })()}

      {/* Value-entry popup */}
      {entryCell && (() => {
      const {
        row,
        task
      } = entryCell;
      return <PeriodicTaskEntryDialog token={token} task={task} target={{
        dbName: row.dbName,
        pharmacyId: row.pharmacyId,
        pharmacyName: displayName(row.pharmacyName, masked)
      }} existingEntry={(() => {
        const c = row.cells?.[String(task.taskId)];
        return c?.entryId ? {
          entryId: c.entryId,
          value: c.value,
          recordedAt: c.recordedAt,
          note: c.note,
          recordedBy: c.recordedBy
        } : null;
      })()} onClose={() => setEntryCell(null)} onSaved={() => {
        setEntryCell(null);
        reload();
      }} />;
    })()}
    </div>;
}

// ---------------------------------------------------------------------------
// SOP compliance card (face) — moved out of the old Compliance menu.
// Face shows: title + sites-using ratio (ever assigned a policy), then a
// row for sites with unread policies (xx/yy), red background if > 0.
// Click → full-page SOPCompliance view.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Staff appraisals card — outstanding appraisals across the fleet.
// Red border + red-tinted count row when any staff member is overdue
// (anniversary model: >12 months since last completed appraisal, or
// never appraised and employed >12 months). Amber if only "due".
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Clock in/out card — opens the attendance panel (Day / Month / Summary).
// The "today" dynamics (who's in now, late arrivals so far, etc.) are
// deliberately deferred: the card is a plain opener until the detail views
// are signed off, then a status endpoint will light it up like the others.
// ---------------------------------------------------------------------------
// Holiday rota card - opens the per-branch rota (leave, absence, clockings).
// A plain opener for now: a status endpoint could light it up later with, say,
// how many branches are short-staffed this week.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Surveys row — "Feedback" card. A plain opener for now, matching the
// holiday-rota / staff-absences cards beside it. What (if anything) shows
// on the front of the card is still to be decided — see PatientFeedback.js
// for the full module it opens.
// ---------------------------------------------------------------------------
function SurveysCard({
  token,
  onOpen
}) {
  const [data, setData] = useState(null); // { ratingsReceived, commentsReceived, complaintsReviewed, complaintsOutstanding }
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    fetch(`${API_BASE}/api/feedback/status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: "{}"
    }).then(r => r.json().then(d => {
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      return d;
    })).then(d => {
      if (!cancelled) {
        setData(d);
        setLoaded(true);
      }
    }).catch(e => {
      if (!cancelled) {
        setError(e.message);
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const hasOutstanding = !!(loaded && data && data.complaintsOutstanding > 0);
  const cardBg = hasOutstanding ? "#fde2e1" : undefined;
  return <div style={{
    ...S.card,
    border: "3px solid #722ED1",
    cursor: "pointer",
    background: cardBg
  }} onClick={onOpen} title="Patient feedback — design a wall poster and a webpage, then review what comes back branch by branch.">
      <div style={S.cardTitle}>Feedback</div>
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginBottom: 8,
      lineHeight: 1.4
    }}>
        Design an A4 poster and the webpage its QR code opens, then review
        ratings, comments and complaints as they come in.
      </div>
      {loaded && data ? <>
          <div style={S.metaRow}>
            <span>Feedback ratings received</span>
            <span>{data.ratingsReceived}</span>
          </div>
          <div style={S.metaRow}>
            <span>Comments received</span>
            <span>{data.commentsReceived}</span>
          </div>
          <div style={S.metaRow}>
            <span>Complaints reviewed</span>
            <span>{data.complaintsReviewed}</span>
          </div>
          <div style={S.metaRow}>
            <span>Complaints outstanding</span>
            <span style={hasOutstanding ? S.urgent : S.ok}>{data.complaintsOutstanding}</span>
          </div>
        </> : <div style={S.metaRow}>
          <span>Patient feedback</span>
          <span style={{
        color: "#5D5D5D"
      }}>{error ? "—" : "loading…"}</span>
        </div>}
      {error && <div style={{
      fontSize: 11,
      color: "#CF1322",
      marginTop: 6
    }}>{error}</div>}
    </div>;
}
function HolidayRotaCard({
  onOpen
}) {
  return <div style={{
    ...S.card,
    border: "3px solid #52C41A"
  }} onClick={onOpen}>
      <div style={S.cardTitle}>Holiday rota</div>
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginBottom: 8,
      lineHeight: 1.4
    }}>
        Who is in, who is off and why - branch by branch, month by month.
        Leave, absence and actual clockings in one view.
      </div>
      <div style={S.metaRow}>
        <span>Outstanding requests</span>
        <span style={{
        color: "#5D5D5D"
      }}>open to view</span>
      </div>
    </div>;
}

// ---------------------------------------------------------------------------
// Staff absences card - opens the fleet-wide absence grid.
// A plain opener, like the rota card beside it. A summary endpoint could light
// it up later with, say, absence days across the fleet this month.
// ---------------------------------------------------------------------------
function StaffAbsencesCard({
  onOpen
}) {
  return <div style={{
    ...S.card,
    border: "3px solid #CF1322",
    cursor: "pointer"
  }} onClick={onOpen} title="Staff absences - every recorded absence by branch and type over any date range, counted as events, days or hours.">
      <div style={S.cardTitle}>Staff absences</div>
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginBottom: 8,
      lineHeight: 1.4
    }}>
        Every recorded absence, branch by branch and type by type. Count it as
        events, days or hours, over any date range.
      </div>
      <div style={S.metaRow}>
        <span>This year to date</span>
        <span style={{
        color: "#5D5D5D"
      }}>open to view</span>
      </div>
    </div>;
}

/**
 * Staff clock in/out.
 *
 * How many of the staff EXPECTED today have actually clocked in, as a bar
 * running red through amber to green. "Expected" comes from the working
 * pattern in staff_times; anyone on approved holiday today is taken out of
 * the denominator rather than counted as a no-show.
 */
function ClockCard({
  token,
  onOpen
}) {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    apiGet("clock_card", token).then(d => {
      if (!cancelled) {
        setData(d);
        setLoaded(true);
      }
    }).catch(() => {
      if (!cancelled) setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const pct = data?.percent; // null when nobody is scheduled
  const has = typeof pct === "number";

  // Staged rather than a smooth gradient: these sit in a row of small cards
  // and a continuous ramp makes 62% and 71% look identical.
  const stage = !has ? {
    bar: "#8C8C8C",
    edge: "#0E8CFF",
    text: "#5D5D5D"
  } : pct >= 95 ? {
    bar: "#389E0D",
    edge: "#52C41A",
    text: "#15803d"
  } : pct >= 80 ? {
    bar: "#65a30d",
    edge: "#84cc16",
    text: "#4d7c0f"
  } : pct >= 60 ? {
    bar: "#eab308",
    edge: "#facc15",
    text: "#a16207"
  } : pct >= 40 ? {
    bar: "#f97316",
    edge: "#FFA940",
    text: "#c2410c"
  } : {
    bar: "#CF1322",
    edge: "#F5222D",
    text: "#CF1322"
  };
  return <div style={{
    ...S.card,
    border: `3px solid ${loaded ? stage.edge : "#0E8CFF"}`,
    cursor: "pointer"
  }} onClick={onOpen} title="Staff clock in/out — day and month views, summaries, workforce planner, raw attendance data and the pharmacist rota.">
      <div style={S.cardTitle}>Staff clock in/out</div>

      {!loaded && <div style={{
      fontSize: 11,
      color: "#8C8C8C"
    }}>Loading today's figures…</div>}

      {loaded && !has && <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      lineHeight: 1.4
    }}>
          Nobody is scheduled to work today.
          {data?.onHoliday > 0 && ` ${data.onHoliday} on holiday.`}
        </div>}

      {loaded && has && <>
          <div style={{
        display: "flex",
        alignItems: "baseline",
        gap: 6,
        marginBottom: 5
      }}>
            <span style={{
          fontSize: 21,
          fontWeight: 700,
          color: stage.text
        }}>{pct}%</span>
            <span style={{
          fontSize: 11,
          color: "#5D5D5D"
        }}>
              {data.clockedIn} of {data.expected} clocked in
            </span>
          </div>

          <div style={{
        height: 8,
        borderRadius: 4,
        background: "#F0F0F0",
        overflow: "hidden",
        marginBottom: 7
      }}>
            <div style={{
          width: `${Math.min(100, Math.max(0, pct))}%`,
          height: "100%",
          background: stage.bar,
          borderRadius: 4,
          transition: "width 300ms"
        }} />
          </div>

          <div style={{
        ...S.metaRow,
        fontSize: 11
      }}>
            <span>On holiday today</span>
            <span style={{
          color: "#5D5D5D"
        }}>{data.onHoliday}</span>
          </div>
          {data.absent > 0 &&
      // Absent staff ARE still in the denominator, so say so rather than
      // leaving a shortfall unexplained.
      <div style={{
        ...S.metaRow,
        fontSize: 11
      }}>
              <span style={{
          color: "#b45309"
        }}>Recorded absent</span>
              <span style={{
          color: "#b45309"
        }}>{data.absent}</span>
            </div>}
        </>}
    </div>;
}

// ** NOT LIVE ** Staff training card — placeholder with mock figures until
// the training-card generator (staff_training_assignments) ships. Amber
// styling like the MHRA card; deliberately NOT wired to any UI yet.
// ---------------------------------------------------------------------------
function StaffTrainingCard() {
  // ** NOT LIVE ** — mock numbers; replace with a real endpoint when the
  // training assignments system is built.
  const upToDate = 41;
  const overdue = 9;
  const imminent = 6;
  const row = (label, value, bg, fg) => <div style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: bg,
    color: fg,
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 13,
    marginBottom: 4
  }}>
      <span>{label}</span>
      <span style={{
      fontWeight: 700
    }}>{value}</span>
    </div>;
  return <div style={{
    ...S.card,
    border: "3px solid #FA8C16",
    backgroundColor: "#FFF7E6",
    cursor: "default"
  }} title="** NOT LIVE ** Staff training compliance — placeholder figures; not yet wired to a training UI.">
      <div style={S.cardTitle}>
        Staff training
        <span style={{
        fontWeight: 400,
        color: "#AD4E00",
        fontSize: 11
      }}> · NOT LIVE</span>
      </div>
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginBottom: 8,
      lineHeight: 1.4
    }}>
        Training register compliance (placeholder figures).
      </div>
      {row("Staff up to date with training", upToDate, "#F6FFED", "#389E0D")}
      {row("Staff overdue / needing training", overdue, "#FFF1F0", "#820014")}
      {row("Staff with training imminent", imminent, "#FFFBE6", "#AD6800")}
    </div>;
}
function AppraisalsCard({
  token,
  onOpen
}) {
  const [data, setData] = useState(null); // { overall, perPharmacy, outstanding }
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    fetch(`${API_BASE}/api/appraisals/status`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      body: "{}"
    }).then(r => r.json().then(d => {
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      return d;
    })).then(d => {
      if (!cancelled) {
        setData(d);
        setLoaded(true);
      }
    }).catch(e => {
      if (!cancelled) {
        setError(e.message);
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const overdue = data ? data.perPharmacy.reduce((a, p) => a + (p.overdue || 0), 0) : null;
  const due = data ? data.perPharmacy.reduce((a, p) => a + (p.due || 0), 0) : null;
  const sitesAffected = data ? data.perPharmacy.filter(p => (p.overdue || 0) > 0).length : null;
  const hasOverdue = typeof overdue === "number" && overdue > 0;
  const hasDue = typeof due === "number" && due > 0;
  const borderColor = !loaded ? "#FA8C16" : hasOverdue ? "#F5222D" : hasDue ? "#FA8C16" : "#52C41A";
  const rowBg = !loaded ? undefined : hasOverdue ? "#fde2e1" : hasDue ? "#FFF7E6" : "#F6FFED";
  return <div style={{
    ...S.card,
    border: `3px solid ${borderColor}`,
    cursor: "pointer"
  }} onClick={onOpen} title="Staff annual appraisals — click to open the Staff page: full status, outstanding list and appraisal records.">
      <div style={S.cardTitle}>
        Staff appraisals
        {loaded && data && <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }}> ({data.perPharmacy.length} sites)</span>}
      </div>
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginBottom: 8,
      lineHeight: 1.4
    }}>
        Annual appraisal status (12-month anniversary model). Click to open the staff reviews & appraisals page.
      </div>
      <div style={{
      ...S.metaRow,
      background: rowBg,
      borderRadius: rowBg ? 4 : undefined,
      padding: rowBg ? "2px 6px" : undefined,
      margin: rowBg ? "0 -6px" : undefined
    }} title="Staff members more than 12 months past their last completed appraisal (or never appraised and employed over a year).">
        <span>Outstanding appraisals</span>
        <span style={hasOverdue ? S.urgent : S.ok}>
          {loaded ? overdue : "…"}
        </span>
      </div>
      <div style={S.metaRow} title="Staff members whose appraisal anniversary falls within the next 30 days, plus never-appraised staff past 6 months' tenure.">
        <span>Due soon</span>
        <span style={hasDue ? {
        color: "#b45309",
        fontWeight: 700
      } : S.ok}>
          {loaded ? due : "…"}
        </span>
      </div>
      {hasOverdue && <div style={{
      fontSize: 11,
      color: "#A8071A",
      marginTop: 6
    }}>
          Across {sitesAffected} site{sitesAffected === 1 ? "" : "s"} — outstanding list on the Staff page.
        </div>}
      {error && <div style={{
      fontSize: 11,
      color: "#CF1322",
      marginTop: 6
    }}>{error}</div>}
    </div>;
}
function SopCard({
  token,
  onOpen
}) {
  const [data, setData] = useState(null); // { everUsed, total, sitesUnread, sitesUnreadDenom }
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    apiPostRoot("headoffice_sop_card", token).then(d => {
      if (!cancelled) {
        setData(d);
        setLoaded(true);
      }
    }).catch(e => {
      if (!cancelled) {
        setError(e.message);
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const unread = data ? Number(data.sitesUnread) : null;
  const unreadDenom = data ? Number(data.sitesUnreadDenom ?? data.everUsed) : null;
  const hasUnread = typeof unread === "number" && unread > 0;

  // Border: amber while loading, red if any site has unread policies, else green.
  const borderColor = !loaded ? "#FA8C16" : hasUnread ? "#F5222D" : "#52C41A";
  // Unread row background: red tint when > 0, plain otherwise.
  const rowBg = !loaded ? undefined : hasUnread ? "#fde2e1" : "#F6FFED";
  return <div style={{
    ...S.card,
    border: `3px solid ${borderColor}`
  }} onClick={onOpen}>
      <div style={S.cardTitle}>
        SOP compliance
        <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }} title="Pharmacies that have ever been assigned a policy, out of all sites in the group.">
          {" "}({data?.everUsed ?? "…"}/{data?.total ?? "…"})
        </span>
      </div>
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginBottom: 8,
      lineHeight: 1.4
    }}>
        Live now. Click to open the full SOP compliance view.
      </div>
      <div style={{
      ...S.metaRow,
      background: rowBg,
      borderRadius: rowBg ? 4 : undefined,
      padding: rowBg ? "2px 6px" : undefined,
      margin: rowBg ? "0 -6px" : undefined
    }} title="Pharmacies with at least one active, due, unread reading card right now.">
        <span>Sites with unread policies</span>
        <span style={hasUnread ? S.urgent : S.ok}>
          {loaded ? `${unread} / ${unreadDenom ?? "…"}` : "…"}
        </span>
      </div>
      {error && <div style={{
      fontSize: 11,
      color: "#CF1322",
      marginTop: 6
    }}>{error}</div>}
    </div>;
}

// ---------------------------------------------------------------------------
// Near Miss card (face) — moved out of the old Compliance menu.
// Face shows: title + sites-using ratio (ever recorded a near miss).
// Click → full-page NearMissAnalysis view.
// ---------------------------------------------------------------------------
function NearMissCard({
  token,
  onOpen
}) {
  const [data, setData] = useState(null); // { everUsed, total }
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    apiPostRoot("headoffice_nearmiss_card", token).then(d => {
      if (!cancelled) {
        setData(d);
        setLoaded(true);
      }
    }).catch(e => {
      if (!cancelled) {
        setError(e.message);
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // No urgent rule on the face — neutral grey while loading, green once loaded.
  const borderColor = !loaded ? "#FA8C16" : "#52C41A";
  return <div style={{
    ...S.card,
    border: `3px solid ${borderColor}`
  }} onClick={onOpen}>
      <div style={S.cardTitle}>
        Near miss &amp; incidents
        <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }} title="Pharmacies that have ever recorded a near miss, out of all sites in the group.">
          {" "}({data?.everUsed ?? "…"}/{data?.total ?? "…"})
        </span>
      </div>
      <div style={{
      fontSize: 11,
      color: "#5D5D5D",
      marginBottom: 8,
      lineHeight: 1.4
    }}>
        Click to open the near-miss analysis, trends and monthly review.
      </div>
      {error && <div style={{
      fontSize: 11,
      color: "#CF1322",
      marginTop: 6
    }}>{error}</div>}
    </div>;
}

// ---------------------------------------------------------------------------
// Staff card (face) — mock data for now. Amber card throughout.
// Face shows: title + sites-with-appraisals ratio, and a red "Complete
// appraisals" line with an outstanding count.
// Click → full-page Staff grid (same as the left-menu Staff view).
// ---------------------------------------------------------------------------
function MhraAlertsCard({
  token,
  onOpen
}) {
  // Live figures from headoffice.js v43 /mhra/summary.
  const [rollup, setRollup] = useState(null); // { sitesUpToDate, sitesOutstanding, totalSites }
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    setError(null);
    apiSend("mhra/summary", token, "POST", {}).then(d => {
      if (!cancelled) {
        setRollup(d.rollup || null);
        setLoaded(true);
      }
    }).catch(e => {
      if (!cancelled) {
        setError(e.message);
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const outstanding = rollup?.sitesOutstanding ?? 0;
  const borderColor = !loaded ? "#FA8C16" : outstanding > 0 ? "#F5222D" : "#52C41A";
  return <div style={{
    ...S.card,
    border: `3px solid ${borderColor}`
  }} onClick={onOpen}>
      <div style={S.cardTitle}>
        MHRA alerts
        <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }} title="Pharmacies with every live MHRA alert actioned, out of all sites in the group.">
          {" "}({rollup ? `${rollup.sitesUpToDate}/${rollup.totalSites}` : "…"})
        </span>
      </div>
      <div style={{
      marginTop: 6
    }}>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: "#F6FFED",
        color: "#389E0D",
        padding: "4px 8px",
        borderRadius: 4,
        fontSize: 13,
        marginBottom: 4
      }} title="Sites where every live MHRA alert has been acknowledged and actioned.">
          <span>Sites — all alerts actioned</span>
          <span style={{
          fontWeight: 700
        }}>{rollup ? rollup.sitesUpToDate : "…"}</span>
        </div>
        <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        backgroundColor: outstanding > 0 ? "#FFF1F0" : undefined,
        color: outstanding > 0 ? "#A8071A" : undefined,
        padding: "4px 8px",
        borderRadius: 4,
        fontSize: 13
      }} title="Sites with at least one live MHRA alert not yet actioned.">
          <span>Sites with outstanding actions</span>
          <span style={{
          fontWeight: 700
        }}>{rollup ? outstanding : "…"}</span>
        </div>
      </div>
      {error && <div style={{
      fontSize: 11,
      color: "#CF1322",
      marginTop: 6
    }}>{error}</div>}
    </div>;
}

// ---------------------------------------------------------------------------
// Accident book card (face). Pulls live counts from /api/headoffice/accident-book/riddor-card.
//   • ratio = pharmacies with any accident-book entries / total sites
//   • Total non-RIDDOR reportable accidents
//   • Total RIDDOR reportable accidents (amber if > 0)
//   • Total accidents with RIDDOR status not yet known (red if > 0)
// Click → full-page Accident book detail (three panels).
// ---------------------------------------------------------------------------
function AccidentBookCard({
  token,
  onOpen
}) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    apiGet("accident-book/riddor-card", token).then(d => {
      if (!cancelled) setData(d);
    }).catch(() => {
      if (!cancelled) setData(null);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const sitesWithEntries = data?.sitesWithEntries ?? "–";
  const totalSites = data?.totalSites ?? "–";
  const notReportable = data?.notReportable ?? 0;
  const reportable = data?.reportable ?? 0;
  const notYetKnown = data?.notYetKnown ?? 0;
  const line = (label, value, highlight) => <div style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 8px",
    fontSize: 13,
    borderRadius: 4,
    marginBottom: 4,
    ...(highlight === "amber" ? {
      backgroundColor: "#FFF7E6",
      color: "#AD4E00"
    } : highlight === "red" ? {
      backgroundColor: "#FFF1F0",
      color: "#820014"
    } : {})
  }}>
      <span>{label}</span>
      <span style={{
      fontWeight: 700
    }}>{value}</span>
    </div>;
  return <div style={S.card} onClick={onOpen}>
      <div style={S.cardTitle}>
        Accident book
        <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }} title="Pharmacies with any accident-book entries, out of all sites in the group.">
          {" "}({sitesWithEntries}/{totalSites})
        </span>
      </div>
      <div style={{
      marginTop: 6
    }}>
        {line("Total non-RIDDOR reportable accidents", notReportable, null)}
        {line("Total RIDDOR reportable accidents", reportable, reportable > 0 ? "amber" : null)}
        {line("Total accidents RIDDOR status not yet known", notYetKnown, notYetKnown > 0 ? "red" : null)}
      </div>
    </div>;
}

// ---------------------------------------------------------------------------
// Action Centre panel — sits to the right of the live-view banner.
// Shows the fleet-wide pending alert count and a button into the full-page
// Action Centre. Amber border/badge when there are pending alerts.
// ---------------------------------------------------------------------------
function ActionCentrePanel({
  token,
  onOpen
}) {
  // "Not opened" was dropped: there's no reliable way to detect a real email
  // being opened, so it's no longer tracked as an outstanding-work signal —
  // see fleetActionCentreCard in headoffice.js. Acknowledgement is real.
  const [counts, setCounts] = useState(null); // { notAcknowledged }
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    apiGet("action-centre/card", token).then(d => {
      if (!cancelled) {
        setCounts(d || {
          notAcknowledged: 0
        });
        setLoaded(true);
      }
    }).catch(() => {
      if (!cancelled) {
        setCounts(null);
        setLoaded(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [token]);
  const notAck = counts?.notAcknowledged ?? 0;
  const has = notAck > 0;
  const borderColor = !loaded ? "#FA8C16" : has ? "#FA8C16" : "#52C41A";
  const badge = (label, count, color) => <span style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 999,
    color: color.text,
    background: color.bg,
    border: `1px solid ${color.border}`
  }}>
      {label}
      <span style={{
      fontWeight: 700
    }}>{loaded ? count : "…"}</span>
    </span>;

  // Horizontal strip — sits in line with the page heading at the top.
  return <div style={{
    background: "#fff",
    border: `2px solid ${borderColor}`,
    borderRadius: 6,
    padding: "3px 10px",
    display: "flex",
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap"
  }}>
      <div style={{
      fontSize: 14,
      fontWeight: 600,
      color: "#262626",
      whiteSpace: "nowrap"
    }}>
        Action Centre
      </div>
      {badge("Not acknowledged", notAck, {
      text: "#820014",
      bg: "#FFF1F0",
      border: "#FFCCC7"
    })}
      <button style={S.btnGhost} onClick={onOpen}>
        Open Action Centre
      </button>
    </div>;
}

// ---------------------------------------------------------------------------
// Full-page compliance views, rendered inline inside HeadOffice with a Back
// bar. The components self-fetch their token from localStorage and hit the
// API root directly, so they need no props.
// ---------------------------------------------------------------------------
function ComplianceFullPage({
  kind,
  onClose
}) {
  return <div>
      {/* "Back to dashboard" removed per request (kept in code). Navigation
          back is via the left rail. Restore by removing this wrapper.
       <div style={{ marginBottom: 12 }}>
        <button style={S.btnGhost} onClick={onClose}>← Back to dashboard</button>
       </div>
       */}
      {kind === "sop" && <SOPCompliance />}
      {kind === "holidayRota" && <HolidayRota />}
      {kind === "staffAbsences" && <StaffAbsences />}
      {kind === "nearMiss" && <NearMissAnalysis />}
      {kind === "staff" && <Staff />}
      {kind === "clock" && <ClockInOut />}
      {kind === "mhra" && <MhraAlerts />}
      {kind === "patientFeedback" && <PatientFeedback />}
      {kind === "accidentBook" && <AccidentBookDetail />}
      {/* actionCentre case removed with the strip above — see Communications. */}
    </div>;
}

// ---------------------------------------------------------------------------
// Campaigns — fleet-wide patient messaging campaign history.
// ---------------------------------------------------------------------------
function CampaignsCard({
  onOpen
}) {
  return <div style={{
    ...S.card,
    border: "3px solid #91D5FF",
    cursor: "pointer"
  }} onClick={onOpen}>
      <div style={S.cardTitle}>Campaigns</div>
      <div style={{
      fontSize: 12,
      color: "#5D5D5D",
      lineHeight: 1.45
    }}>
        View patient messaging campaigns sent by every branch, including send date,
        recipient count and total delivery cost.
      </div>
      <div style={{
      marginTop: 12,
      color: "#0E8CFF",
      fontWeight: 700,
      fontSize: 13
    }}>
        Open campaigns →
      </div>
    </div>;
}
function CampaignsView({
  token,
  onClose
}) {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState({
    key: "dateSent",
    dir: "desc"
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const q = new URLSearchParams();
        if (from) q.set("from", from);
        if (to) q.set("to", to);
        const data = await apiGet(`messaging/campaigns${q.toString() ? `?${q}` : ""}`, token);
        if (!cancelled) {
          setRows(Array.isArray(data?.rows) ? data.rows : []);
          setBranches(Array.isArray(data?.branches) ? data.branches : []);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, from, to]);
  const shown = rows.filter(r => !branch || r.branchName === branch).sort((a, b) => {
    const av = a?.[sort.key],
      bv = b?.[sort.key];
    let n;
    if (sort.key === "recipients" || sort.key === "totalCost") n = Number(av || 0) - Number(bv || 0);else if (sort.key === "ageRange") n = Number(a.minAge ?? -1) - Number(b.minAge ?? -1) || Number(a.maxAge ?? -1) - Number(b.maxAge ?? -1);else if (sort.key === "campaignDate" || sort.key === "dateSent") n = new Date(av || 0) - new Date(bv || 0);else n = String(av || "").localeCompare(String(bv || ""), undefined, {
      sensitivity: "base"
    });
    return sort.dir === "asc" ? n : -n;
  });
  const toggleSort = key => setSort(p => ({
    key,
    dir: p.key === key && p.dir === "asc" ? "desc" : "asc"
  }));
  const arrow = key => sort.key === key ? sort.dir === "asc" ? " ▲" : " ▼" : "";
  const fmtDate = v => v ? new Date(v).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }) : "—";
  const fmtDateTime = v => v ? new Date(v).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }) : "—";
  const fmtAgeRange = r => {
    if (r?.includeAllAges) return "All ages";
    const min = r?.minAge,
      max = r?.maxAge;
    if (min != null && max != null) return `${min}–${max}`;
    if (min != null) return `${min}+`;
    if (max != null) return `Up to ${max}`;
    return "—";
  };
  const th = {
    textAlign: "left",
    padding: "10px 12px",
    borderBottom: "2px solid #F0F0F0",
    background: "#FAFAFA",
    cursor: "pointer",
    whiteSpace: "nowrap",
    fontSize: 12
  };
  const td = {
    padding: "10px 12px",
    borderBottom: "1px solid #F0F0F0",
    fontSize: 13,
    color: "#434343"
  };
  return <div>
      <div style={{
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginBottom: 16
    }}>
        <button style={S.btnGhost} onClick={onClose}>← Back</button>
        <div>
          <div style={{
          fontSize: 20,
          fontWeight: 800,
          color: "#262626"
        }}>Campaigns</div>
          <div style={{
          fontSize: 12,
          color: "#5D5D5D"
        }}>Patient messaging campaign history across your branches.</div>
        </div>
      </div>
      <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: 12,
      alignItems: "end",
      marginBottom: 16,
      padding: 12,
      border: "1px solid #F0F0F0",
      borderRadius: 8,
      background: "#fff"
    }}>
        <label style={{
        fontSize: 12,
        fontWeight: 600
      }}>From<br /><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={{
          padding: 7,
          marginTop: 4
        }} /></label>
        <label style={{
        fontSize: 12,
        fontWeight: 600
      }}>To<br /><input type="date" value={to} onChange={e => setTo(e.target.value)} style={{
          padding: 7,
          marginTop: 4
        }} /></label>
        <label style={{
        fontSize: 12,
        fontWeight: 600
      }}>Branch<br />
          <select value={branch} onChange={e => setBranch(e.target.value)} style={{
          padding: 8,
          marginTop: 4,
          minWidth: 220
        }}>
            <option value="">All branches</option>{branches.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>
        {(from || to || branch) && <button style={S.btnGhost} onClick={() => {
        setFrom("");
        setTo("");
        setBranch("");
      }}>Clear filters</button>}
      </div>
      {error && <div style={S.error}>{error}</div>}
      {loading ? <div style={{
      padding: 24,
      color: "#5D5D5D"
    }}>Loading campaigns…</div> : <div style={{
      overflowX: "auto",
      border: "1px solid #F0F0F0",
      borderRadius: 8,
      background: "#fff"
    }}>
          <table style={{
        width: "100%",
        borderCollapse: "collapse"
      }}>
            <thead><tr>
              <th style={th} onClick={() => toggleSort("branchName")}>Branch name{arrow("branchName")}</th>
              <th style={th} onClick={() => toggleSort("campaignDate")}>Date{arrow("campaignDate")}</th>
              <th style={th} onClick={() => toggleSort("campaignTitle")}>Campaign title{arrow("campaignTitle")}</th>
              <th style={th} onClick={() => toggleSort("ageRange")}>Age range{arrow("ageRange")}</th>
              <th style={th} onClick={() => toggleSort("dateSent")}>Date sent{arrow("dateSent")}</th>
              <th style={{
              ...th,
              textAlign: "right"
            }} onClick={() => toggleSort("recipients")}>Recipients{arrow("recipients")}</th>
              <th style={{
              ...th,
              textAlign: "right"
            }} onClick={() => toggleSort("totalCost")}>Total cost{arrow("totalCost")}</th>
            </tr></thead>
            <tbody>{shown.length ? shown.map(r => <tr key={r.id}>
              <td style={td}>{r.branchName || "—"}</td><td style={td}>{fmtDate(r.campaignDate)}</td>
              <td style={td}>{r.campaignTitle || "—"}</td><td style={td}>{fmtAgeRange(r)}</td><td style={td}>{fmtDateTime(r.dateSent)}</td>
              <td style={{
              ...td,
              textAlign: "right"
            }}>{Number(r.recipients || 0).toLocaleString("en-GB")}</td>
              <td style={{
              ...td,
              textAlign: "right"
            }}>£{Number(r.totalCost || 0).toFixed(2)}</td>
            </tr>) : <tr><td colSpan={7} style={{
              ...td,
              textAlign: "center",
              padding: 24,
              color: "#5D5D5D"
            }}>No campaigns found for these filters.</td></tr>}</tbody>
          </table>
        </div>}
    </div>;
}

// ---------------------------------------------------------------------------
// Dashboard row groups. Hoisted to module scope because the "Show rows"
// checkbox strip now renders in the page header, outside the block that used
// to own this list. Computed once — MODULES is a module constant, and any key
// not assigned to a group falls into "Other" so a new module never vanishes.
// ---------------------------------------------------------------------------
const DASHBOARD_GROUPS = (() => {
  const groups = [{
    label: "Scan to shelf, deliveries and messaging",
    keys: ["rx_retrievals", "rx_deliveries", "messaging", "campaigns"]
  }, {
    label: "Site daily activities",
    keys: ["rpSignIn", "fridge", "cleaning", "handover", "visitorLogs"]
  }, {
    label: "Staff",
    keys: ["sop", "appraisals", "clock", "training", "holidayRota", "staffAbsences"]
  }, {
    label: "Compliance",
    keys: ["dateChecking", "cdRegister", "periodic"]
  }, {
    label: "Safety",
    keys: ["nearMiss", "patientSafety", "accidentBook", "mhra"]
  }, {
    label: "Surveys",
    keys: ["patientFeedback"]
  }];
  const assigned = new Set(groups.flatMap(g => g.keys));
  const otherKeys = MODULES.map(m => m.key).filter(k => !assigned.has(k));
  if (otherKeys.length) groups.push({
    label: "Other",
    keys: otherKeys
  });
  return groups.map(group => ({
    ...group,
    keys: group.keys.filter(key => CARE_HEAD_OFFICE_KEYS.has(key))
  })).filter(group => group.keys.length);
})();

// Refresh / Job runs / Inactive sites / Pat App - dev are hidden per request but
// kept in code. Flip to false to restore that toolbar row.
const HIDDEN_TOOLBAR = true;

// ---------------------------------------------------------------------------
// HeaderPortal — if App.js provides the #section-header-slot next to the
// organisation title, the section heading (and anything beside it, like the
// Action Centre strip) renders INTO that top line instead of taking a row
// of its own — killing the vertical waste. Falls back to normal inline
// rendering when the slot doesn't exist (older App.js, tests, embeds).
// ---------------------------------------------------------------------------
function HeaderPortal({
  children
}) {
  const [slot, setSlot] = useState(null);
  useEffect(() => {
    setSlot(document.getElementById("section-header-slot"));
    return () => {
      // leave the slot itself in place; React removes our portal content
    };
  }, []);
  return slot ? createPortal(children, slot) : children;
}

// ---------------------------------------------------------------------------
// Top-level HeadOffice view
// ---------------------------------------------------------------------------
export default function HeadOffice({
  token: tokenProp,
  onNavigateApp,
  onOpenSettings
} = {}) {
  const [token] = useState(() => tokenProp || localStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("jwt") || "");
  const [selected, setSelected] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showInfo, setShowInfo] = useState(false); // hover popup on the heading
  const [view, setView] = useState("modules"); // "modules" | "job_runs" | "inactive"
  // Dashboard row visibility: labels the user has HIDDEN via the checkbox
  // strip above the groups. Default: nothing hidden (all rows shown).
  // Persisted per browser in localStorage.
  const [hiddenGroups, setHiddenGroups] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("care_ho_hidden_groups") || "[]"));
    } catch (_) {
      return new Set();
    }
  });
  const toggleGroup = label => {
    setHiddenGroups(prev => {
      const n = new Set(prev);
      if (n.has(label)) n.delete(label);else n.add(label);
      try {
        localStorage.setItem("care_ho_hidden_groups", JSON.stringify([...n]));
      } catch (_) {}
      return n;
    });
  };
  // Messaging SIM detail (placeholder) — set to the messaging module when the
  // SIM line on its card is clicked.
  const [simDetail, setSimDetail] = useState(null);
  const [campaignsOpen, setCampaignsOpen] = useState(false);
  // Periodic-tasks view opened with the overdue filter pre-applied.
  const [periodicOverdue, setPeriodicOverdue] = useState(false);

  // Prescription activity snapshot (shared by the two activity cards + grids)
  const [rxSnapshot, setRxSnapshot] = useState(null);
  // selectedRx = "retrievals" | "deliveries" | null
  const [selectedRx, setSelectedRx] = useState(null);

  // Compliance full-page view: "sop" | "nearMiss" | "actionCentre" | null.
  // Renders the migrated full-page components inline (replaces the old
  // Compliance menu entries).
  const [compliance, setCompliance] = useState(null);
  const [patAppConfigOpen, setPatAppConfigOpen] = useState(false);
  useScreenHelp({ title: selected ? `Head Office — ${selected.title}` : compliance ? `Head Office — ${{ sop: "SOP compliance", appraisals: "Staff appraisals", staff: "Staff appraisals", training: "Staff training", accidentBook: "Accident book", mhra: "MHRA alerts", patientFeedback: "Feedback" }[compliance] || "Record detail"}` : "Head Office", blurb: selected || compliance ? "Detailed records for the selected module across the organisations in your group." : "Group-wide indicators for daily checks, staff development, safety alerts and feedback.", can: selected || compliance ? ["Use the organisation and period controls to focus the records.", "Open a record to inspect its detail or history.", "Return to Head Office to choose a different module."] : ["Review cards showing missing or overdue records.", "Open a card to see the organisations and records behind its indicator."], related: [] }, 20);

  // ---------------------------------------------------------------------
  // Configuration summary click-through — interprets the `navigate`
  // descriptor returned by /api/config-summary for each setting and lands
  // on exactly the screen that setting is edited from. Three shapes:
  //   { type: 'headoffice', key }  — a module card (compliance kind, the
  //                                  generic MODULES grid, or the Pat App
  //                                  config dialog — the three mechanisms
  //                                  already used elsewhere in this file)
  //   { type: 'view', view }       — a top-level App.js page; forwarded to
  //                                  the optional onNavigateApp prop
  //   { type: 'settingsDialog' }   — the gear-icon Settings dialog;
  //                                  forwarded to the optional onOpenSettings prop
  // ---------------------------------------------------------------------
  const goToConfigItem = useCallback(navigate => {
    if (!navigate || !isCareNavigationAllowed(navigate)) return;
    if (navigate.type === "view") {
      onNavigateApp && onNavigateApp(navigate.view);
      return;
    }
    if (navigate.type === "settingsDialog") {
      onOpenSettings && onOpenSettings();
      return;
    }
    if (navigate.type === "headoffice") {
      setSelected(null);
      setSelectedRx(null);
      setSimDetail(null);
      setCompliance(null);
      setPatAppConfigOpen(false);
      if (navigate.key === "patAppConfig") {
        setView("modules");
        setPatAppConfigOpen(true);
        return;
      }
      const mod = MODULES.find(m => m.key === navigate.key);
      if (mod) {
        setView("modules");
        setSelected(mod);
        return;
      }
      // Everything else (patientFeedback, mhra, sop, holidayRota, staffAbsences,
      // nearMiss, staff, clock, accidentBook, messaging…) is a "compliance" kind.
      setView("modules");
      setCompliance(navigate.key);
    }
  }, [onNavigateApp, onOpenSettings]);
  useEffect(() => {
    if (!token || !CARE_HEAD_OFFICE_KEYS.has("rx_retrievals")) return;
    let cancelled = false;
    setRxSnapshot(null);
    apiGet("prescriptions/snapshot", token).then(data => {
      if (!cancelled) setRxSnapshot(Array.isArray(data) ? data : []);
    }).catch(() => {
      if (!cancelled) setRxSnapshot([]);
    });
    return () => {
      cancelled = true;
    };
  }, [token, refreshKey]);
  if (!token) {
    return <div style={S.wrap}><div style={S.error}>Not signed in.</div></div>;
  }
  return <div style={S.wrap}>
      {/* Heading row: title (hover for the live-view explainer) with the
          Action Centre strip beside it. Rendered INTO the top organisation
          line via HeaderPortal when App.js provides the slot, so the whole
          header costs no vertical space of its own. */}
      <HeaderPortal>
      <div style={{
        display: "flex",
        gap: 16,
        alignItems: "center",
        flexWrap: "wrap"
      }}>
        <div style={{
          position: "relative",
          display: "inline-block"
        }} onMouseEnter={() => setShowInfo(true)} onMouseLeave={() => setShowInfo(false)}>
          <h1 style={{
            ...S.h1,
            margin: 0,
            fontSize: 18,
            cursor: "help",
            borderBottom: "1px dotted #8C8C8C",
            whiteSpace: "nowrap"
          }}>
            Head Office — Fleet Dashboard
          </h1>
          {showInfo && <div style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 500,
            width: "min(640px, 80vw)",
            marginTop: 6,
            background: "#E6F7FF",
            border: "1px solid #BAE7FF",
            borderRadius: 6,
            padding: "12px 14px",
            fontSize: 13,
            color: "#1e3a5f",
            lineHeight: 1.5,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)"
          }}>
              <b>This is a live view, calculated right now</b> — not the overnight
              monthly archive. Numbers reflect the current state of every pharmacy in
              your group and recent rolling activity, refreshed each time you open this
              page.
              <br />
              On each compliance card, <b>"Active pharmacies"</b> is how many sites
              have used that module recently, and the <b style={{
              color: "#CF1322"
            }}>red
              numbers</b> are how many sites are <b>currently failing</b> that check.
              Hover any row for a one-line explanation. Click a card to see which
              pharmacies and the detail. The blue activity cards at the top show
              today's group-wide bag totals.
            </div>}
        </div>
        {/* Action Centre strip removed — the Action centre has moved to the
            Communications page in full. Nothing here links to it any more;
            ActionCentrePanel below is left in place, unused, so it can be
            restored with one line if the move is reversed. */}

        {/* Hide names + row visibility, moved up here from two full-width rows
            of their own. Deliberately unboxed and compact: in the header they
            are chrome beside the title, not a panel, and the slot wraps if the
            window is too narrow to hold them on one line. */}
        <MaskToggleButton />

        <div style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 12
        }}>
          <span style={{
            fontSize: 10.5,
            fontWeight: 800,
            color: "#5D5D5D",
            textTransform: "uppercase",
            letterSpacing: 0.5
          }}>
            Show rows:
          </span>
          {DASHBOARD_GROUPS.map(g => <label key={g.label} style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 12,
            cursor: "pointer",
            color: "#434343",
            whiteSpace: "nowrap"
          }} title={hiddenGroups.has(g.label) ? `Show the "${g.label}" row` : `Hide the "${g.label}" row`}>
              <input type="checkbox" checked={!hiddenGroups.has(g.label)} onChange={() => toggleGroup(g.label)} />
              {g.label}
            </label>)}
        </div>
      </div>
      </HeaderPortal>

      {/* Always visible (unlike the Refresh / Job runs / Inactive sites /
          Pat App - dev row below, which is hidden per HIDDEN_TOOLBAR) — a
          setup checklist across every module, not gated behind the dev row.
          Only on the dashboard itself (and on the summary, where it is the way
          back) — not above every full-page module opened from it. */}
      {(view === "config_summary" || view === "modules" && !compliance && !selected && !selectedRx && !simDetail && !campaignsOpen) && <div style={S.sub}>
        <button style={S.btnGhost} onClick={() => {
        setView(view === "config_summary" ? "modules" : "config_summary");
        setSelected(null);
        setSelectedRx(null);
        setSimDetail(null);
        setCompliance(null);
      }}>
          {view === "config_summary" ? "Modules" : "Configuration summary"}
        </button>
      </div>}

      {view === "config_summary" && <ConfigurationSummary token={token} onNavigate={goToConfigItem} />}

      {/* Refresh / Job runs / Inactive sites / Pat App - dev hidden per request
          but kept in code — flip HIDDEN_TOOLBAR (module scope) to false to
          restore them. The views they open (job_runs / inactive / the Pat App
          config) remain fully functional underneath. The whole row is gated
          now rather than just its contents: "Hide names" has moved to the
          header, so leaving the row in place would render an empty div with a
          16px bottom margin. */}
      {!HIDDEN_TOOLBAR && <div style={S.sub}>
        {(() => {
        return <>
        <button style={S.btnGhost} onClick={() => setRefreshKey(k => k + 1)}>
          Refresh
        </button>
        <button style={{
            ...S.btnGhost,
            marginLeft: 8
          }} onClick={() => {
            setView(view === "job_runs" ? "modules" : "job_runs");
            setSelected(null);
            setSelectedRx(null);
            setSimDetail(null);
            setCompliance(null);
          }}>
          {view === "job_runs" ? "Modules" : "Job runs"}
        </button>
        <button style={{
            ...S.btnGhost,
            marginLeft: 8
          }} onClick={() => {
            setView(view === "inactive" ? "modules" : "inactive");
            setSelected(null);
            setSelectedRx(null);
            setSimDetail(null);
            setCompliance(null);
          }}>
          {view === "inactive" ? "Modules" : "Inactive sites"}
        </button>
        <button style={{
            ...S.btnGhost,
            marginLeft: 8
          }} onClick={() => setPatAppConfigOpen(true)}>
          Pat App - dev
        </button>
          </>;
      })()}
      </div>}

      {view === "job_runs" && <JobRunsView token={token} onClose={() => setView("modules")} />}

      {view === "inactive" && <InactiveSitesView token={token} onClose={() => setView("modules")} />}

      {view === "periodic" && <PeriodicTasksView token={token} initialOverdue={periodicOverdue} onClose={() => {
      setView("modules");
      setPeriodicOverdue(false);
    }} />}

      {patAppConfigOpen && <PatAppConfig token={token} onClose={() => setPatAppConfigOpen(false)} />}

      {/* Compliance full-page (SOP / Near Miss / Action Centre) takes over the
          whole modules area when active. */}
      {view === "modules" && compliance && <ComplianceFullPage kind={compliance} onClose={() => setCompliance(null)} />}

      {/* Prescription-activity drill-in takes over the modules area */}
      {view === "modules" && !compliance && selectedRx && <PrescriptionActivityDetail variant={selectedRx} snapshotData={rxSnapshot} token={token} onClose={() => setSelectedRx(null)} />}

      {view === "modules" && !compliance && simDetail && <SimDetailPlaceholder mod={simDetail} token={token} onClose={() => setSimDetail(null)} />}

      {view === "modules" && !compliance && !selected && !selectedRx && !simDetail && !campaignsOpen && <>
          {/* Dashboard cards, arranged in labelled groups. Module cards are
              looked up from MODULES by key; custom cards use special tokens.
              Any MODULES key not assigned to a group lands in "Other"
              (hidden while empty), so future modules never vanish. */}
          {(() => {
        const GROUPS = DASHBOARD_GROUPS;
        const openCompliance = kind => () => {
          if (!CARE_HEAD_OFFICE_KEYS.has(kind)) return;
          setCompliance(kind);
          setSelected(null);
          setSelectedRx(null);
          setSimDetail(null);
        };
        const cardFor = key => {
          if (!CARE_HEAD_OFFICE_KEYS.has(key)) return null;
          switch (key) {
            case "campaigns":
              return <CampaignsCard key={key} onOpen={() => {
                setCampaignsOpen(true);
                setSelected(null);
                setSelectedRx(null);
                setSimDetail(null);
              }} />;
            case "rx_retrievals":
              return <PrescriptionActivityCard key={key} variant="retrievals" snapshotData={rxSnapshot} onSelect={setSelectedRx} />;
            case "rx_deliveries":
              return <PrescriptionActivityCard key={key} variant="deliveries" snapshotData={rxSnapshot} onSelect={setSelectedRx} />;
            case "periodic":
              return <PeriodicEntriesCard key={key} token={token} onOpen={() => {
                setPeriodicOverdue(false);
                setView("periodic");
              }} onOpenOverdue={() => {
                setPeriodicOverdue(true);
                setView("periodic");
              }} />;
            case "sop":
              return <SopCard key={key} token={token} onOpen={openCompliance("sop")} />;
            case "holidayRota":
              return <HolidayRotaCard key={key} onOpen={openCompliance("holidayRota")} />;
            case "staffAbsences":
              return <StaffAbsencesCard key={key} onOpen={openCompliance("staffAbsences")} />;
            case "nearMiss":
              return <NearMissCard key={key} token={token} onOpen={openCompliance("nearMiss")} />;
            case "appraisals":
              return <AppraisalsCard key={key} token={token} onOpen={openCompliance("staff")} />;
            case "mhra":
              return <MhraAlertsCard key={key} token={token} onOpen={openCompliance("mhra")} />;
            case "patientFeedback":
              return <SurveysCard key={key} token={token} onOpen={openCompliance("patientFeedback")} />;
            case "clock":
              return <ClockCard key={key} token={token} onOpen={openCompliance("clock")} />;
            case "training":
              return <StaffTrainingCard key={key} />;
            default:
              {
                const m = MODULES.find(x => x.key === key);
                if (!m) return null;
                if (m.key === "messaging") return <MessagingCard key={m.key} mod={m} token={token} onSelect={setSelected} onSelectSim={setSimDetail} />;
                if (m.key === "accidentBook") return <AccidentBookCard key={m.key} token={token} onOpen={openCompliance("accidentBook")} />;
                return <ModuleCard key={m.key} mod={m} token={token} onSelect={setSelected} />;
              }
          }
        };
        return <div key={refreshKey}>
                {/* Row visibility controls moved to the page header, beside
                    the "Head Office — Fleet Dashboard" label, to give the
                    panels below the vertical space instead. */}
                {GROUPS.filter(g => !hiddenGroups.has(g.label)).map(g => <div key={g.label} style={{
            border: "1px solid #D9D9D9",
            borderRadius: 10,
            padding: "12px 14px 14px",
            marginBottom: 18,
            background: "#FCFCFC"
          }}>
                    <div style={{
              fontSize: 12,
              fontWeight: 800,
              color: "#434343",
              textTransform: "uppercase",
              letterSpacing: 0.6,
              marginBottom: 10
            }}>
                      {g.label}
                    </div>
                    <div style={S.grid}>
                      {g.keys.map(k => cardFor(k))}
                    </div>
                  </div>)}
              </div>;
      })()}
        </>}

      {view === "modules" && !compliance && campaignsOpen && <CampaignsView token={token} onClose={() => setCampaignsOpen(false)} />}

      {view === "modules" && !compliance && selected && <ModuleDetail mod={selected} token={token} onClose={() => setSelected(null)} />}
    </div>;
}
